import * as test from './alan_api';
import * as client_bindings from '../../libraries/gui_definition_lib/libraries/client_lib/types/client_bindings/alan_api';
import * as gui_model from '../../libraries/gui_definition_lib/types/gui_model/alan_api';
import * as settings from '../../libraries/gui_definition_lib/types/settings/alan_api';
import * as views from '../../libraries/gui_definition_lib/types/views/alan_api';

type SymbolsFile = [string, SymbolsList];
type SymbolsList = Array<[string, string, string, [number, number, number], [number, number, number]]>;
type Symbols = {source_file: string, source_map: SymbolsMap};
type SymbolsMap = Map<string, SymbolPos>;
type SymbolPos = { begin: { line: number; column: number }; end: { line: number; column: number }; type: string; instance: string };
export type Symbol = { source_file: string; line: number; column: number };
function parseSymbolsFile(symbols_file: SymbolsFile): Symbols {
	const source_file = symbols_file[0];
	const symbols_list = symbols_file[1];
	const source_map: SymbolsMap = new Map();
	for (const el of symbols_list) {
		source_map.set(el[0], {
			begin: { line: el[3][1], column: el[3][2] },
			end: { line: el[4][1], column: el[4][2] },
			type: el[2],
			instance: el[1]
		});
	}
	return {source_file: source_file, source_map: source_map};
}
// https://github.com/jbt/tiny-hashes/blob/master/sha1/sha1.js
function sha1sum(b: any): string {
	var i,
		W = [],
		A: any, B, C, D,
		h = [ A = 0x67452301, B = 0xEFCDAB89, ~A, ~B, 0xC3D2E1F0 ],
		words = [],
		s: any = unescape(encodeURI(b)) + '\x80',
		j = s.length;
	words[b = (--j / 4 + 2) | 15] = j * 8;
	for (; ~j;) {
		words[j >> 2] |= s.charCodeAt(j) << 8 * ~j--;
	}
	for (i = j = 0; i < b; i += 16) {
		A = h;
		for (; j < 80;
			A = [
				(
					A[4] +
					(
						W[j] =
							(j < 16)
								? ~~words[i + j]
								: s * 2 | (s < 0 as any)
					) +
					1518500249 +
					[
						(B & C | ~B & D),
						s = (B ^ C ^ D) + 341275144,
						(B & C | B & D | C & D) + 882459459,
						s + 1535694389
					][j++ / 5 >> 2] +
					((s = A[0]) << 5 | s >>> 27)
				),
				s,
				B << 30 | B >>> 2,
				C,
				D
			]
		) {
			s = W[j - 3] ^ W[j - 8] ^ W[j - 14] ^ W[j - 16];
			B = A[1];
			C = A[2];
			D = A[3];
		}
		for (j = 5; j;) h[--j] += A[j];
	}
	for (s = ''; j < 40;) {
		s += (h[j >> 3] >> (7 - j++) * 4 & 15).toString(16);
	}
	return s;
}
function getSymbol(symbols: Symbols, path: string): Symbol | undefined {
	const hash = sha1sum(path);
	const pos = symbols.source_map.get(hash);
	if (pos !== undefined) {
		return { source_file: symbols.source_file, line: pos.begin.line, column: pos.begin.column };
	}
	return undefined;
}

const debug = false;
function isFunction(p: any): p is Function {
	return (typeof p === 'function');
}
function assert(predicate:boolean) {
	if (predicate === false) throw new Error(`Assertion error`);
}
function resolve<T>(context:T) {
	return {
		then: <RT>(callback: (context: T) => RT) => resolve(callback(context)),
		result: context
	};
}
export type dictionary_type<T> = {[key:string]:T};


enum ResolutionStatus {
	Resolved,
	Resolving,
	Unresolved,
	Detached,
}
function cache<T extends IAlanObject>(callback:(detach:boolean) => T) {
	let cached_value:T;
	let status:ResolutionStatus = ResolutionStatus.Unresolved;
	return (detach = false) => {
		switch (status) {
			case ResolutionStatus.Resolving:
				throw new Error(`Cyclic dependency detected!`);
			case ResolutionStatus.Detached: {
				if (detach) break;
				(cached_value as any) = undefined;
				status = ResolutionStatus.Resolving;
				cached_value = callback(detach);
				status = ResolutionStatus.Resolved;
			} break;
			case ResolutionStatus.Resolved: {
				if (!detach) break;
				callback(detach);
				status = ResolutionStatus.Detached;
			} break;
			case ResolutionStatus.Unresolved: {
				if (detach) break;
				status = ResolutionStatus.Resolving;
				cached_value = callback(detach);
				status = ResolutionStatus.Resolved;
			} break;
		}
		return cached_value;
	}
}

function maybe_cache<T extends IAlanObject>(callback:(detach:boolean) => T|undefined) {
	let cached_value:T|undefined;
	let status:ResolutionStatus = ResolutionStatus.Unresolved;
	return (detach = false) => {
		switch (status) {
			case ResolutionStatus.Resolving:
				throw new Error(`Cyclic dependency detected!`);
			case ResolutionStatus.Detached: {
				if (detach) break;
				cached_value = undefined;
				status = ResolutionStatus.Resolving;
				cached_value = callback(detach);
				status = ResolutionStatus.Resolved;
			} break;
			case ResolutionStatus.Resolved: {
				if (!detach) break;
				callback(detach);
				status = ResolutionStatus.Detached;
			} break;
			case ResolutionStatus.Unresolved: {
				if (detach) break;
				status = ResolutionStatus.Resolving;
				cached_value = callback(detach);
				status = ResolutionStatus.Resolved;
			} break;
		}
		return cached_value;
	}
}

/* number validation */
function number__is_positive(val:number) { assert(val > 0); }
function number__is_negative(val:number) { assert(val < 0); }
function number__is_zero(val:number) { assert(val === 0); }
function number__is_non_positive(val:number) { assert(val <= 0); }
function number__is_non_negative(val:number) { assert(val >= 0); }
function number__is_non_zero(val:number) { assert(val !== 0); }

/* complex value wrappers */
export abstract class AlanObject {
	public abstract get path(): string;
	private _destroyed?: true;
	get destroyed(): boolean {
		return this._destroyed ? true : false;
	}
}
export interface IAlanObject {
	readonly path: string;
	readonly destroyed: boolean;
};

export abstract class AlanStruct extends AlanObject {
	public abstract is(other:AlanStruct):boolean;
}
export interface Tree<T> {
	types: {[name:string]:T};
	subtrees: {[name:string]:Tree<T>};
}
export abstract class Reference<T extends IAlanObject, V extends (string|string[])> extends AlanObject {
	constructor(public readonly entry:V, private readonly resolve:() => T, protected readonly parent: AlanNode) { super(); }
	public get ref() { return this.resolve(); }

	get symbol(): Symbol | undefined {
		const symbols = this.parent.root.symbols;
		if (symbols) {
			return getSymbol(symbols, this.path);
		}
		return undefined;
	}
}
export abstract class AlanInteger extends AlanObject {
	constructor(public readonly value:number, protected readonly parent: AlanNode) { super(); }

	get symbol(): Symbol | undefined {
		const symbols = this.parent.root.symbols;
		if (symbols) {
			return getSymbol(symbols, this.path);
		}
		return undefined;
	}
}
export abstract class AlanDictionary<T extends {node: AlanDictionaryEntry, init: any }, P extends AlanNode> extends AlanObject {
	protected _entries: Map<string, T['node']>;

	constructor (entries:{[key:string]:T['init']},	protected parent:P) {
		super();
		this._entries = new Map(Object.keys(entries).map(entry_key => [entry_key, this.initialize(parent, entry_key, entries[entry_key])]));
	}

	protected abstract initialize(parent:P, key:string, obj:T['init']): T['node'];
	protected abstract finalize(obj:T['node'], detach?:boolean): void;

	get size() { return this._entries.size; }
	[Symbol.iterator]():IterableIterator<[string,T['node']]> {
		if (this.destroyed) throw new Error("Trying to access destroyed property! " + this.path);
		return this._entries.entries();
	}
	entries(): IterableIterator<[string,T['node']]> {
		if (this.destroyed) throw new Error("Trying to access destroyed property! " + this.path);
		return this._entries.entries();
	}
	values(): IterableIterator<T['node']> {
		if (this.destroyed) throw new Error("Trying to access destroyed property! " + this.path);
		return this._entries.values();
	}
	keys(): IterableIterator<string> {
		if (this.destroyed) throw new Error("Trying to access destroyed property! " + this.path);
		return this._entries.keys();
	}
	forEach(walk_function: ($:T['node']) => void) {
		if (this.destroyed) throw new Error("Trying to access destroyed property! " + this.path);
		for (const entry of this.values()) {
			walk_function(entry);
		}
	}
	toArray(): [string, T['node']][] {
		if (this.destroyed) throw new Error("Trying to access destroyed property! " + this.path);
		return Array.from(this.entries());
	}
	map<RT>(callback:(value:T['node']) => RT): Record<string, RT> {
		if (this.destroyed) throw new Error("Trying to access destroyed property! " + this.path);
		const result:Record<string, RT> = {};
		for (const [key, entry] of this.entries()) {
			result[key] = callback(entry);
		}
		return result;
	}
	get(key:string): T['node']|undefined {
		if (this.destroyed) throw new Error("Trying to access destroyed property! " + this.path);
		return this._entries.get(key);
	}
	has(key:string): boolean { return this._entries.has(key); }
	switchOnEntryExists<RT>(key:string, onExists:(($: T['node']) => RT)|Exclude<RT, Function>, onNotExists:(() => RT)|Exclude<RT, Function>): RT {
		if (this.destroyed) throw new Error("Trying to access destroyed property! " + this.path);
		const entry = this._entries.get(key);
		if (entry === undefined) {
			return isFunction(onNotExists) ? onNotExists() : onNotExists;
		} else {
			return isFunction(onExists) ? onExists(entry) : onExists;
		}
	}
}

export abstract class AlanSet<T extends {node: AlanNode, init: any }, P extends AlanNode> extends AlanObject {
	private _entries:Set<T['node']>;
	constructor (
		entries:Array<T['init']>,
		protected parent:P) {
		super();
		this._entries = new Set(entries.map(entry => this.initialize(parent, entry)));
	}

	protected abstract initialize(parent:P, obj:T['init']):T['node'];
	protected abstract finalize(obj:T['node'],detach?:boolean):void;

	get size() { return this._entries.size; }
	[Symbol.iterator]() {
		return this._entries[Symbol.iterator]();
	}
	entries() {
		return this._entries.entries();
	}
	forEach(walk_function: ($:T['node']) => void) {
		this._entries.forEach(walk_function);
	}
	has(key:T['node']):boolean { return this._entries.has(key); }
}
type DistributiveOmit<T, K extends keyof any> = T extends any ? Omit<T, K> : never;
export abstract class StateGroup<T extends {name:string, node:AlanNode & {parent:AlanNode}, init:any}> extends AlanObject {
	public state: DistributiveOmit<T,'init'>;
	public readonly root:Ctest
	private init(state_name:T['name'], init:T['init'], parent:AlanNode) {
		this.state = {
			name: state_name,
			node: this.initializer(state_name)(init, parent),
		} as DistributiveOmit<T,'init'>;
	}
	constructor (s:[T['name'],T['init']]|T['name'], protected readonly parent: AlanNode, readonly name: string) {
		super();
		this.root = parent.root
		const state_name:T['name'] = (typeof s === 'string') ? s : s[0];
		this.init(state_name, typeof s === 'string' ? {} : s[1], parent);
	}

	protected abstract initializer(state_name:T['name']): ($:T['init'], parent:AlanNode) => T['node'];
	protected abstract finalizer(state:T['name']): ($:T['node'], detach?:boolean) => void;

	switch<TS> (cases:{[K in T['name']]:(($:Extract<T, {name:K}>['node']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		if (this.destroyed) throw new Error("Trying to access destroyed property! " + this.path);
		const handler = cases[this.state.name as T['name']];
		if (isFunction(handler)) {
			return handler(this.state.node);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	cast<S extends T['name']>(state:S):Extract<T, {name:S}>['node'] {
		if (this.destroyed) throw new Error("Trying to access destroyed property! " + this.path);
		if (this.state.name === state) {
			return this.state.node;
		} else {
			throw new Error(`Invalid cast to state '${state}'; actual state is '${this.state.name}' at ${this.state.node.path} .`);
		}
	}

	get symbol(): Symbol | undefined {
		const symbols = this.root.symbols;
		if (symbols) {
			return getSymbol(symbols, this.path);
		}
		return undefined;
	}
}

export abstract class AlanCombinator extends AlanObject {}
export abstract class AlanNode extends AlanStruct {
	protected _root:Ctest|undefined;
	public abstract get root():Ctest;
	public abstract get entity():AlanNode;
	public is(other:AlanNode):boolean {
		return this === other;
	}
	get symbol(): Symbol | undefined {
		const symbols = this.root.symbols;
		if (symbols) {
			return getSymbol(symbols, this.path);
		}
		return undefined;
	}
}

export abstract class AlanDictionaryEntry extends AlanNode {
	public abstract key:(string|Reference<IAlanObject,string>);
	public abstract get key_value():string;
};
type AlanGraphEdge<T extends AlanNode> = {
	ref: {
		entity: T
	}
};
abstract class AlanGraphVertex extends AlanDictionaryEntry {
	abstract _edges: { [key: string ]: Set<AlanGraphEdge<AlanGraphVertex>> }
}
export abstract class AlanTopology<T extends { node:AlanGraphVertex, init:any }, P extends AlanNode, G extends string> extends AlanDictionary<T,P> {
	protected abstract _graphs: { [key: string ]: T['node'][] };
	topo_forEach(graph:G, walk_function: ($:T['node']) => void) {
		for (let entry of this.topo_entries(graph)) { walk_function(entry); }
	}
	topo_entries(graph:G):IterableIterator<T['node']> {
		return this._graphs[graph][Symbol.iterator]();
	}
	topo_toArray(graph:G):T['node'][] {
		return Array.from(this.topo_entries(graph));
	}
	topo_sort(g:G) {
		const $this = this;
		$this._graphs[g] = []
		const indegree:Map<T['node'], number> = new Map(Array.from($this.entries()).map(([_,v]) => [v as T['node'],0]));
		for (let [_,v] of $this.entries()) {
			v._edges[g].forEach(edge => indegree.set(edge.ref.entity as AlanGraphVertex, indegree.get(edge.ref.entity as AlanGraphVertex)! + 1));
		}
		let queue: T['node'][] = [];

		//find all vertices with indegree 0
		indegree.forEach((v,k) => { if (v === 0) queue.push(k); });
		let visited = 0;
		while (queue.length > 0) {
			++visited;
			const v = queue.pop()!;
			$this._graphs[g].push(v);
			v._edges[g].forEach(edge => {
				const u = indegree.get(edge.ref.entity as T['node'])!;
				if (u === 1) {
					queue.push(edge.ref.entity as T['node']);
				} else {
					indegree.set(edge.ref.entity as T['node'], u - 1)
				}
			});
		}

		// Check if there was a cycle
		if (visited !== this._entries.size) {
			throw new Error(`Cycle found in graph.`);
		}
	}
	totally_ordered(g:G) {
		if (this._graphs[g].length < 2) return;
		this._graphs[g].reduce((prev, curr) => {
			if (prev._edges[g].size === 0) { return curr; }
			let connected = false;
			prev._edges[g].forEach(e => { connected = (connected || e.ref.entity === curr) });
			if (!connected)
				throw new Error(`Totally ordered graph constraint violation.`);
			return curr;
		});
	}
}

/* alan objects */
type Vvalue_context = { name: 'query entry', definition: views.Cqueries}|{ name: 'ordered graph', definition: gui_model.Cordered_graphs}|{ name: 'rule', definition: gui_model.Crules}|{ name: 'state', definition: test.Cstate__test}|{ name: 'entry', definition: test.Ccollection__test}|{ name: 'attribute', definition: test.Cattribute__test}|{ name: 'value', definition: test.Coptional_value}
export class Cvalue_context extends AlanStruct {
	constructor(
		public readonly variant:Vvalue_context) { super(); }
	public readonly output:{
		value: () => test.Coptional_value;
	} = {
		value: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => {
				switch (context?.variant.name) {
					case 'attribute': {
						const test__value_context_out___value___attribute_nval = context.cast('attribute');
						return resolve(context)
							.then(context => test__value_context_out___value___attribute_nval)
							.then(context => {
								switch (context?.variant.name) {
									case 'binding': {
										const test__value_context_out___value___attribute___binding_nval = context.cast('binding');
										return resolve(context)
											.then(context => test__value_context_out___value___attribute___binding_nval)
											.then(context => context?.properties.type)
											.then(context => {
												const state_group = context;
												switch (state_group?.state.name) {
													case 'collection': {
														return resolve(context)
															.then(() => this)
															.then(() => test.Coptional_value.Pnone).result;
													}
													case 'number': {
														return resolve(context)
															.then(() => this)
															.then(() => test.Cvalue__test.Pnumber)
															.then(context => context?.definitions.optional_value).result;
													}
													case 'reference': {
														return resolve(context)
															.then(() => this)
															.then(() => test.Cvalue__test.Ptext)
															.then(context => context?.definitions.optional_value).result;
													}
													case 'state group': {
														return resolve(context)
															.then(() => this)
															.then(() => test.Coptional_value.Pnone).result;
													}
													case 'text': {
														return resolve(context)
															.then(() => this)
															.then(() => test.Cvalue__test.Ptext)
															.then(context => context?.definitions.optional_value).result;
													}
													case undefined: return undefined;
													default: throw new Error(`Unexpected state '${(state_group!.state as any).name}'`);
												};}).result;
									}
									case 'gui': {
										const test__value_context_out___value___attribute___gui_nval = context.cast('gui');
										return resolve(context)
											.then(context => test__value_context_out___value___attribute___gui_nval)
											.then(context => context?.properties.type)
											.then(context => {
												const state_group = context;
												switch (state_group?.state.name) {
													case 'action': {
														const test__value_context_out___value___attribute___gui___action_nval = state_group.cast('action');
														return resolve(context)
															.then(context => test__value_context_out___value___attribute___gui___action_nval)
															.then(context => context?.properties.parameters)
															.then(context => {
																const conv_context = resolve(context).result!;
																return new Cnode__test({name: 'gui', definition: conv_context});
															})
															.then(context => context?.definitions.value)
															.then(context => context?.definitions.optional_value).result;
													}
													case 'command': {
														const test__value_context_out___value___attribute___gui___command_nval = state_group.cast('command');
														return resolve(context)
															.then(context => test__value_context_out___value___attribute___gui___command_nval)
															.then(context => context?.properties.type)
															.then(context => {
																const state_group = context;
																switch (state_group?.state.name) {
																	case 'component': {
																		return resolve(context)
																			.then(() => this)
																			.then(() => test.Coptional_value.Pnone).result;
																	}
																	case 'global': {
																		const test__value_context_out___value___attribute___gui___command___global_nval = state_group.cast('global');
																		return resolve(context)
																			.then(context => test__value_context_out___value___attribute___gui___command___global_nval)
																			.then(context => context?.properties.parameters)
																			.then(context => {
																				const conv_context = resolve(context).result!;
																				return new Cnode__test({name: 'gui', definition: conv_context});
																			})
																			.then(context => context?.definitions.value)
																			.then(context => context?.definitions.optional_value).result;
																	}
																	case undefined: return undefined;
																	default: throw new Error(`Unexpected state '${(state_group!.state as any).name}'`);
																};}).result;
													}
													case 'property': {
														const test__value_context_out___value___attribute___gui___property_nval = state_group.cast('property');
														return resolve(context)
															.then(context => test__value_context_out___value___attribute___gui___property_nval)
															.then(context => context?.properties.type)
															.then(context => {
																const state_group = context;
																switch (state_group?.state.name) {
																	case 'collection': {
																		return resolve(context)
																			.then(() => this)
																			.then(() => test.Coptional_value.Pnone).result;
																	}
																	case 'file': {
																		return resolve(context)
																			.then(() => this)
																			.then(() => test.Coptional_value.Pnone).result;
																	}
																	case 'group': {
																		const test__value_context_out___value___attribute___gui___property___group_nval = state_group.cast('group');
																		return resolve(context)
																			.then(context => test__value_context_out___value___attribute___gui___property___group_nval)
																			.then(context => context?.properties.gui_node)
																			.then(context => {
																				const conv_context = resolve(context).result!;
																				return new Cnode__test({name: 'gui', definition: conv_context});
																			})
																			.then(context => context?.definitions.value)
																			.then(context => context?.definitions.optional_value).result;
																	}
																	case 'number': {
																		return resolve(context)
																			.then(() => this)
																			.then(() => test.Coptional_value.Pnone).result;
																	}
																	case 'state group': {
																		return resolve(context)
																			.then(() => this)
																			.then(() => test.Coptional_value.Pnone).result;
																	}
																	case 'text': {
																		return resolve(context)
																			.then(() => this)
																			.then(() => test.Coptional_value.Pnone).result;
																	}
																	case undefined: return undefined;
																	default: throw new Error(`Unexpected state '${(state_group!.state as any).name}'`);
																};}).result;
													}
													case 'reference set': {
														return resolve(context)
															.then(() => this)
															.then(() => test.Coptional_value.Pnone).result;
													}
													case undefined: return undefined;
													default: throw new Error(`Unexpected state '${(state_group!.state as any).name}'`);
												};}).result;
									}
									case undefined: return undefined;
									default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
								};
							}).result;
					}
					case 'entry': {
						const test__value_context_out___value___entry_nval = context.cast('entry');
						return resolve(context)
							.then(context => test__value_context_out___value___entry_nval)
							.then(context => {
								switch (context?.variant.name) {
									case 'binding': {
										const test__value_context_out___value___entry___binding_nval = context.cast('binding');
										return resolve(context)
											.then(context => test__value_context_out___value___entry___binding_nval)
											.then(context => context?.properties.node)
											.then(context => {
												const conv_context = resolve(context).result!;
												return new Cnode__test({name: 'binding', definition: conv_context});
											})
											.then(context => context?.definitions.value)
											.then(context => context?.definitions.optional_value).result;
									}
									case 'gui': {
										const test__value_context_out___value___entry___gui_nval = context.cast('gui');
										return resolve(context)
											.then(context => test__value_context_out___value___entry___gui_nval)
											.then(context => context?.properties.node)
											.then(context => {
												const conv_context = resolve(context).result!;
												return new Cnode__test({name: 'gui', definition: conv_context});
											})
											.then(context => context?.definitions.value)
											.then(context => context?.definitions.optional_value).result;
									}
									case undefined: return undefined;
									default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
								};
							}).result;
					}
					case 'ordered graph': {
						return resolve(context)
							.then(() => this)
							.then(() => test.Coptional_value.Pnone).result;
					}
					case 'query entry': {
						return resolve(context)
							.then(() => this)
							.then(() => test.Coptional_value.Pnone).result;
					}
					case 'rule': {
						return resolve(context)
							.then(() => this)
							.then(() => test.Coptional_value.Pnone).result;
					}
					case 'state': {
						const test__value_context_out___value___state_nval = context.cast('state');
						return resolve(context)
							.then(context => test__value_context_out___value___state_nval)
							.then(context => {
								switch (context?.variant.name) {
									case 'binding': {
										const test__value_context_out___value___state___binding_nval = context.cast('binding');
										return resolve(context)
											.then(context => test__value_context_out___value___state___binding_nval)
											.then(context => context?.properties.node)
											.then(context => {
												const conv_context = resolve(context).result!;
												return new Cnode__test({name: 'binding', definition: conv_context});
											})
											.then(context => context?.definitions.value)
											.then(context => context?.definitions.optional_value).result;
									}
									case 'gui': {
										const test__value_context_out___value___state___gui_nval = context.cast('gui');
										return resolve(context)
											.then(context => test__value_context_out___value___state___gui_nval)
											.then(context => context?.properties.gui_node)
											.then(context => {
												const conv_context = resolve(context).result!;
												return new Cnode__test({name: 'gui', definition: conv_context});
											})
											.then(context => context?.definitions.value)
											.then(context => context?.definitions.optional_value).result;
									}
									case undefined: return undefined;
									default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
								};
							}).result;
					}
					case 'value': {
						const test__value_context_out___value___value_nval = context.cast('value');
						return resolve(context)
							.then(context => test__value_context_out___value___value_nval).result;
					}
					case undefined: return undefined;
					default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
				};
			}).result!)
	};
	public cast<K extends Vvalue_context['name']>(_variant:K):Extract<Vvalue_context, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vvalue_context['name']]:(($:Extract<Vvalue_context, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'value context'`; }
	public is(other:Cvalue_context):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
type Voptional_value = { name: 'none', definition: (typeof Coptional_value.Pnone)}|{ name: 'value', definition: test.Cvalue__test}
export class Coptional_value extends AlanStruct {
	public static Pnone:Coptional_value = new class PrimitiveInstance extends Coptional_value {
		constructor () {
			super({name: 'none', definition: undefined as unknown as Coptional_value})
			this.variant.definition = this;
		}
	}
	constructor(
		public readonly variant:Voptional_value) { super(); }
	public definitions:{
		value_context: Cvalue_context;
	} = {
		value_context: new Cvalue_context({name:'value', definition: this})
	}
	public cast<K extends Voptional_value['name']>(_variant:K):Extract<Voptional_value, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Voptional_value['name']]:(($:Extract<Voptional_value, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'optional value'`; }
	public is(other:Coptional_value):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
export import Cnone__optional_value = test.Coptional_value;
type Vvalue__test = { name: 'file', definition: (typeof Cvalue__test.Pfile)}|{ name: 'number', definition: (typeof Cvalue__test.Pnumber)}|{ name: 'text', definition: (typeof Cvalue__test.Ptext)}|{ name: 'query', definition: views.Cqueries}|{ name: 'view', definition: test.Cview__test}|{ name: 'node', definition: test.Cnode__test}
export class Cvalue__test extends AlanStruct {
	public static Pfile:Cvalue__test = new class PrimitiveInstance extends Cvalue__test {
		constructor () {
			super({name: 'file', definition: undefined as unknown as Cvalue__test})
			this.variant.definition = this;
		}
	}
	public static Pnumber:Cvalue__test = new class PrimitiveInstance extends Cvalue__test {
		constructor () {
			super({name: 'number', definition: undefined as unknown as Cvalue__test})
			this.variant.definition = this;
		}
	}
	public static Ptext:Cvalue__test = new class PrimitiveInstance extends Cvalue__test {
		constructor () {
			super({name: 'text', definition: undefined as unknown as Cvalue__test})
			this.variant.definition = this;
		}
	}
	constructor(
		public readonly variant:Vvalue__test) { super(); }
	public definitions:{
		optional_value: Coptional_value;
	} = {
		optional_value: new Coptional_value({name:'value', definition: this})
	}
	public readonly output:{
		node: () => test.Coptional_node;
	} = {
		node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => {
				switch (context?.variant.name) {
					case 'file': {
						return resolve(context)
							.then(() => this)
							.then(() => test.Coptional_node.Pnone).result;
					}
					case 'node': {
						const test__value_out___node___node_nval = context.cast('node');
						return resolve(context)
							.then(context => test__value_out___node___node_nval)
							.then(context => {
								switch (context?.variant.name) {
									case 'binding': {
										const test__value_out___node___node___binding_nval = context.cast('binding');
										return resolve(context)
											.then(context => test__value_out___node___node___binding_nval)
											.then(context => {
												const conv_context = resolve(context).result!;
												return new Cnode__test({name: 'binding', definition: conv_context});
											})
											.then(context => context?.definitions.optional_node).result;
									}
									case 'gui': {
										const test__value_out___node___node___gui_nval = context.cast('gui');
										return resolve(context)
											.then(context => test__value_out___node___node___gui_nval)
											.then(context => {
												const conv_context = resolve(context).result!;
												return new Cnode__test({name: 'gui', definition: conv_context});
											})
											.then(context => context?.definitions.optional_node).result;
									}
									case undefined: return undefined;
									default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
								};
							}).result;
					}
					case 'number': {
						return resolve(context)
							.then(() => this)
							.then(() => test.Coptional_node.Pnone).result;
					}
					case 'query': {
						return resolve(context)
							.then(() => this)
							.then(() => test.Coptional_node.Pnone).result;
					}
					case 'text': {
						return resolve(context)
							.then(() => this)
							.then(() => test.Coptional_node.Pnone).result;
					}
					case 'view': {
						const test__value_out___node___view_nval = context.cast('view');
						return resolve(context)
							.then(context => test__value_out___node___view_nval)
							.then(context => {
								switch (context?.variant.name) {
									case 'explicit': {
										const test__value_out___node___view___explicit_nval = context.cast('explicit');
										return resolve(context)
											.then(context => test__value_out___node___view___explicit_nval)
											.then(context => {
												switch (context?.variant.name) {
													case 'open view': {
														const test__value_out___node___view___explicit___open_view_nval = context.cast('open view');
														return resolve(context)
															.then(context => test__value_out___node___view___explicit___open_view_nval)
															.then(context => context?.properties.node_type)
															.then(context => context?.component_root.output.result_gui_node())
															.then(context => {
																const conv_context = resolve(context).result!;
																return new Cnode__test({name: 'gui', definition: conv_context});
															})
															.then(context => context?.definitions.optional_node).result;
													}
													case 'view definition': {
														return resolve(context)
															.then(() => this)
															.then(() => test.Coptional_node.Pnone).result;
													}
													case undefined: return undefined;
													default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
												};
											}).result;
									}
									case 'implicit': {
										return resolve(context)
											.then(() => this)
											.then(() => test.Coptional_node.Pnone).result;
									}
									case undefined: return undefined;
									default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
								};
							}).result;
					}
					case undefined: return undefined;
					default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
				};
			}).result!)
	};
	public cast<K extends Vvalue__test['name']>(_variant:K):Extract<Vvalue__test, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vvalue__test['name']]:(($:Extract<Vvalue__test, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'value'`; }
	public is(other:Cvalue__test):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
export import Cfile__value = test.Cvalue__test;
export import Cnumber__value = test.Cvalue__test;
export import Ctext__value = test.Cvalue__test;
type Vview__test = { name: 'explicit', definition: test.Cexplicit_view}|{ name: 'implicit', definition: test.Cimplicit_view}
export class Cview__test extends AlanStruct {
	constructor(
		public readonly variant:Vview__test) { super(); }
	public definitions:{
		value: Cvalue__test;
	} = {
		value: new Cvalue__test({name:'view', definition: this})
	}
	public readonly output:{
		open: () => test.Cboolean;
	} = {
		open: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => {
				switch (context?.variant.name) {
					case 'explicit': {
						const test__view_out___open___explicit_nval = context.cast('explicit');
						return resolve(context)
							.then(context => test__view_out___open___explicit_nval)
							.then(context => {
								switch (context?.variant.name) {
									case 'open view': {
										return resolve(context)
											.then(() => this)
											.then(() => test.Cboolean.Ptrue_).result;
									}
									case 'view definition': {
										return resolve(context)
											.then(() => this)
											.then(() => test.Cboolean.Pfalse_).result;
									}
									case undefined: return undefined;
									default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
								};
							}).result;
					}
					case 'implicit': {
						const test__view_out___open___implicit_nval = context.cast('implicit');
						return resolve(context)
							.then(context => test__view_out___open___implicit_nval)
							.then(context => {
								switch (context?.variant.name) {
									case 'open view': {
										return resolve(context)
											.then(() => this)
											.then(() => test.Cboolean.Ptrue_).result;
									}
									case 'view definition': {
										return resolve(context)
											.then(() => this)
											.then(() => test.Cboolean.Pfalse_).result;
									}
									case undefined: return undefined;
									default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
								};
							}).result;
					}
					case undefined: return undefined;
					default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
				};
			}).result!)
	};
	public cast<K extends Vview__test['name']>(_variant:K):Extract<Vview__test, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vview__test['name']]:(($:Extract<Vview__test, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'view'`; }
	public is(other:Cview__test):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
export type Tvalue_type_definition = {
	'type':['binding', Tbinding__type]|'file'|['file', {}]|['node', Tnode__type__value_type_definition]|'number'|['number', {}]|['query', Tquery__type]|'text'|['text', {}]|'view'|['view', {}];
};
export class Cvalue_type_definition extends AlanNode {
	public readonly properties:{
		readonly type:Cvalue_type_definition.Dtype
	};
	public readonly output:{
		value: () => test.Cvalue__test;
	} = {
		value: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.type?.state.node.output.value()).result!)
	};
	constructor(init:Tvalue_type_definition, public location:AlanNode, private key: string) {
		super();
		const $this = this;
		this.properties = {
			type: new Cvalue_type_definition.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tbinding__type = {
	'client binding':string;
	'type':Tnode_type_path;
};
export class Cbinding__type extends AlanNode {
	public readonly properties:{
		readonly client_binding:Cbinding__type.Dclient_binding,
		readonly type:Cnode_type_path
	};
	public readonly output:{
		value: () => test.Cvalue__test;
	} = {
		value: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.type)
			.then(context => context?.component_root.output.node())
			.then(context => context?.definitions.value).result!)
	}
	constructor(init:Tbinding__type, public parent:Cvalue_type_definition) {
		super();
		const $this = this;
		this.properties = {
			client_binding: new Cbinding__type.Dclient_binding(init['client binding'], $this),
			type: new Cbinding__type.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'binding'`; }
	public get entity() { return this.parent.entity; }
}
export type Tfile__type__value_type_definition = {
};
export class Cfile__type__value_type_definition extends AlanNode {
	public readonly output:{
		value: () => test.Cvalue__test;
	} = {
		value: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => test.Cvalue__test.Pfile).result!)
	}
	constructor(init:Tfile__type__value_type_definition, public parent:Cvalue_type_definition) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'file'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnode__type__value_type_definition = {
	'type':Tnode_type_path;
};
export class Cnode__type__value_type_definition extends AlanNode {
	public readonly properties:{
		readonly type:Cnode_type_path
	};
	public readonly output:{
		value: () => test.Cvalue__test;
	} = {
		value: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.type)
			.then(context => context?.component_root.output.node())
			.then(context => context?.definitions.value).result!)
	}
	constructor(init:Tnode__type__value_type_definition, public parent:Cvalue_type_definition) {
		super();
		const $this = this;
		this.properties = {
			type: new Cnode__type__value_type_definition.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'node'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnumber__type__value_type_definition = {
};
export class Cnumber__type__value_type_definition extends AlanNode {
	public readonly output:{
		value: () => test.Cvalue__test;
	} = {
		value: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => test.Cvalue__test.Pnumber).result!)
	}
	constructor(init:Tnumber__type__value_type_definition, public parent:Cvalue_type_definition) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'number'`; }
	public get entity() { return this.parent.entity; }
}
export type Tquery__type = {
	'query':string;
	'select views':['choose', Tchoose__select_views__query]|'default'|['default', {}];
	'view':string;
};
export class Cquery__type extends AlanNode {
	public readonly properties:{
		readonly query:Cquery__type.Dquery,
		readonly select_views:Cquery__type.Dselect_views,
		readonly view:Cquery__type.Dview
	};
	public readonly output:{
		value: () => test.Cvalue__test;
	} = {
		value: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.query?.ref)
			.then(context => {
				const conv_context = resolve(context).result!;
				return new Cvalue__test({name: 'query', definition: conv_context});
			}).result!)
	}
	constructor(init:Tquery__type, public parent:Cvalue_type_definition) {
		super();
		const $this = this;
		this.properties = {
			query: new Cquery__type.Dquery(init['query'], $this),
			select_views: new Cquery__type.Dselect_views(init['select views'], $this),
			view: new Cquery__type.Dview(init['view'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'query'`; }
	public get entity() { return this.parent.entity; }
}
export type Tchoose__select_views__query = {
	'views':string;
};
export class Cchoose__select_views__query extends AlanNode {
	public readonly properties:{
		readonly views:Cchoose__select_views__query.Dviews
	};
	public readonly output:{
		views: () => views.Cviews;
	} = {
		views: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.views?.ref).result!)
	}
	constructor(init:Tchoose__select_views__query, public parent:Cquery__type) {
		super();
		const $this = this;
		this.properties = {
			views: new Cchoose__select_views__query.Dviews(init['views'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'select views'?'choose'`; }
	public get entity() { return this.parent.entity; }
}
export type Tdefault__select_views__query = {
};
export class Cdefault__select_views__query extends AlanNode {
	public readonly output:{
		views: () => views.Cviews;
	} = {
		views: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.inferences.default_views_is_set())
			.then(context => context?.properties.views_lib?.ref).result!)
	}
	public readonly inferences:{
		default_views_is_set: () => test.Cset__default_views_lib
	} = {
		default_views_is_set: cache((detach:boolean) => {
			const test__value_type_definition__type__query__select_views__default_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.root)
				.then(context => {
					if (context?.properties.default_views_lib.state.name === 'set') {
						return resolve(context.properties.default_views_lib.state.node as test.Cset__default_views_lib).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tdefault__select_views__query, public parent:Cquery__type) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'select views'?'default'`; }
	public get entity() { return this.parent.entity; }
}
export type Ttext__type__value_type_definition = {
};
export class Ctext__type__value_type_definition extends AlanNode {
	public readonly output:{
		value: () => test.Cvalue__test;
	} = {
		value: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => test.Cvalue__test.Ptext).result!)
	}
	constructor(init:Ttext__type__value_type_definition, public parent:Cvalue_type_definition) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'text'`; }
	public get entity() { return this.parent.entity; }
}
export type Tview__type = {
};
export class Cview__type extends AlanNode {
	public readonly output:{
		value: () => test.Cvalue__test;
	} = {
		value: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => test.Cimplicit_view.Pview_definition)
			.then(context => context?.definitions.view)
			.then(context => context?.definitions.value).result!)
	}
	constructor(init:Tview__type, public parent:Cvalue_type_definition) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'view'`; }
	public get entity() { return this.parent.entity; }
}
export type Tvalue_selection_path = {
	'has steps':['no', Tno__has_steps__value_selection_path]|['yes', Tyes__has_steps__value_selection_path];
};
export class Cvalue_selection_path extends AlanNode {
	public readonly properties:{
		readonly has_steps:Cvalue_selection_path.Dhas_steps
	};
	public readonly output:{
		value: () => test.Coptional_value;
	} = {
		value: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.has_steps?.state.node.output.value()).result!)
	};
	constructor(init:Tvalue_selection_path, public location:AlanNode, public input: {
		context: () => test.Cvalue_context,
		stack: () => test.Cstack
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			has_steps: new Cvalue_selection_path.Dhas_steps(init['has steps'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tno__has_steps__value_selection_path = {
	'select':'context'|['context', {}]|'key'|['key', {}];
};
export class Cno__has_steps__value_selection_path extends AlanNode {
	public readonly properties:{
		readonly select:Cno__has_steps__value_selection_path.Dselect
	};
	public readonly output:{
		value: () => test.Coptional_value;
	} = {
		value: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.select?.state.node.output.value()).result!)
	}
	constructor(init:Tno__has_steps__value_selection_path, public parent:Cvalue_selection_path) {
		super();
		const $this = this;
		this.properties = {
			select: new Cno__has_steps__value_selection_path.Dselect(init['select'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has steps'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tcontext = {
};
export class Ccontext extends AlanNode {
	public readonly output:{
		value: () => test.Coptional_value;
	} = {
		value: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.context())
			.then(context => context?.component_root.output.value()).result!)
	}
	constructor(init:Tcontext, public parent:Cno__has_steps__value_selection_path) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'select'?'context'`; }
	public get entity() { return this.parent.entity; }
}
export type Tkey = {
};
export class Ckey extends AlanNode {
	public readonly output:{
		value: () => test.Coptional_value;
	} = {
		value: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => test.Cvalue__test.Ptext)
			.then(context => context?.definitions.optional_value).result!)
	}
	public readonly inferences:{
		is_entry: () => test.Cboolean,
		value_is_entry: () => test.Ctrue
	} = {
		is_entry: cache((detach:boolean) => {
			const test__value_selection_path__has_steps__no__select__key_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.context())
				.then(context => {
					switch (context?.variant.name) {
						case 'attribute': {
							return resolve(context)
								.then(() => this.parent)
								.then(() => test.Cboolean.Pfalse_).result;
						}
						case 'entry': {
							return resolve(context)
								.then(() => this.parent)
								.then(() => test.Cboolean.Ptrue_).result;
						}
						case 'ordered graph': {
							return resolve(context)
								.then(() => this.parent)
								.then(() => test.Cboolean.Pfalse_).result;
						}
						case 'query entry': {
							return resolve(context)
								.then(() => this.parent)
								.then(() => test.Cboolean.Ptrue_).result;
						}
						case 'rule': {
							return resolve(context)
								.then(() => this.parent)
								.then(() => test.Cboolean.Pfalse_).result;
						}
						case 'state': {
							return resolve(context)
								.then(() => this.parent)
								.then(() => test.Cboolean.Pfalse_).result;
						}
						case 'value': {
							const test__value_selection_path__has_steps__no__select__key_inf___is_entry___value_nval = context.cast('value');
							return resolve(context)
								.then(context => test__value_selection_path__has_steps__no__select__key_inf___is_entry___value_nval)
								.then(context => {
									switch (context?.variant.name) {
										case 'none': {
											return resolve(context)
												.then(() => this.parent)
												.then(() => test.Cboolean.Pfalse_).result;
										}
										case 'value': {
											const test__value_selection_path__has_steps__no__select__key_inf___is_entry___value___value_nval = context.cast('value');
											return resolve(context)
												.then(context => test__value_selection_path__has_steps__no__select__key_inf___is_entry___value___value_nval)
												.then(context => {
													switch (context?.variant.name) {
														case 'file': {
															return resolve(context)
																.then(() => this.parent)
																.then(() => test.Cboolean.Pfalse_).result;
														}
														case 'node': {
															const test__value_selection_path__has_steps__no__select__key_inf___is_entry___value___value___node_nval = context.cast('node');
															return resolve(context)
																.then(context => test__value_selection_path__has_steps__no__select__key_inf___is_entry___value___value___node_nval)
																.then(context => {
																	switch (context?.variant.name) {
																		case 'binding': {
																			const test__value_selection_path__has_steps__no__select__key_inf___is_entry___value___value___node___binding_nval = context.cast('binding');
																			return resolve(context)
																				.then(context => test__value_selection_path__has_steps__no__select__key_inf___is_entry___value___value___node___binding_nval)
																				.then(context => context?.component_root.output.binding_type())
																				.then(context => {
																					switch (context?.variant.name) {
																						case 'engine state': {
																							return resolve(context)
																								.then(() => this.parent)
																								.then(() => test.Cboolean.Pfalse_).result;
																						}
																						case 'node': {
																							const test__value_selection_path__has_steps__no__select__key_inf___is_entry___value___value___node___binding___node_nval = context.cast('node');
																							return resolve(context)
																								.then(context => test__value_selection_path__has_steps__no__select__key_inf___is_entry___value___value___node___binding___node_nval)
																								.then(context => {
																									switch (context?.variant.name) {
																										case 'collection': {
																											return resolve(context)
																												.then(() => this.parent)
																												.then(() => test.Cboolean.Pfalse_).result;
																										}
																										case 'collection entry': {
																											return resolve(context)
																												.then(() => this.parent)
																												.then(() => test.Cboolean.Ptrue_).result;
																										}
																										case 'entity': {
																											return resolve(context)
																												.then(() => this.parent)
																												.then(() => test.Cboolean.Pfalse_).result;
																										}
																										case 'file': {
																											return resolve(context)
																												.then(() => this.parent)
																												.then(() => test.Cboolean.Pfalse_).result;
																										}
																										case 'group': {
																											return resolve(context)
																												.then(() => this.parent)
																												.then(() => test.Cboolean.Pfalse_).result;
																										}
																										case 'join': {
																											return resolve(context)
																												.then(() => this.parent)
																												.then(() => test.Cboolean.Pfalse_).result;
																										}
																										case 'joined entry': {
																											return resolve(context)
																												.then(() => this.parent)
																												.then(() => test.Cboolean.Pfalse_).result;
																										}
																										case 'node': {
																											return resolve(context)
																												.then(() => this.parent)
																												.then(() => test.Cboolean.Pfalse_).result;
																										}
																										case 'number': {
																											return resolve(context)
																												.then(() => this.parent)
																												.then(() => test.Cboolean.Pfalse_).result;
																										}
																										case 'operation': {
																											return resolve(context)
																												.then(() => this.parent)
																												.then(() => test.Cboolean.Pfalse_).result;
																										}
																										case 'ordered graph': {
																											return resolve(context)
																												.then(() => this.parent)
																												.then(() => test.Cboolean.Pfalse_).result;
																										}
																										case 'query': {
																											return resolve(context)
																												.then(() => this.parent)
																												.then(() => test.Cboolean.Pfalse_).result;
																										}
																										case 'sort property': {
																											return resolve(context)
																												.then(() => this.parent)
																												.then(() => test.Cboolean.Pfalse_).result;
																										}
																										case 'state': {
																											return resolve(context)
																												.then(() => this.parent)
																												.then(() => test.Cboolean.Pfalse_).result;
																										}
																										case 'state group': {
																											return resolve(context)
																												.then(() => this.parent)
																												.then(() => test.Cboolean.Pfalse_).result;
																										}
																										case 'text': {
																											return resolve(context)
																												.then(() => this.parent)
																												.then(() => test.Cboolean.Pfalse_).result;
																										}
																										case 'where rule': {
																											return resolve(context)
																												.then(() => this.parent)
																												.then(() => test.Cboolean.Pfalse_).result;
																										}
																										case undefined: return undefined;
																										default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
																									};
																								}).result;
																						}
																						case 'query': {
																							const test__value_selection_path__has_steps__no__select__key_inf___is_entry___value___value___node___binding___query_nval = context.cast('query');
																							return resolve(context)
																								.then(context => test__value_selection_path__has_steps__no__select__key_inf___is_entry___value___value___node___binding___query_nval)
																								.then(context => {
																									switch (context?.variant.name) {
																										case 'entry': {
																											return resolve(context)
																												.then(() => this.parent)
																												.then(() => test.Cboolean.Ptrue_).result;
																										}
																										case 'file': {
																											return resolve(context)
																												.then(() => this.parent)
																												.then(() => test.Cboolean.Pfalse_).result;
																										}
																										case 'number column': {
																											return resolve(context)
																												.then(() => this.parent)
																												.then(() => test.Cboolean.Pfalse_).result;
																										}
																										case 'number entry': {
																											return resolve(context)
																												.then(() => this.parent)
																												.then(() => test.Cboolean.Pfalse_).result;
																										}
																										case 'stategroup column': {
																											return resolve(context)
																												.then(() => this.parent)
																												.then(() => test.Cboolean.Pfalse_).result;
																										}
																										case 'stategroup entry': {
																											return resolve(context)
																												.then(() => this.parent)
																												.then(() => test.Cboolean.Pfalse_).result;
																										}
																										case 'text column': {
																											return resolve(context)
																												.then(() => this.parent)
																												.then(() => test.Cboolean.Pfalse_).result;
																										}
																										case 'text entry': {
																											return resolve(context)
																												.then(() => this.parent)
																												.then(() => test.Cboolean.Pfalse_).result;
																										}
																										case undefined: return undefined;
																										default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
																									};
																								}).result;
																						}
																						case 'report': {
																							return resolve(context)
																								.then(() => this.parent)
																								.then(() => test.Cboolean.Pfalse_).result;
																						}
																						case 'role': {
																							return resolve(context)
																								.then(() => this.parent)
																								.then(() => test.Cboolean.Pfalse_).result;
																						}
																						case 'user': {
																							return resolve(context)
																								.then(() => this.parent)
																								.then(() => test.Cboolean.Pfalse_).result;
																						}
																						case 'view': {
																							return resolve(context)
																								.then(() => this.parent)
																								.then(() => test.Cboolean.Pfalse_).result;
																						}
																						case 'window': {
																							return resolve(context)
																								.then(() => this.parent)
																								.then(() => test.Cboolean.Pfalse_).result;
																						}
																						case undefined: return undefined;
																						default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
																					};
																				}).result;
																		}
																		case 'gui': {
																			const test__value_selection_path__has_steps__no__select__key_inf___is_entry___value___value___node___gui_nval = context.cast('gui');
																			return resolve(context)
																				.then(context => test__value_selection_path__has_steps__no__select__key_inf___is_entry___value___value___node___gui_nval)
																				.then(context => context?.component_root.output.location())
																				.then(context => {
																					switch (context?.variant.name) {
																						case 'collection': {
																							return resolve(context)
																								.then(() => this.parent)
																								.then(() => test.Cboolean.Ptrue_).result;
																						}
																						case 'group': {
																							return resolve(context)
																								.then(() => this.parent)
																								.then(() => test.Cboolean.Pfalse_).result;
																						}
																						case 'operation': {
																							return resolve(context)
																								.then(() => this.parent)
																								.then(() => test.Cboolean.Pfalse_).result;
																						}
																						case 'root': {
																							return resolve(context)
																								.then(() => this.parent)
																								.then(() => test.Cboolean.Pfalse_).result;
																						}
																						case 'state': {
																							return resolve(context)
																								.then(() => this.parent)
																								.then(() => test.Cboolean.Pfalse_).result;
																						}
																						case undefined: return undefined;
																						default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
																					};
																				}).result;
																		}
																		case undefined: return undefined;
																		default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
																	};
																}).result;
														}
														case 'number': {
															return resolve(context)
																.then(() => this.parent)
																.then(() => test.Cboolean.Pfalse_).result;
														}
														case 'query': {
															return resolve(context)
																.then(() => this.parent)
																.then(() => test.Cboolean.Pfalse_).result;
														}
														case 'text': {
															return resolve(context)
																.then(() => this.parent)
																.then(() => test.Cboolean.Pfalse_).result;
														}
														case 'view': {
															return resolve(context)
																.then(() => this.parent)
																.then(() => test.Cboolean.Pfalse_).result;
														}
														case undefined: return undefined;
														default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
													};
												}).result;
										}
										case undefined: return undefined;
										default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
									};
								}).result;
						}
						case undefined: return undefined;
						default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
					};
				}).result!;
		})
		,
		value_is_entry: cache((detach:boolean) => {
			const test__value_selection_path__has_steps__no__select__key_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.inferences.is_entry())
				.then(context => context?.variant.name === 'true' ? context.variant.definition as test.Ctrue : undefined).result!;
		})

	}
	constructor(init:Tkey, public parent:Cno__has_steps__value_selection_path) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'select'?'key'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_steps__value_selection_path = {
	'select':['attribute', Tattribute__select__yes]|['binding', Tbinding__select]|['collection', Tcollection__select]|['query', Tquery__select];
	'tail':Tvalue_selection_path;
};
export class Cyes__has_steps__value_selection_path extends AlanNode {
	public readonly properties:{
		readonly select:Cyes__has_steps__value_selection_path.Dselect,
		readonly tail:Cvalue_selection_path
	};
	public readonly output:{
		value: () => test.Coptional_value;
	} = {
		value: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.tail)
			.then(context => context?.component_root.output.value()).result!)
	}
	constructor(init:Tyes__has_steps__value_selection_path, public parent:Cvalue_selection_path) {
		super();
		const $this = this;
		this.properties = {
			select: new Cyes__has_steps__value_selection_path.Dselect(init['select'], $this),
			tail: new Cyes__has_steps__value_selection_path.Dtail(init['tail'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has steps'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tattribute__select__yes = {
	'attribute':string;
	'select':'attribute'|['attribute', {}]|['ordered graph', Tordered_graph]|['state', Tstate__select__attribute]|['text rule', Ttext_rule];
};
export class Cattribute__select__yes extends AlanNode {
	public readonly properties:{
		readonly attribute:Cattribute__select__yes.Dattribute,
		readonly select:Cattribute__select__yes.Dselect
	};
	public readonly output:{
		context: () => test.Cvalue_context;
	} = {
		context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.select?.state.node.output.context()).result!)
	}
	public readonly inferences:{
		node: () => test.Cnode__test,
		value: () => test.Cvalue__test
	} = {
		node: cache((detach:boolean) => {
			const test__value_selection_path__has_steps__yes__select__attribute_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.inferences.value())
				.then(context => context?.component_root.output.node())
				.then(context => context?.variant.name === 'node' ? context.variant.definition as test.Cnode__test : undefined).result!;
		})
		,
		value: cache((detach:boolean) => {
			const test__value_selection_path__has_steps__yes__select__attribute_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.context())
				.then(context => context?.component_root.output.value())
				.then(context => context?.variant.name === 'value' ? context.variant.definition as test.Cvalue__test : undefined).result!;
		})

	}
	constructor(init:Tattribute__select__yes, public parent:Cyes__has_steps__value_selection_path) {
		super();
		const $this = this;
		this.properties = {
			attribute: new Cattribute__select__yes.Dattribute(init['attribute'], $this),
			select: new Cattribute__select__yes.Dselect(init['select'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'select'?'attribute'`; }
	public get entity() { return this.parent.entity; }
}
export type Tattribute__select__attribute = {
};
export class Cattribute__select__attribute extends AlanNode {
	public readonly output:{
		context: () => test.Cvalue_context;
	} = {
		context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.parent)
			.then(context => context?.properties.attribute?.ref)
			.then(context => context?.definitions.value_context).result!)
	}
	constructor(init:Tattribute__select__attribute, public parent:Cattribute__select__yes) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'select'?'attribute'`; }
	public get entity() { return this.parent.entity; }
}
export type Tordered_graph = {
	'ordered graph':string;
};
export class Cordered_graph extends AlanNode {
	public readonly properties:{
		readonly ordered_graph:Cordered_graph.Dordered_graph
	};
	public readonly output:{
		context: () => test.Cvalue_context;
	} = {
		context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.ordered_graph?.ref)
			.then(context => {
				const conv_context = resolve(context).result!;
				return new Cvalue_context({name: 'ordered graph', definition: conv_context});
			}).result!)
	}
	public readonly inferences:{
		gui_attribute: () => gui_model.Cattributes,
		ordered_collection: () => gui_model.Cyes__has_ordered_graphs
	} = {
		gui_attribute: cache((detach:boolean) => {
			const test__value_selection_path__has_steps__yes__select__attribute__select__ordered_graph_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.properties.attribute?.ref)
				.then(context => context?.variant.name === 'gui' ? context.variant.definition as gui_model.Cattributes : undefined).result!;
		})
		,
		ordered_collection: cache((detach:boolean) => {
			const test__value_selection_path__has_steps__yes__select__attribute__select__ordered_graph_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.inferences.gui_attribute())
				.then(context => {
					if (context?.properties.type.state.name === 'property') {
						return resolve(context.properties.type.state.node as gui_model.Cproperty__type__attributes)
						.then(context => {
							if (context?.properties.type.state.name === 'collection') {
								return context.properties.type.state.node as gui_model.Ccollection__type__property;
							} else {
								return undefined;
							}
						})
						.then(context => {
							if (context?.properties.has_ordered_graphs.state.name === 'yes') {
								return context.properties.has_ordered_graphs.state.node as gui_model.Cyes__has_ordered_graphs;
							} else {
								return undefined;
							}
						}).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tordered_graph, public parent:Cattribute__select__yes) {
		super();
		const $this = this;
		this.properties = {
			ordered_graph: new Cordered_graph.Dordered_graph(init['ordered graph'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'select'?'ordered graph'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstate__select__attribute = {
	'select':'state'|['state', {}]|['state rule', Tstate_rule];
	'state':string;
};
export class Cstate__select__attribute extends AlanNode {
	public readonly properties:{
		readonly select:Cstate__select__attribute.Dselect,
		readonly state:Cstate__select__attribute.Dstate
	};
	public readonly output:{
		context: () => test.Cvalue_context;
	} = {
		context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.select?.state.node.output.context()).result!)
	}
	public readonly inferences:{
		state_group: () => test.Cstate_group
	} = {
		state_group: cache((detach:boolean) => {
			const test__value_selection_path__has_steps__yes__select__attribute__select__state_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.properties.attribute?.ref)
				.then(context => {
					switch (context?.variant.name) {
						case 'binding': {
							const test__value_selection_path__has_steps__yes__select__attribute__select__state_inf___state_group___binding_nval = context.cast('binding');
							return resolve(context)
								.then(context => test__value_selection_path__has_steps__yes__select__attribute__select__state_inf___state_group___binding_nval)
								.then(context => {
									if (context?.properties.type.state.name === 'state group') {
										return resolve(context.properties.type.state.node as client_bindings.Cstate_group__type__properties).result;
									} else {
										return undefined;
									}
								})
								.then(context => {
									const conv_context = resolve(context).result!;
									return new Cstate_group({name: 'binding', definition: conv_context});
								}).result;
						}
						case 'gui': {
							const test__value_selection_path__has_steps__yes__select__attribute__select__state_inf___state_group___gui_nval = context.cast('gui');
							return resolve(context)
								.then(context => test__value_selection_path__has_steps__yes__select__attribute__select__state_inf___state_group___gui_nval)
								.then(context => {
									if (context?.properties.type.state.name === 'property') {
										return resolve(context.properties.type.state.node as gui_model.Cproperty__type__attributes)
										.then(context => {
											if (context?.properties.type.state.name === 'state group') {
												return context.properties.type.state.node as gui_model.Cstate_group__type__property__type__attributes;
											} else {
												return undefined;
											}
										}).result;
									} else {
										return undefined;
									}
								})
								.then(context => {
									const conv_context = resolve(context).result!;
									return new Cstate_group({name: 'gui', definition: conv_context});
								}).result;
						}
						case undefined: return undefined;
						default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
					};
				}).result!;
		})

	}
	constructor(init:Tstate__select__attribute, public parent:Cattribute__select__yes) {
		super();
		const $this = this;
		this.properties = {
			select: new Cstate__select__attribute.Dselect(init['select'], $this),
			state: new Cstate__select__attribute.Dstate(init['state'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'select'?'state'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstate__select__state = {
};
export class Cstate__select__state extends AlanNode {
	public readonly output:{
		context: () => test.Cvalue_context;
	} = {
		context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.parent)
			.then(context => context?.properties.state?.ref)
			.then(context => context?.definitions.value_context).result!)
	}
	constructor(init:Tstate__select__state, public parent:Cstate__select__attribute) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'select'?'state'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstate_rule = {
	'rule':string;
};
export class Cstate_rule extends AlanNode {
	public readonly properties:{
		readonly rule:Cstate_rule.Drule
	};
	public readonly output:{
		context: () => test.Cvalue_context;
	} = {
		context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.rule?.ref)
			.then(context => {
				const conv_context = resolve(context).result!;
				return new Cvalue_context({name: 'rule', definition: conv_context});
			}).result!)
	}
	public readonly inferences:{
		gui_state: () => gui_model.Cstates__state_group__type__property__type__attributes
	} = {
		gui_state: cache((detach:boolean) => {
			const test__value_selection_path__has_steps__yes__select__attribute__select__state__select__state_rule_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.properties.state?.ref)
				.then(context => context?.variant.name === 'gui' ? context.variant.definition as gui_model.Cstates__state_group__type__property__type__attributes : undefined).result!;
		})

	}
	constructor(init:Tstate_rule, public parent:Cstate__select__attribute) {
		super();
		const $this = this;
		this.properties = {
			rule: new Cstate_rule.Drule(init['rule'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'select'?'state rule'`; }
	public get entity() { return this.parent.entity; }
}
export type Ttext_rule = {
	'rule':string;
};
export class Ctext_rule extends AlanNode {
	public readonly properties:{
		readonly rule:Ctext_rule.Drule
	};
	public readonly output:{
		context: () => test.Cvalue_context;
	} = {
		context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.rule?.ref)
			.then(context => {
				const conv_context = resolve(context).result!;
				return new Cvalue_context({name: 'rule', definition: conv_context});
			}).result!)
	}
	public readonly inferences:{
		gui_attribute: () => gui_model.Cattributes,
		reference: () => gui_model.Cyes__navigable
	} = {
		gui_attribute: cache((detach:boolean) => {
			const test__value_selection_path__has_steps__yes__select__attribute__select__text_rule_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.properties.attribute?.ref)
				.then(context => context?.variant.name === 'gui' ? context.variant.definition as gui_model.Cattributes : undefined).result!;
		})
		,
		reference: cache((detach:boolean) => {
			const test__value_selection_path__has_steps__yes__select__attribute__select__text_rule_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.inferences.gui_attribute())
				.then(context => {
					if (context?.properties.type.state.name === 'property') {
						return resolve(context.properties.type.state.node as gui_model.Cproperty__type__attributes)
						.then(context => {
							if (context?.properties.type.state.name === 'text') {
								return context.properties.type.state.node as gui_model.Ctext__type__property__type__attributes;
							} else {
								return undefined;
							}
						})
						.then(context => {
							if (context?.properties.navigable.state.name === 'yes') {
								return context.properties.navigable.state.node as gui_model.Cyes__navigable;
							} else {
								return undefined;
							}
						}).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Ttext_rule, public parent:Cattribute__select__yes) {
		super();
		const $this = this;
		this.properties = {
			rule: new Ctext_rule.Drule(init['rule'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'select'?'text rule'`; }
	public get entity() { return this.parent.entity; }
}
export type Tbinding__select = {
	'context':['binding node', Tbinding_node]|['other', Tother];
};
export class Cbinding__select extends AlanNode {
	public readonly properties:{
		readonly context:Cbinding__select.Dcontext
	};
	public readonly output:{
		context: () => test.Cvalue_context;
	} = {
		context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.context?.state.node.output.context()).result!)
	}
	constructor(init:Tbinding__select, public parent:Cyes__has_steps__value_selection_path) {
		super();
		const $this = this;
		this.properties = {
			context: new Cbinding__select.Dcontext(init['context'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'select'?'binding'`; }
	public get entity() { return this.parent.entity; }
}
export type Tbinding_node = {
	'binding':string;
};
export class Cbinding_node extends AlanNode {
	public readonly properties:{
		readonly binding:Cbinding_node.Dbinding
	};
	public readonly output:{
		context: () => test.Cvalue_context;
	} = {
		context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.binding?.inferences.node())
			.then(context => context?.definitions.value)
			.then(context => context?.definitions.optional_value)
			.then(context => context?.definitions.value_context).result!)
	}
	public readonly inferences:{
		binding_node: () => client_bindings.Cnode__client_bindings,
		value: () => test.Cvalue__test
	} = {
		binding_node: cache((detach:boolean) => {
			const test__value_selection_path__has_steps__yes__select__binding__context__binding_node_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.inferences.value())
				.then(context => context?.component_root.output.node())
				.then(context => context?.variant.name === 'node' ? context.variant.definition as test.Cnode__test : undefined)
				.then(context => context?.variant.name === 'binding' ? context.variant.definition as client_bindings.Cnode__client_bindings : undefined).result!;
		})
		,
		value: cache((detach:boolean) => {
			const test__value_selection_path__has_steps__yes__select__binding__context__binding_node_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.context())
				.then(context => context?.component_root.output.value())
				.then(context => context?.variant.name === 'value' ? context.variant.definition as test.Cvalue__test : undefined).result!;
		})

	}
	constructor(init:Tbinding_node, public parent:Cbinding__select) {
		super();
		const $this = this;
		this.properties = {
			binding: new Cbinding_node.Dbinding(init['binding'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'context'?'binding node'`; }
	public get entity() { return this.parent.entity; }
}
export type Tother = {
	'client binding':string;
};
export class Cother extends AlanNode {
	public readonly properties:{
		readonly client_binding:Cother.Dclient_binding
	};
	public readonly output:{
		context: () => test.Cvalue_context;
	} = {
		context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.client_binding?.ref)
			.then(context => context?.properties.binding)
			.then(context => {
				const conv_context = resolve(context).result!;
				return new Cnode__test({name: 'binding', definition: conv_context});
			})
			.then(context => context?.definitions.value)
			.then(context => context?.definitions.optional_value)
			.then(context => context?.definitions.value_context).result!)
	}
	constructor(init:Tother, public parent:Cbinding__select) {
		super();
		const $this = this;
		this.properties = {
			client_binding: new Cother.Dclient_binding(init['client binding'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'context'?'other'`; }
	public get entity() { return this.parent.entity; }
}
export type Tcollection__select = {
	'select':['entry', Tentry__select]|'size'|['size', {}];
};
export class Ccollection__select extends AlanNode {
	public readonly properties:{
		readonly select:Ccollection__select.Dselect
	};
	public readonly output:{
		context: () => test.Cvalue_context;
	} = {
		context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.select?.state.node.output.context()).result!)
	}
	public readonly inferences:{
		entry_container: () => test.Cvalue_context
	} = {
		entry_container: cache((detach:boolean) => {
			const test__value_selection_path__has_steps__yes__select__collection_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.context())
				.then(context => {
					switch (context?.variant.name) {
						case 'attribute': {
							const test__value_selection_path__has_steps__yes__select__collection_inf___entry_container___attribute_nval = context.cast('attribute');
							return resolve(context)
								.then(context => test__value_selection_path__has_steps__yes__select__collection_inf___entry_container___attribute_nval)
								.then(context => {
									switch (context?.variant.name) {
										case 'binding': {
											const test__value_selection_path__has_steps__yes__select__collection_inf___entry_container___attribute___binding_nval = context.cast('binding');
											return resolve(context)
												.then(context => test__value_selection_path__has_steps__yes__select__collection_inf___entry_container___attribute___binding_nval)
												.then(context => context?.properties.type)
												.then(context => {
													const state_group = context;
													switch (state_group?.state.name) {
														case 'collection': {
															const test__value_selection_path__has_steps__yes__select__collection_inf___entry_container___attribute___binding___collection_nval = state_group.cast('collection');
															return resolve(context)
																.then(context => test__value_selection_path__has_steps__yes__select__collection_inf___entry_container___attribute___binding___collection_nval)
																.then(context => {
																	const conv_context = resolve(context).result!;
																	return new Ccollection__test({name: 'binding', definition: conv_context});
																})
																.then(context => context?.definitions.value_context).result;
														}
														case 'number': {
															return resolve(context)
																.then(() => this.parent)
																.then(() => test.Coptional_value.Pnone)
																.then(context => context?.definitions.value_context).result;
														}
														case 'reference': {
															return resolve(context)
																.then(() => this.parent)
																.then(() => test.Coptional_value.Pnone)
																.then(context => context?.definitions.value_context).result;
														}
														case 'state group': {
															return resolve(context)
																.then(() => this.parent)
																.then(() => test.Coptional_value.Pnone)
																.then(context => context?.definitions.value_context).result;
														}
														case 'text': {
															return resolve(context)
																.then(() => this.parent)
																.then(() => test.Coptional_value.Pnone)
																.then(context => context?.definitions.value_context).result;
														}
														case undefined: return undefined;
														default: throw new Error(`Unexpected state '${(state_group!.state as any).name}'`);
													};}).result;
										}
										case 'gui': {
											const test__value_selection_path__has_steps__yes__select__collection_inf___entry_container___attribute___gui_nval = context.cast('gui');
											return resolve(context)
												.then(context => test__value_selection_path__has_steps__yes__select__collection_inf___entry_container___attribute___gui_nval)
												.then(context => context?.properties.type)
												.then(context => {
													const state_group = context;
													switch (state_group?.state.name) {
														case 'action': {
															return resolve(context)
																.then(() => this.parent)
																.then(() => test.Coptional_value.Pnone)
																.then(context => context?.definitions.value_context).result;
														}
														case 'command': {
															return resolve(context)
																.then(() => this.parent)
																.then(() => test.Coptional_value.Pnone)
																.then(context => context?.definitions.value_context).result;
														}
														case 'property': {
															const test__value_selection_path__has_steps__yes__select__collection_inf___entry_container___attribute___gui___property_nval = state_group.cast('property');
															return resolve(context)
																.then(context => test__value_selection_path__has_steps__yes__select__collection_inf___entry_container___attribute___gui___property_nval)
																.then(context => context?.properties.type)
																.then(context => {
																	const state_group = context;
																	switch (state_group?.state.name) {
																		case 'collection': {
																			const test__value_selection_path__has_steps__yes__select__collection_inf___entry_container___attribute___gui___property___collection_nval = state_group.cast('collection');
																			return resolve(context)
																				.then(context => test__value_selection_path__has_steps__yes__select__collection_inf___entry_container___attribute___gui___property___collection_nval)
																				.then(context => {
																					const conv_context = resolve(context).result!;
																					return new Ccollection__test({name: 'gui', definition: conv_context});
																				})
																				.then(context => context?.definitions.value_context).result;
																		}
																		case 'file': {
																			return resolve(context)
																				.then(() => this.parent)
																				.then(() => test.Coptional_value.Pnone)
																				.then(context => context?.definitions.value_context).result;
																		}
																		case 'group': {
																			return resolve(context)
																				.then(() => this.parent)
																				.then(() => test.Coptional_value.Pnone)
																				.then(context => context?.definitions.value_context).result;
																		}
																		case 'number': {
																			return resolve(context)
																				.then(() => this.parent)
																				.then(() => test.Coptional_value.Pnone)
																				.then(context => context?.definitions.value_context).result;
																		}
																		case 'state group': {
																			return resolve(context)
																				.then(() => this.parent)
																				.then(() => test.Coptional_value.Pnone)
																				.then(context => context?.definitions.value_context).result;
																		}
																		case 'text': {
																			return resolve(context)
																				.then(() => this.parent)
																				.then(() => test.Coptional_value.Pnone)
																				.then(context => context?.definitions.value_context).result;
																		}
																		case undefined: return undefined;
																		default: throw new Error(`Unexpected state '${(state_group!.state as any).name}'`);
																	};}).result;
														}
														case 'reference set': {
															return resolve(context)
																.then(() => this.parent)
																.then(() => test.Coptional_value.Pnone)
																.then(context => context?.definitions.value_context).result;
														}
														case undefined: return undefined;
														default: throw new Error(`Unexpected state '${(state_group!.state as any).name}'`);
													};}).result;
										}
										case undefined: return undefined;
										default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
									};
								}).result;
						}
						case 'entry': {
							return resolve(context)
								.then(() => this.parent)
								.then(() => test.Coptional_value.Pnone)
								.then(context => context?.definitions.value_context).result;
						}
						case 'ordered graph': {
							return resolve(context)
								.then(() => this.parent)
								.then(() => test.Coptional_value.Pnone)
								.then(context => context?.definitions.value_context).result;
						}
						case 'query entry': {
							return resolve(context)
								.then(() => this.parent)
								.then(() => test.Coptional_value.Pnone)
								.then(context => context?.definitions.value_context).result;
						}
						case 'rule': {
							return resolve(context)
								.then(() => this.parent)
								.then(() => test.Coptional_value.Pnone)
								.then(context => context?.definitions.value_context).result;
						}
						case 'state': {
							return resolve(context)
								.then(() => this.parent)
								.then(() => test.Coptional_value.Pnone)
								.then(context => context?.definitions.value_context).result;
						}
						case 'value': {
							const test__value_selection_path__has_steps__yes__select__collection_inf___entry_container___value_nval = context.cast('value');
							return resolve(context)
								.then(context => test__value_selection_path__has_steps__yes__select__collection_inf___entry_container___value_nval)
								.then(context => {
									switch (context?.variant.name) {
										case 'none': {
											return resolve(context)
												.then(() => this.parent)
												.then(() => test.Coptional_value.Pnone)
												.then(context => context?.definitions.value_context).result;
										}
										case 'value': {
											const test__value_selection_path__has_steps__yes__select__collection_inf___entry_container___value___value_nval = context.cast('value');
											return resolve(context)
												.then(context => test__value_selection_path__has_steps__yes__select__collection_inf___entry_container___value___value_nval)
												.then(context => {
													switch (context?.variant.name) {
														case 'file': {
															return resolve(context)
																.then(() => this.parent)
																.then(() => test.Coptional_value.Pnone)
																.then(context => context?.definitions.value_context).result;
														}
														case 'node': {
															return resolve(context)
																.then(() => this.parent)
																.then(() => test.Coptional_value.Pnone)
																.then(context => context?.definitions.value_context).result;
														}
														case 'number': {
															return resolve(context)
																.then(() => this.parent)
																.then(() => test.Coptional_value.Pnone)
																.then(context => context?.definitions.value_context).result;
														}
														case 'query': {
															const test__value_selection_path__has_steps__yes__select__collection_inf___entry_container___value___value___query_nval = context.cast('query');
															return resolve(context)
																.then(context => test__value_selection_path__has_steps__yes__select__collection_inf___entry_container___value___value___query_nval)
																.then(context => {
																	const conv_context = resolve(context).result!;
																	return new Cvalue_context({name: 'query entry', definition: conv_context});
																}).result;
														}
														case 'text': {
															return resolve(context)
																.then(() => this.parent)
																.then(() => test.Coptional_value.Pnone)
																.then(context => context?.definitions.value_context).result;
														}
														case 'view': {
															return resolve(context)
																.then(() => this.parent)
																.then(() => test.Coptional_value.Pnone)
																.then(context => context?.definitions.value_context).result;
														}
														case undefined: return undefined;
														default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
													};
												}).result;
										}
										case undefined: return undefined;
										default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
									};
								}).result;
						}
						case undefined: return undefined;
						default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
					};
				}).result!;
		})

	}
	constructor(init:Tcollection__select, public parent:Cyes__has_steps__value_selection_path) {
		super();
		const $this = this;
		this.properties = {
			select: new Ccollection__select.Dselect(init['select'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'select'?'collection'`; }
	public get entity() { return this.parent.entity; }
}
export type Tentry__select = {
	'key':Tvalue_expression;
};
export class Centry__select extends AlanNode {
	public readonly properties:{
		readonly key:Cvalue_expression & { readonly inferences: {
			text: () => test.Ctext__value;
		} }
	};
	public readonly output:{
		context: () => test.Cvalue_context;
	} = {
		context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.parent)
			.then(context => context?.inferences.entry_container()).result!)
	}
	constructor(init:Tentry__select, public parent:Ccollection__select) {
		super();
		const $this = this;
		this.properties = {
			key: new Centry__select.Dkey(init['key'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'select'?'entry'`; }
	public get entity() { return this.parent.entity; }
}
export type Tsize = {
};
export class Csize extends AlanNode {
	public readonly output:{
		context: () => test.Cvalue_context;
	} = {
		context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => test.Cvalue__test.Pnumber)
			.then(context => context?.definitions.optional_value)
			.then(context => context?.definitions.value_context).result!)
	}
	constructor(init:Tsize, public parent:Ccollection__select) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'select'?'size'`; }
	public get entity() { return this.parent.entity; }
}
export type Tquery__select = {
	'query':string;
	'select view':['expression', Texpression__select_view]|'from context'|['from context', {}];
};
export class Cquery__select extends AlanNode {
	public readonly properties:{
		readonly query:Cquery__select.Dquery,
		readonly select_view:Cquery__select.Dselect_view
	};
	public readonly output:{
		context: () => test.Cvalue_context;
	} = {
		context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.query?.ref)
			.then(context => {
				const conv_context = resolve(context).result!;
				return new Cvalue__test({name: 'query', definition: conv_context});
			})
			.then(context => context?.definitions.optional_value)
			.then(context => context?.definitions.value_context).result!)
	}
	constructor(init:Tquery__select, public parent:Cyes__has_steps__value_selection_path) {
		super();
		const $this = this;
		this.properties = {
			query: new Cquery__select.Dquery(init['query'], $this),
			select_view: new Cquery__select.Dselect_view(init['select view'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'select'?'query'`; }
	public get entity() { return this.parent.entity; }
}
export type Texpression__select_view = {
	'view selection':Tvalue_expression;
};
export class Cexpression__select_view extends AlanNode {
	public readonly properties:{
		readonly view_selection:Cvalue_expression & { readonly inferences: {
			view: () => test.Cexplicit_view;
		} }
	};
	public readonly output:{
		view: () => test.Cexplicit_view;
	} = {
		view: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.view_selection?.inferences.view()).result!)
	}
	constructor(init:Texpression__select_view, public parent:Cquery__select) {
		super();
		const $this = this;
		this.properties = {
			view_selection: new Cexpression__select_view.Dview_selection(init['view selection'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'select view'?'expression'`; }
	public get entity() { return this.parent.entity; }
}
export type Tfrom_context = {
};
export class Cfrom_context extends AlanNode {
	public readonly output:{
		view: () => test.Cexplicit_view;
	} = {
		view: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.inferences.view()).result!)
	}
	public readonly inferences:{
		value: () => test.Cvalue__test,
		view: () => test.Cexplicit_view
	} = {
		value: cache((detach:boolean) => {
			const test__value_selection_path__has_steps__yes__select__query__select_view__from_context_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.context())
				.then(context => context?.component_root.output.value())
				.then(context => context?.variant.name === 'value' ? context.variant.definition as test.Cvalue__test : undefined).result!;
		})
		,
		view: cache((detach:boolean) => {
			const test__value_selection_path__has_steps__yes__select__query__select_view__from_context_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.inferences.value())
				.then(context => context?.variant.name === 'view' ? context.variant.definition as test.Cview__test : undefined)
				.then(context => context?.variant.name === 'explicit' ? context.variant.definition as test.Cexplicit_view : undefined).result!;
		})

	}
	constructor(init:Tfrom_context, public parent:Cquery__select) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'select view'?'from context'`; }
	public get entity() { return this.parent.entity; }
}
export type Tvalue_expression = {
	'type':['select', Tselect]|['static', Tstatic];
};
export class Cvalue_expression extends AlanNode {
	public readonly properties:{
		readonly type:Cvalue_expression.Dtype
	};
	public readonly output:{
		value: () => test.Coptional_value;
	} = {
		value: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.type?.state.node.output.value()).result!)
	};
	constructor(init:Tvalue_expression, public location:AlanNode, public input: {
		stack: () => test.Cstack
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			type: new Cvalue_expression.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tselect = {
	'selection':Tvalue_selection_path;
	'start from':['named value', Tnamed_value]|'root node'|['root node', {}]|['run procedure', Trun_procedure];
};
export class Cselect extends AlanNode {
	public readonly properties:{
		readonly selection:Cvalue_selection_path,
		readonly start_from:Cselect.Dstart_from
	};
	public readonly output:{
		value: () => test.Coptional_value;
	} = {
		value: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.selection)
			.then(context => context?.component_root.output.value()).result!)
	}
	constructor(init:Tselect, public parent:Cvalue_expression) {
		super();
		const $this = this;
		this.properties = {
			selection: new Cselect.Dselection(init['selection'], $this),
			start_from: new Cselect.Dstart_from(init['start from'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'select'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnamed_value = {
	'selector':Tstack_selector;
};
export class Cnamed_value extends AlanNode {
	public readonly properties:{
		readonly selector:Cstack_selector
	};
	public readonly output:{
		value: () => test.Coptional_value;
	} = {
		value: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.selector)
			.then(context => context?.component_root.output.value())
			.then(context => context?.definitions.optional_value).result!)
	}
	constructor(init:Tnamed_value, public parent:Cselect) {
		super();
		const $this = this;
		this.properties = {
			selector: new Cnamed_value.Dselector(init['selector'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'start from'?'named value'`; }
	public get entity() { return this.parent.entity; }
}
export type Troot_node = {
};
export class Croot_node extends AlanNode {
	public readonly output:{
		value: () => test.Coptional_value;
	} = {
		value: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.root.input.gui_model)
			.then(context => context?.properties.root)
			.then(context => {
				const conv_context = resolve(context).result!;
				return new Cnode__test({name: 'gui', definition: conv_context});
			})
			.then(context => context?.definitions.value)
			.then(context => context?.definitions.optional_value).result!)
	}
	constructor(init:Troot_node, public parent:Cselect) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'start from'?'root node'`; }
	public get entity() { return this.parent.entity; }
}
export type Trun_procedure = {
	'has parameters':'no'|['no', {}]|['yes', Tyes__has_parameters__run_procedure];
	'procedure':string;
};
export class Crun_procedure extends AlanNode {
	public readonly properties:{
		readonly has_parameters:Crun_procedure.Dhas_parameters,
		readonly procedure:Crun_procedure.Dprocedure
	};
	public readonly output:{
		value: () => test.Coptional_value;
	} = {
		value: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.procedure?.ref)
			.then(context => context?.properties.body)
			.then(context => context?.properties.output_type?.state.node.output.value()).result!)
	}
	constructor(init:Trun_procedure, public parent:Cselect) {
		super();
		const $this = this;
		this.properties = {
			has_parameters: new Crun_procedure.Dhas_parameters(init['has parameters'], $this),
			procedure: new Crun_procedure.Dprocedure(init['procedure'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'start from'?'run procedure'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__has_parameters__run_procedure = {
};
export class Cno__has_parameters__run_procedure extends AlanNode {
	public readonly inferences:{
		no: () => test.Cno__has_parameters__procedures
	} = {
		no: cache((detach:boolean) => {
			const test__value_expression__type__select__start_from__run_procedure__has_parameters__no_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.properties.procedure?.ref)
				.then(context => {
					if (context?.properties.has_parameters.state.name === 'no') {
						return resolve(context.properties.has_parameters.state.node as test.Cno__has_parameters__procedures).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tno__has_parameters__run_procedure, public parent:Crun_procedure) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has parameters'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_parameters__run_procedure = {
	'parameters':Tparameter_implementation;
};
export class Cyes__has_parameters__run_procedure extends AlanNode {
	public readonly properties:{
		readonly parameters:Cparameter_implementation
	};
	public readonly inferences:{
		yes: () => test.Cyes__has_parameters__procedures
	} = {
		yes: cache((detach:boolean) => {
			const test__value_expression__type__select__start_from__run_procedure__has_parameters__yes_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.properties.procedure?.ref)
				.then(context => {
					if (context?.properties.has_parameters.state.name === 'yes') {
						return resolve(context.properties.has_parameters.state.node as test.Cyes__has_parameters__procedures).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tyes__has_parameters__run_procedure, public parent:Crun_procedure) {
		super();
		const $this = this;
		this.properties = {
			parameters: new Cyes__has_parameters__run_procedure.Dparameters(init['parameters'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has parameters'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstatic = {
	'type':['file', Tfile__type__static]|['number', Tnumber__type__static]|['open view', Topen_view__type]|['text', Ttext__type__static]|['view definition', Tview_definition__type];
};
export class Cstatic extends AlanNode {
	public readonly properties:{
		readonly type:Cstatic.Dtype
	};
	public readonly output:{
		value: () => test.Coptional_value;
	} = {
		value: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.type?.state.node.output.value()).result!)
	}
	constructor(init:Tstatic, public parent:Cvalue_expression) {
		super();
		const $this = this;
		this.properties = {
			type: new Cstatic.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'static'`; }
	public get entity() { return this.parent.entity; }
}
export type Tfile__type__static = {
	'filename':string;
};
export class Cfile__type__static extends AlanNode {
	public readonly properties:{
		readonly filename:string
	};
	public readonly output:{
		value: () => test.Coptional_value;
	} = {
		value: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => test.Cvalue__test.Pfile)
			.then(context => context?.definitions.optional_value).result!)
	}
	constructor(init:Tfile__type__static, public parent:Cstatic) {
		super();
		const $this = this;
		this.properties = {
			filename: init['filename']
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'file'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnumber__type__static = {
	'value':number;
};
export class Cnumber__type__static extends AlanNode {
	public readonly properties:{
		readonly value:Cnumber__type__static.Dvalue
	};
	public readonly output:{
		value: () => test.Coptional_value;
	} = {
		value: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => test.Cvalue__test.Pnumber)
			.then(context => context?.definitions.optional_value).result!)
	}
	constructor(init:Tnumber__type__static, public parent:Cstatic) {
		super();
		const $this = this;
		this.properties = {
			value: new Cnumber__type__static.Dvalue(init['value'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'number'`; }
	public get entity() { return this.parent.entity; }
}
export type Topen_view__type = {
	'on node':Tvalue_expression;
	'view selection':Tvalue_expression;
};
export class Copen_view__type extends AlanNode {
	public readonly properties:{
		readonly on_node:Cvalue_expression & { readonly inferences: {
			equal_view_context: () => gui_model.Cgui_node;
			gui_node: () => gui_model.Cgui_node;
			value_exists: () => test.Cvalue__test;
		} },
		readonly view_selection:Cvalue_expression & { readonly inferences: {
			view: () => test.Cview__test;
		} }
	};
	public readonly output:{
		value: () => test.Coptional_value;
	} = {
		value: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.view_selection?.inferences.view())
			.then(context => {
				switch (context?.variant.name) {
					case 'explicit': {
						const test__value_expression__type__static__type__open_view_sout___value___explicit_nval = context.cast('explicit');
						return resolve(context)
							.then(context => test__value_expression__type__static__type__open_view_sout___value___explicit_nval)
							.then(context => context?.component_root.output.definition())
							.then(context => {
								const conv_context = resolve(context).result!;
								return new Cexplicit_view({name: 'open view', definition: conv_context});
							})
							.then(context => context?.definitions.view)
							.then(context => context?.definitions.value)
							.then(context => context?.definitions.optional_value).result;
					}
					case 'implicit': {
						return resolve(context)
							.then(() => this)
							.then(() => test.Cimplicit_view.Popen_view)
							.then(context => context?.definitions.view)
							.then(context => context?.definitions.value)
							.then(context => context?.definitions.optional_value).result;
					}
					case undefined: return undefined;
					default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
				};
			}).result!)
	}
	constructor(init:Topen_view__type, public parent:Cstatic) {
		super();
		const $this = this;
		this.properties = {
			on_node: new Copen_view__type.Don_node(init['on node'], $this),
			view_selection: new Copen_view__type.Dview_selection(init['view selection'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'open view'`; }
	public get entity() { return this.parent.entity; }
}
export type Ttext__type__static = {
	'value':string;
};
export class Ctext__type__static extends AlanNode {
	public readonly properties:{
		readonly value:string
	};
	public readonly output:{
		value: () => test.Coptional_value;
	} = {
		value: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => test.Cvalue__test.Ptext)
			.then(context => context?.definitions.optional_value).result!)
	}
	constructor(init:Ttext__type__static, public parent:Cstatic) {
		super();
		const $this = this;
		this.properties = {
			value: init['value']
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'text'`; }
	public get entity() { return this.parent.entity; }
}
export type Tview_definition__type = {
	'select views':['choose', Tchoose__select_views__view_definition]|'default'|['default', {}];
	'select window':['choose', Tchoose__select_window]|'default'|['default', {}];
	'view':string;
};
export class Cview_definition__type extends AlanNode {
	public readonly properties:{
		readonly select_views:Cview_definition__type.Dselect_views,
		readonly select_window:Cview_definition__type.Dselect_window,
		readonly view:Cview_definition__type.Dview
	};
	public readonly output:{
		value: () => test.Coptional_value;
	} = {
		value: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.view?.ref)
			.then(context => {
				const conv_context = resolve(context).result!;
				return new Cexplicit_view({name: 'view definition', definition: conv_context});
			})
			.then(context => context?.definitions.view)
			.then(context => context?.definitions.value)
			.then(context => context?.definitions.optional_value).result!)
	}
	constructor(init:Tview_definition__type, public parent:Cstatic) {
		super();
		const $this = this;
		this.properties = {
			select_views: new Cview_definition__type.Dselect_views(init['select views'], $this),
			select_window: new Cview_definition__type.Dselect_window(init['select window'], $this),
			view: new Cview_definition__type.Dview(init['view'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'view definition'`; }
	public get entity() { return this.parent.entity; }
}
export type Tchoose__select_views__view_definition = {
	'views':string;
};
export class Cchoose__select_views__view_definition extends AlanNode {
	public readonly properties:{
		readonly views:Cchoose__select_views__view_definition.Dviews
	};
	public readonly output:{
		views: () => views.Cviews;
	} = {
		views: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.views?.ref).result!)
	}
	constructor(init:Tchoose__select_views__view_definition, public parent:Cview_definition__type) {
		super();
		const $this = this;
		this.properties = {
			views: new Cchoose__select_views__view_definition.Dviews(init['views'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'select views'?'choose'`; }
	public get entity() { return this.parent.entity; }
}
export type Tdefault__select_views__view_definition = {
};
export class Cdefault__select_views__view_definition extends AlanNode {
	public readonly output:{
		views: () => views.Cviews;
	} = {
		views: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.inferences.default_views_is_set())
			.then(context => context?.properties.views_lib?.ref).result!)
	}
	public readonly inferences:{
		default_views_is_set: () => test.Cset__default_views_lib
	} = {
		default_views_is_set: cache((detach:boolean) => {
			const test__value_expression__type__static__type__view_definition__select_views__default_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.root)
				.then(context => {
					if (context?.properties.default_views_lib.state.name === 'set') {
						return resolve(context.properties.default_views_lib.state.node as test.Cset__default_views_lib).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tdefault__select_views__view_definition, public parent:Cview_definition__type) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'select views'?'default'`; }
	public get entity() { return this.parent.entity; }
}
export type Tchoose__select_window = {
	'window':string;
};
export class Cchoose__select_window extends AlanNode {
	public readonly properties:{
		readonly window:Cchoose__select_window.Dwindow
	};
	public readonly output:{
		window: () => settings.Cwindows;
	} = {
		window: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.window?.ref).result!)
	}
	constructor(init:Tchoose__select_window, public parent:Cview_definition__type) {
		super();
		const $this = this;
		this.properties = {
			window: new Cchoose__select_window.Dwindow(init['window'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'select window'?'choose'`; }
	public get entity() { return this.parent.entity; }
}
export type Tdefault__select_window = {
};
export class Cdefault__select_window extends AlanNode {
	public readonly output:{
		window: () => settings.Cwindows;
	} = {
		window: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.inferences.default_window_is_set())
			.then(context => context?.properties.window?.ref).result!)
	}
	public readonly inferences:{
		default_window_is_set: () => test.Cset__default_window
	} = {
		default_window_is_set: cache((detach:boolean) => {
			const test__value_expression__type__static__type__view_definition__select_window__default_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.root)
				.then(context => {
					if (context?.properties.default_window.state.name === 'set') {
						return resolve(context.properties.default_window.state.node as test.Cset__default_window).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tdefault__select_window, public parent:Cview_definition__type) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'select window'?'default'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstatements = {
	'has next':'no'|['no', {}]|['yes', Tyes__has_next__statements];
	'statement':Tstatement;
};
export class Cstatements extends AlanNode {
	public readonly properties:{
		readonly has_next:Cstatements.Dhas_next,
		readonly statement:Cstatement
	};
	public readonly output:{
		stack: () => test.Cstack;
	} = {
		stack: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.has_next?.state.node.output.stack()).result!)
	};
	constructor(init:Tstatements, public location:AlanNode, public input: {
		stack: () => test.Cstack
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			has_next: new Cstatements.Dhas_next(init['has next'], $this),
			statement: new Cstatements.Dstatement(init['statement'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tno__has_next__statements = {
};
export class Cno__has_next__statements extends AlanNode {
	public readonly output:{
		stack: () => test.Cstack;
	} = {
		stack: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.parent)
			.then(context => context?.properties.statement)
			.then(context => context?.component_root.output.stack()).result!)
	}
	constructor(init:Tno__has_next__statements, public parent:Cstatements) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has next'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_next__statements = {
	'statements':Tstatements;
};
export class Cyes__has_next__statements extends AlanNode {
	public readonly properties:{
		readonly statements:Cstatements
	};
	public readonly output:{
		stack: () => test.Cstack;
	} = {
		stack: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.statements)
			.then(context => context?.component_root.output.stack()).result!)
	}
	constructor(init:Tyes__has_next__statements, public parent:Cstatements) {
		super();
		const $this = this;
		this.properties = {
			statements: new Cyes__has_next__statements.Dstatements(init['statements'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has next'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstatement = {
	'type':['assertion', Tassertion]|['expression', Texpression__type]|['if', Tif]|['instruction', Tinstruction]|['panic', Tpanic]|['set network conditions', Tset_network_conditions]|['set user', Tset_user]|'unset user'|['unset user', {}]|['wait', Twait]|['walk', Twalk];
};
export class Cstatement extends AlanNode {
	public readonly properties:{
		readonly type:Cstatement.Dtype
	};
	public readonly output:{
		stack: () => test.Cstack;
	} = {
		stack: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.type?.state.node.output.stack()).result!)
	};
	constructor(init:Tstatement, public location:AlanNode, public input: {
		stack: () => test.Cstack
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			type: new Cstatement.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tassertion = {
	'condition':Tcondition;
};
export class Cassertion extends AlanNode {
	public readonly properties:{
		readonly condition:Ccondition
	};
	public readonly output:{
		stack: () => test.Cstack;
	} = {
		stack: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.condition)
			.then(context => context?.component_root.output.stack()).result!)
	}
	constructor(init:Tassertion, public parent:Cstatement) {
		super();
		const $this = this;
		this.properties = {
			condition: new Cassertion.Dcondition(init['condition'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'assertion'`; }
	public get entity() { return this.parent.entity; }
}
export type Texpression__type = {
	'assignment':Toptional_assignment;
	'value expression':Tvalue_expression;
};
export class Cexpression__type extends AlanNode {
	public readonly properties:{
		readonly assignment:Coptional_assignment,
		readonly value_expression:Cvalue_expression
	};
	public readonly output:{
		stack: () => test.Cstack;
	} = {
		stack: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.assignment)
			.then(context => context?.component_root.output.stack()).result!)
	}
	constructor(init:Texpression__type, public parent:Cstatement) {
		super();
		const $this = this;
		this.properties = {
			assignment: new Cexpression__type.Dassignment(init['assignment'], $this),
			value_expression: new Cexpression__type.Dvalue_expression(init['value expression'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'expression'`; }
	public get entity() { return this.parent.entity; }
}
export type Tif = {
	'condition':Tcondition;
	'else':'nothing'|['nothing', {}]|['something', Tsomething];
	'then':Tstatements;
};
export class Cif extends AlanNode {
	public readonly properties:{
		readonly condition:Ccondition,
		readonly else_:Cif.Delse_,
		readonly then:Cstatements
	};
	public readonly output:{
		stack: () => test.Cstack;
	} = {
		stack: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.stack()).result!)
	}
	constructor(init:Tif, public parent:Cstatement) {
		super();
		const $this = this;
		this.properties = {
			condition: new Cif.Dcondition(init['condition'], $this),
			else_: new Cif.Delse_(init['else'], $this),
			then: new Cif.Dthen(init['then'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'if'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnothing = {
};
export class Cnothing extends AlanNode {
	constructor(init:Tnothing, public parent:Cif) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'else'?'nothing'`; }
	public get entity() { return this.parent.entity; }
}
export type Tsomething = {
	'statements':Tstatements;
};
export class Csomething extends AlanNode {
	public readonly properties:{
		readonly statements:Cstatements
	};
	constructor(init:Tsomething, public parent:Cif) {
		super();
		const $this = this;
		this.properties = {
			statements: new Csomething.Dstatements(init['statements'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'else'?'something'`; }
	public get entity() { return this.parent.entity; }
}
export type Tinstruction = {
	'has arguments':'no'|['no', {}]|['yes', Tyes__has_arguments];
	'ignore errors':'no'|['no', {}]|'yes'|['yes', {}];
	'instruction':string;
	'value expression':Tvalue_expression;
	'wait on finish':'no'|['no', {}]|'yes'|['yes', {}];
};
export class Cinstruction extends AlanNode {
	public readonly properties:{
		readonly has_arguments:Cinstruction.Dhas_arguments,
		readonly ignore_errors:Cinstruction.Dignore_errors,
		readonly instruction:Cinstruction.Dinstruction,
		readonly value_expression:Cvalue_expression & { readonly inferences: {
			binding_node: () => client_bindings.Cnode__client_bindings;
		} },
		readonly wait_on_finish:Cinstruction.Dwait_on_finish
	};
	public readonly output:{
		stack: () => test.Cstack;
	} = {
		stack: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.stack()).result!)
	}
	constructor(init:Tinstruction, public parent:Cstatement) {
		super();
		const $this = this;
		this.properties = {
			has_arguments: new Cinstruction.Dhas_arguments(init['has arguments'], $this),
			ignore_errors: new Cinstruction.Dignore_errors(init['ignore errors'], $this),
			instruction: new Cinstruction.Dinstruction(init['instruction'], $this),
			value_expression: new Cinstruction.Dvalue_expression(init['value expression'], $this),
			wait_on_finish: new Cinstruction.Dwait_on_finish(init['wait on finish'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'instruction'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__has_arguments = {
};
export class Cno__has_arguments extends AlanNode {
	public readonly inferences:{
		no: () => client_bindings.Cno__has_arguments
	} = {
		no: cache((detach:boolean) => {
			const test__statement__type__instruction__has_arguments__no_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.properties.instruction?.ref)
				.then(context => {
					if (context?.properties.has_arguments.state.name === 'no') {
						return resolve(context.properties.has_arguments.state.node as client_bindings.Cno__has_arguments).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tno__has_arguments, public parent:Cinstruction) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has arguments'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_arguments = {
	'first argument':Tinstruction_argument;
};
export class Cyes__has_arguments extends AlanNode {
	public readonly properties:{
		readonly first_argument:Cinstruction_argument
	};
	public readonly inferences:{
		yes: () => client_bindings.Cyes__has_arguments
	} = {
		yes: cache((detach:boolean) => {
			const test__statement__type__instruction__has_arguments__yes_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.properties.instruction?.ref)
				.then(context => {
					if (context?.properties.has_arguments.state.name === 'yes') {
						return resolve(context.properties.has_arguments.state.node as client_bindings.Cyes__has_arguments).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tyes__has_arguments, public parent:Cinstruction) {
		super();
		const $this = this;
		this.properties = {
			first_argument: new Cyes__has_arguments.Dfirst_argument(init['first argument'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has arguments'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__ignore_errors = {
};
export class Cno__ignore_errors extends AlanNode {
	constructor(init:Tno__ignore_errors, public parent:Cinstruction) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'ignore errors'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__ignore_errors = {
};
export class Cyes__ignore_errors extends AlanNode {
	constructor(init:Tyes__ignore_errors, public parent:Cinstruction) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'ignore errors'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__wait_on_finish = {
};
export class Cno__wait_on_finish extends AlanNode {
	constructor(init:Tno__wait_on_finish, public parent:Cinstruction) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'wait on finish'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__wait_on_finish = {
};
export class Cyes__wait_on_finish extends AlanNode {
	constructor(init:Tyes__wait_on_finish, public parent:Cinstruction) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'wait on finish'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tpanic = {
	'message':string;
};
export class Cpanic extends AlanNode {
	public readonly properties:{
		readonly message:string
	};
	public readonly output:{
		stack: () => test.Cstack;
	} = {
		stack: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.stack()).result!)
	}
	constructor(init:Tpanic, public parent:Cstatement) {
		super();
		const $this = this;
		this.properties = {
			message: init['message']
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'panic'`; }
	public get entity() { return this.parent.entity; }
}
export type Tset_network_conditions = {
	'preset':'DSL'|['DSL', {}]|'GPRS'|['GPRS', {}]|'WiFi'|['WiFi', {}]|'good 2G'|['good 2G', {}]|'good 3G'|['good 3G', {}]|'no throttling'|['no throttling', {}]|'offline'|['offline', {}]|'regular 2G'|['regular 2G', {}]|'regular 3G'|['regular 3G', {}]|'regular 4G'|['regular 4G', {}];
};
export class Cset_network_conditions extends AlanNode {
	public readonly properties:{
		readonly preset:Cset_network_conditions.Dpreset
	};
	public readonly output:{
		stack: () => test.Cstack;
	} = {
		stack: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.stack()).result!)
	}
	constructor(init:Tset_network_conditions, public parent:Cstatement) {
		super();
		const $this = this;
		this.properties = {
			preset: new Cset_network_conditions.Dpreset(init['preset'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'set network conditions'`; }
	public get entity() { return this.parent.entity; }
}
export type TDSL = {
};
export class CDSL extends AlanNode {
	constructor(init:TDSL, public parent:Cset_network_conditions) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'preset'?'DSL'`; }
	public get entity() { return this.parent.entity; }
}
export type TGPRS = {
};
export class CGPRS extends AlanNode {
	constructor(init:TGPRS, public parent:Cset_network_conditions) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'preset'?'GPRS'`; }
	public get entity() { return this.parent.entity; }
}
export type TWiFi = {
};
export class CWiFi extends AlanNode {
	constructor(init:TWiFi, public parent:Cset_network_conditions) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'preset'?'WiFi'`; }
	public get entity() { return this.parent.entity; }
}
export type Tgood_2G = {
};
export class Cgood_2G extends AlanNode {
	constructor(init:Tgood_2G, public parent:Cset_network_conditions) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'preset'?'good 2G'`; }
	public get entity() { return this.parent.entity; }
}
export type Tgood_3G = {
};
export class Cgood_3G extends AlanNode {
	constructor(init:Tgood_3G, public parent:Cset_network_conditions) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'preset'?'good 3G'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno_throttling = {
};
export class Cno_throttling extends AlanNode {
	constructor(init:Tno_throttling, public parent:Cset_network_conditions) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'preset'?'no throttling'`; }
	public get entity() { return this.parent.entity; }
}
export type Toffline = {
};
export class Coffline extends AlanNode {
	constructor(init:Toffline, public parent:Cset_network_conditions) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'preset'?'offline'`; }
	public get entity() { return this.parent.entity; }
}
export type Tregular_2G = {
};
export class Cregular_2G extends AlanNode {
	constructor(init:Tregular_2G, public parent:Cset_network_conditions) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'preset'?'regular 2G'`; }
	public get entity() { return this.parent.entity; }
}
export type Tregular_3G = {
};
export class Cregular_3G extends AlanNode {
	constructor(init:Tregular_3G, public parent:Cset_network_conditions) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'preset'?'regular 3G'`; }
	public get entity() { return this.parent.entity; }
}
export type Tregular_4G = {
};
export class Cregular_4G extends AlanNode {
	constructor(init:Tregular_4G, public parent:Cset_network_conditions) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'preset'?'regular 4G'`; }
	public get entity() { return this.parent.entity; }
}
export type Tset_user = {
	'user':string;
};
export class Cset_user extends AlanNode {
	public readonly properties:{
		readonly user:string
	};
	public readonly output:{
		stack: () => test.Cstack;
	} = {
		stack: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.stack()).result!)
	}
	constructor(init:Tset_user, public parent:Cstatement) {
		super();
		const $this = this;
		this.properties = {
			user: init['user']
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'set user'`; }
	public get entity() { return this.parent.entity; }
}
export type Tunset_user = {
};
export class Cunset_user extends AlanNode {
	public readonly output:{
		stack: () => test.Cstack;
	} = {
		stack: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.stack()).result!)
	}
	constructor(init:Tunset_user, public parent:Cstatement) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'unset user'`; }
	public get entity() { return this.parent.entity; }
}
export type Twait = {
	'timeout':number;
};
export class Cwait extends AlanNode {
	public readonly properties:{
		readonly timeout:Cwait.Dtimeout
	};
	public readonly output:{
		stack: () => test.Cstack;
	} = {
		stack: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.stack()).result!)
	}
	constructor(init:Twait, public parent:Cstatement) {
		super();
		const $this = this;
		this.properties = {
			timeout: new Cwait.Dtimeout(init['timeout'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'wait'`; }
	public get entity() { return this.parent.entity; }
}
export type Twalk = {
	'assignment':Toptional_assignment;
	'collection':string;
	'statements':Tstatements;
	'value expression':Tvalue_expression;
};
export class Cwalk extends AlanNode {
	public readonly properties:{
		readonly assignment:Coptional_assignment,
		readonly collection:Cwalk.Dcollection,
		readonly statements:Cstatements,
		readonly value_expression:Cvalue_expression & { readonly inferences: {
			node: () => test.Cnode__test;
			value: () => test.Cvalue__test;
		} }
	};
	public readonly output:{
		stack: () => test.Cstack;
	} = {
		stack: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.stack()).result!)
	}
	constructor(init:Twalk, public parent:Cstatement) {
		super();
		const $this = this;
		this.properties = {
			assignment: new Cwalk.Dassignment(init['assignment'], $this),
			collection: new Cwalk.Dcollection(init['collection'], $this),
			statements: new Cwalk.Dstatements(init['statements'], $this),
			value_expression: new Cwalk.Dvalue_expression(init['value expression'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'walk'`; }
	public get entity() { return this.parent.entity; }
}
type Vstate_group = { name: 'binding', definition: client_bindings.Cstate_group__type__properties}|{ name: 'gui', definition: gui_model.Cstate_group__type__property__type__attributes}
export class Cstate_group extends AlanStruct {
	constructor(
		public readonly variant:Vstate_group) { super(); }
	public cast<K extends Vstate_group['name']>(_variant:K):Extract<Vstate_group, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vstate_group['name']]:(($:Extract<Vstate_group, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'state group'`; }
	public is(other:Cstate_group):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
type Vstate__test = { name: 'binding', definition: client_bindings.Cstates}|{ name: 'gui', definition: gui_model.Cstates__state_group__type__property__type__attributes}
export class Cstate__test extends AlanStruct {
	constructor(
		public readonly variant:Vstate__test) { super(); }
	public definitions:{
		value_context: Cvalue_context;
	} = {
		value_context: new Cvalue_context({name:'state', definition: this})
	}
	public cast<K extends Vstate__test['name']>(_variant:K):Extract<Vstate__test, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vstate__test['name']]:(($:Extract<Vstate__test, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'state'`; }
	public is(other:Cstate__test):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
export type Tstack_selector = {
	'select':['parent frame', Tparent_frame]|['this frame', Tthis_frame];
};
export class Cstack_selector extends AlanNode {
	public readonly properties:{
		readonly select:Cstack_selector.Dselect
	};
	public readonly output:{
		value: () => test.Cvalue__test;
	} = {
		value: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.select?.state.node.output.value()).result!)
	};
	constructor(init:Tstack_selector, public location:AlanNode, public input: {
		stack: () => test.Cstack
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			select: new Cstack_selector.Dselect(init['select'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tparent_frame = {
	'tail':Tstack_selector;
};
export class Cparent_frame extends AlanNode {
	public readonly properties:{
		readonly tail:Cstack_selector
	};
	public readonly output:{
		value: () => test.Cvalue__test;
	} = {
		value: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.tail)
			.then(context => context?.component_root.output.value()).result!)
	}
	public readonly inferences:{
		frame: () => test.Cstack_frame
	} = {
		frame: cache((detach:boolean) => {
			const test__stack_selector__select__parent_frame_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.stack())
				.then(context => context?.variant.name === 'frame' ? context.variant.definition as test.Cstack_frame : undefined).result!;
		})

	}
	constructor(init:Tparent_frame, public parent:Cstack_selector) {
		super();
		const $this = this;
		this.properties = {
			tail: new Cparent_frame.Dtail(init['tail'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'select'?'parent frame'`; }
	public get entity() { return this.parent.entity; }
}
export type Tthis_frame = {
	'name':string;
};
export class Cthis_frame extends AlanNode {
	public readonly properties:{
		readonly name:Cthis_frame.Dname
	};
	public readonly output:{
		value: () => test.Cvalue__test;
	} = {
		value: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.inferences.frame())
			.then(context => context?.component_root.output.value()).result!)
	}
	public readonly inferences:{
		frame: () => test.Cstack_frame
	} = {
		frame: cache((detach:boolean) => {
			const test__stack_selector__select__this_frame_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.stack())
				.then(context => context?.variant.name === 'frame' ? context.variant.definition as test.Cstack_frame : undefined).result!;
		})

	}
	constructor(init:Tthis_frame, public parent:Cstack_selector) {
		super();
		const $this = this;
		this.properties = {
			name: new Cthis_frame.Dname(init['name'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'select'?'this frame'`; }
	public get entity() { return this.parent.entity; }
}
type Vstack = { name: 'frame', definition: test.Cstack_frame}|{ name: 'empty', definition: (typeof Cstack.Pempty)}
export class Cstack extends AlanStruct {
	public static Pempty:Cstack = new class PrimitiveInstance extends Cstack {
		constructor () {
			super({name: 'empty', definition: undefined as unknown as Cstack})
			this.variant.definition = this;
		}
	}
	constructor(
		public readonly variant:Vstack) { super(); }
	public cast<K extends Vstack['name']>(_variant:K):Extract<Vstack, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vstack['name']]:(($:Extract<Vstack, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'stack'`; }
	public is(other:Cstack):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
export import Cempty = test.Cstack;
export type Tstack_frame = {
	'name':string;
	'named values':Record<string, Tnamed_values>;
};
export class Cstack_frame extends AlanNode {
	public definitions:{
		stack: Cstack;
	} = {
		stack: new Cstack({name:'frame', definition: this})
	}
	public readonly properties:{
		readonly name:Cstack_frame.Dname,
		readonly named_values:Cstack_frame.Dnamed_values
	};
	public readonly output:{
		parent: () => test.Cstack;
		value: () => test.Cvalue__test;
	} = {
		parent: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.parent()).result!),
		value: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.value()).result!)
	};
	constructor(init:Tstack_frame, public location:AlanNode, public input: {
		parent: () => test.Cstack,
		value: () => test.Cvalue__test
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			name: new Cstack_frame.Dname(init['name'], $this),
			named_values: new Cstack_frame.Dnamed_values(init['named values'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tnamed_values = {
	'has successor':'no'|['no', {}]|'yes'|['yes', {}];
};
export class Cnamed_values extends AlanDictionaryEntry {
	public key:string;
	public get key_value() { return this.key; }
	public readonly properties:{
		readonly has_successor:Cnamed_values.Dhas_successor
	};
	constructor(key:string, init:Tnamed_values, public parent:Cstack_frame) {
		super();
		const $this = this;
		this.key = key;
		this.properties = {
			has_successor: new Cnamed_values.Dhas_successor(init['has successor'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'named values'['${this.key}']`; }
	public get entity() { return this; }
}
export type Tno__has_successor = {
};
export class Cno__has_successor extends AlanNode {
	constructor(init:Tno__has_successor, public parent:Cnamed_values) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has successor'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_successor = {
};
export class Cyes__has_successor extends AlanNode {
	constructor(init:Tyes__has_successor, public parent:Cnamed_values) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has successor'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tparameter_implementation = {
	'has next':'no'|['no', {}]|['yes', Tyes__has_next__parameter_implementation];
	'value expression':Tvalue_expression;
};
export class Cparameter_implementation extends AlanNode {
	public readonly properties:{
		readonly has_next:Cparameter_implementation.Dhas_next,
		readonly value_expression:Cvalue_expression & { readonly inferences: {
			correct_value: () => test.Cvalue__test;
			value: () => test.Cvalue__test;
		} }
	};
	constructor(init:Tparameter_implementation, public location:AlanNode, public input: {
		definition: () => test.Cparameter_definition,
		stack: () => test.Cstack
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			has_next: new Cparameter_implementation.Dhas_next(init['has next'], $this),
			value_expression: new Cparameter_implementation.Dvalue_expression(init['value expression'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tno__has_next__parameter_implementation = {
};
export class Cno__has_next__parameter_implementation extends AlanNode {
	public readonly inferences:{
		no: () => test.Cno__has_next__parameter_definition
	} = {
		no: cache((detach:boolean) => {
			const test__parameter_implementation__has_next__no_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.definition())
				.then(context => {
					if (context?.properties.has_next.state.name === 'no') {
						return resolve(context.properties.has_next.state.node as test.Cno__has_next__parameter_definition).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tno__has_next__parameter_implementation, public parent:Cparameter_implementation) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has next'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_next__parameter_implementation = {
	'parameters':Tparameter_implementation;
};
export class Cyes__has_next__parameter_implementation extends AlanNode {
	public readonly properties:{
		readonly parameters:Cparameter_implementation
	};
	public readonly inferences:{
		yes: () => test.Cyes__has_next__parameter_definition
	} = {
		yes: cache((detach:boolean) => {
			const test__parameter_implementation__has_next__yes_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.definition())
				.then(context => {
					if (context?.properties.has_next.state.name === 'yes') {
						return resolve(context.properties.has_next.state.node as test.Cyes__has_next__parameter_definition).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tyes__has_next__parameter_implementation, public parent:Cparameter_implementation) {
		super();
		const $this = this;
		this.properties = {
			parameters: new Cyes__has_next__parameter_implementation.Dparameters(init['parameters'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has next'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tparameter_definition = {
	'has next':'no'|['no', {}]|['yes', Tyes__has_next__parameter_definition];
	'stack frame':Tstack_frame;
	'type':Tvalue_type_definition;
};
export class Cparameter_definition extends AlanNode {
	public readonly properties:{
		readonly has_next:Cparameter_definition.Dhas_next,
		readonly stack_frame:Cstack_frame,
		readonly type:Cvalue_type_definition
	};
	public readonly output:{
		stack: () => test.Cstack;
	} = {
		stack: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.has_next?.state.node.output.stack()).result!)
	};
	constructor(init:Tparameter_definition, public location:AlanNode, public input: {
		stack: () => test.Cstack
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			has_next: new Cparameter_definition.Dhas_next(init['has next'], $this),
			stack_frame: new Cparameter_definition.Dstack_frame(init['stack frame'], $this),
			type: new Cparameter_definition.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tno__has_next__parameter_definition = {
};
export class Cno__has_next__parameter_definition extends AlanNode {
	public readonly output:{
		stack: () => test.Cstack;
	} = {
		stack: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.parent)
			.then(context => context?.properties.stack_frame)
			.then(context => context?.definitions.stack).result!)
	}
	constructor(init:Tno__has_next__parameter_definition, public parent:Cparameter_definition) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has next'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_next__parameter_definition = {
	'parameters':Tparameter_definition;
};
export class Cyes__has_next__parameter_definition extends AlanNode {
	public readonly properties:{
		readonly parameters:Cparameter_definition
	};
	public readonly output:{
		stack: () => test.Cstack;
	} = {
		stack: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.parameters)
			.then(context => context?.component_root.output.stack()).result!)
	}
	constructor(init:Tyes__has_next__parameter_definition, public parent:Cparameter_definition) {
		super();
		const $this = this;
		this.properties = {
			parameters: new Cyes__has_next__parameter_definition.Dparameters(init['parameters'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has next'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
type Voptional_node = { name: 'none', definition: (typeof Coptional_node.Pnone)}|{ name: 'node', definition: test.Cnode__test}
export class Coptional_node extends AlanStruct {
	public static Pnone:Coptional_node = new class PrimitiveInstance extends Coptional_node {
		constructor () {
			super({name: 'none', definition: undefined as unknown as Coptional_node})
			this.variant.definition = this;
		}
	}
	constructor(
		public readonly variant:Voptional_node) { super(); }
	public cast<K extends Voptional_node['name']>(_variant:K):Extract<Voptional_node, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Voptional_node['name']]:(($:Extract<Voptional_node, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'optional node'`; }
	public is(other:Coptional_node):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
export import Cnone__optional_node = test.Coptional_node;
export type Toptional_assignment = {
	'assign':'no'|['no', {}]|['yes', Tyes__assign__optional_assignment];
};
export class Coptional_assignment extends AlanNode {
	public readonly properties:{
		readonly assign:Coptional_assignment.Dassign
	};
	public readonly output:{
		stack: () => test.Cstack;
	} = {
		stack: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.assign?.state.node.output.stack()).result!)
	};
	constructor(init:Toptional_assignment, public location:AlanNode, public input: {
		stack: () => test.Cstack,
		value: () => test.Coptional_value
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			assign: new Coptional_assignment.Dassign(init['assign'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tno__assign__optional_assignment = {
};
export class Cno__assign__optional_assignment extends AlanNode {
	public readonly output:{
		stack: () => test.Cstack;
	} = {
		stack: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.stack()).result!)
	}
	constructor(init:Tno__assign__optional_assignment, public parent:Coptional_assignment) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'assign'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__assign__optional_assignment = {
	'stack frame':Tstack_frame;
};
export class Cyes__assign__optional_assignment extends AlanNode {
	public readonly properties:{
		readonly stack_frame:Cstack_frame
	};
	public readonly output:{
		stack: () => test.Cstack;
	} = {
		stack: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.stack_frame)
			.then(context => context?.definitions.stack).result!)
	}
	public readonly inferences:{
		value: () => test.Cvalue__test
	} = {
		value: cache((detach:boolean) => {
			const test__optional_assignment__assign__yes_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.value())
				.then(context => context?.variant.name === 'value' ? context.variant.definition as test.Cvalue__test : undefined).result!;
		})

	}
	constructor(init:Tyes__assign__optional_assignment, public parent:Coptional_assignment) {
		super();
		const $this = this;
		this.properties = {
			stack_frame: new Cyes__assign__optional_assignment.Dstack_frame(init['stack frame'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'assign'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnode_type_path = {
	'has steps':'no'|['no', {}]|['yes', Tyes__has_steps__node_type_path];
};
export class Cnode_type_path extends AlanNode {
	public readonly properties:{
		readonly has_steps:Cnode_type_path.Dhas_steps
	};
	public readonly output:{
		node: () => test.Cnode__test;
	} = {
		node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.has_steps?.state.node.output.node()).result!)
	};
	constructor(init:Tnode_type_path, public location:AlanNode, public input: {
		node: () => test.Cnode__test
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			has_steps: new Cnode_type_path.Dhas_steps(init['has steps'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tno__has_steps__node_type_path = {
};
export class Cno__has_steps__node_type_path extends AlanNode {
	public readonly output:{
		node: () => test.Cnode__test;
	} = {
		node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.node()).result!)
	}
	constructor(init:Tno__has_steps__node_type_path, public parent:Cnode_type_path) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has steps'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_steps__node_type_path = {
	'attribute':string;
	'tail':Tnode_type_path;
	'type':'entry'|['entry', {}]|'node'|['node', {}]|['state', Tstate__type];
};
export class Cyes__has_steps__node_type_path extends AlanNode {
	public readonly properties:{
		readonly attribute:Cyes__has_steps__node_type_path.Dattribute,
		readonly tail:Cnode_type_path,
		readonly type:Cyes__has_steps__node_type_path.Dtype
	};
	public readonly output:{
		node: () => test.Cnode__test;
	} = {
		node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.tail)
			.then(context => context?.component_root.output.node()).result!)
	}
	constructor(init:Tyes__has_steps__node_type_path, public parent:Cnode_type_path) {
		super();
		const $this = this;
		this.properties = {
			attribute: new Cyes__has_steps__node_type_path.Dattribute(init['attribute'], $this),
			tail: new Cyes__has_steps__node_type_path.Dtail(init['tail'], $this),
			type: new Cyes__has_steps__node_type_path.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has steps'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tentry__type = {
};
export class Centry__type extends AlanNode {
	public readonly output:{
		node: () => test.Cnode__test;
	} = {
		node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.inferences.node()).result!)
	}
	public readonly inferences:{
		collection: () => test.Ccollection__test,
		node: () => test.Cnode__test
	} = {
		collection: cache((detach:boolean) => {
			const test__node_type_path__has_steps__yes__type__entry_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.properties.attribute?.ref)
				.then(context => {
					switch (context?.variant.name) {
						case 'binding': {
							const test__node_type_path__has_steps__yes__type__entry_inf___collection___binding_nval = context.cast('binding');
							return resolve(context)
								.then(context => test__node_type_path__has_steps__yes__type__entry_inf___collection___binding_nval)
								.then(context => {
									if (context?.properties.type.state.name === 'collection') {
										return resolve(context.properties.type.state.node as client_bindings.Ccollection__type__properties).result;
									} else {
										return undefined;
									}
								})
								.then(context => {
									const conv_context = resolve(context).result!;
									return new Ccollection__test({name: 'binding', definition: conv_context});
								}).result;
						}
						case 'gui': {
							const test__node_type_path__has_steps__yes__type__entry_inf___collection___gui_nval = context.cast('gui');
							return resolve(context)
								.then(context => test__node_type_path__has_steps__yes__type__entry_inf___collection___gui_nval)
								.then(context => {
									if (context?.properties.type.state.name === 'property') {
										return resolve(context.properties.type.state.node as gui_model.Cproperty__type__attributes)
										.then(context => {
											if (context?.properties.type.state.name === 'collection') {
												return context.properties.type.state.node as gui_model.Ccollection__type__property;
											} else {
												return undefined;
											}
										}).result;
									} else {
										return undefined;
									}
								})
								.then(context => {
									const conv_context = resolve(context).result!;
									return new Ccollection__test({name: 'gui', definition: conv_context});
								}).result;
						}
						case undefined: return undefined;
						default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
					};
				}).result!;
		})
		,
		node: cache((detach:boolean) => {
			const test__node_type_path__has_steps__yes__type__entry_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.inferences.collection())
				.then(context => {
					switch (context?.variant.name) {
						case 'binding': {
							const test__node_type_path__has_steps__yes__type__entry_inf___node___binding_nval = context.cast('binding');
							return resolve(context)
								.then(context => test__node_type_path__has_steps__yes__type__entry_inf___node___binding_nval)
								.then(context => context?.properties.node)
								.then(context => {
									const conv_context = resolve(context).result!;
									return new Cnode__test({name: 'binding', definition: conv_context});
								}).result;
						}
						case 'gui': {
							const test__node_type_path__has_steps__yes__type__entry_inf___node___gui_nval = context.cast('gui');
							return resolve(context)
								.then(context => test__node_type_path__has_steps__yes__type__entry_inf___node___gui_nval)
								.then(context => context?.properties.node)
								.then(context => {
									const conv_context = resolve(context).result!;
									return new Cnode__test({name: 'gui', definition: conv_context});
								}).result;
						}
						case undefined: return undefined;
						default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
					};
				}).result!;
		})

	}
	constructor(init:Tentry__type, public parent:Cyes__has_steps__node_type_path) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'entry'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnode__type__yes = {
};
export class Cnode__type__yes extends AlanNode {
	public readonly output:{
		node: () => test.Cnode__test;
	} = {
		node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.inferences.node()).result!)
	}
	public readonly inferences:{
		node: () => test.Cnode__test,
		optional_value: () => test.Coptional_value
	} = {
		node: cache((detach:boolean) => {
			const test__node_type_path__has_steps__yes__type__node_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.inferences.optional_value())
				.then(context => context?.variant.name === 'value' ? context.variant.definition as test.Cvalue__test : undefined)
				.then(context => context?.variant.name === 'node' ? context.variant.definition as test.Cnode__test : undefined).result!;
		})
		,
		optional_value: cache((detach:boolean) => {
			const test__node_type_path__has_steps__yes__type__node_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.properties.attribute?.ref)
				.then(context => {
					switch (context?.variant.name) {
						case 'binding': {
							return resolve(context)
								.then(() => this.parent)
								.then(() => test.Coptional_value.Pnone).result;
						}
						case 'gui': {
							const test__node_type_path__has_steps__yes__type__node_inf___optional_value___gui_nval = context.cast('gui');
							return resolve(context)
								.then(context => test__node_type_path__has_steps__yes__type__node_inf___optional_value___gui_nval)
								.then(context => context?.properties.type)
								.then(context => {
									const state_group = context;
									switch (state_group?.state.name) {
										case 'action': {
											const test__node_type_path__has_steps__yes__type__node_inf___optional_value___gui___action_nval = state_group.cast('action');
											return resolve(context)
												.then(context => test__node_type_path__has_steps__yes__type__node_inf___optional_value___gui___action_nval)
												.then(context => context?.properties.parameters)
												.then(context => {
													const conv_context = resolve(context).result!;
													return new Cnode__test({name: 'gui', definition: conv_context});
												})
												.then(context => context?.definitions.value)
												.then(context => context?.definitions.optional_value).result;
										}
										case 'command': {
											const test__node_type_path__has_steps__yes__type__node_inf___optional_value___gui___command_nval = state_group.cast('command');
											return resolve(context)
												.then(context => test__node_type_path__has_steps__yes__type__node_inf___optional_value___gui___command_nval)
												.then(context => context?.properties.type)
												.then(context => {
													const state_group = context;
													switch (state_group?.state.name) {
														case 'component': {
															return resolve(context)
																.then(() => this.parent)
																.then(() => test.Coptional_value.Pnone).result;
														}
														case 'global': {
															const test__node_type_path__has_steps__yes__type__node_inf___optional_value___gui___command___global_nval = state_group.cast('global');
															return resolve(context)
																.then(context => test__node_type_path__has_steps__yes__type__node_inf___optional_value___gui___command___global_nval)
																.then(context => context?.properties.parameters)
																.then(context => {
																	const conv_context = resolve(context).result!;
																	return new Cnode__test({name: 'gui', definition: conv_context});
																})
																.then(context => context?.definitions.value)
																.then(context => context?.definitions.optional_value).result;
														}
														case undefined: return undefined;
														default: throw new Error(`Unexpected state '${(state_group!.state as any).name}'`);
													};}).result;
										}
										case 'property': {
											const test__node_type_path__has_steps__yes__type__node_inf___optional_value___gui___property_nval = state_group.cast('property');
											return resolve(context)
												.then(context => test__node_type_path__has_steps__yes__type__node_inf___optional_value___gui___property_nval)
												.then(context => context?.properties.type)
												.then(context => {
													const state_group = context;
													switch (state_group?.state.name) {
														case 'collection': {
															return resolve(context)
																.then(() => this.parent)
																.then(() => test.Coptional_value.Pnone).result;
														}
														case 'file': {
															return resolve(context)
																.then(() => this.parent)
																.then(() => test.Coptional_value.Pnone).result;
														}
														case 'group': {
															const test__node_type_path__has_steps__yes__type__node_inf___optional_value___gui___property___group_nval = state_group.cast('group');
															return resolve(context)
																.then(context => test__node_type_path__has_steps__yes__type__node_inf___optional_value___gui___property___group_nval)
																.then(context => context?.properties.gui_node)
																.then(context => {
																	const conv_context = resolve(context).result!;
																	return new Cnode__test({name: 'gui', definition: conv_context});
																})
																.then(context => context?.definitions.value)
																.then(context => context?.definitions.optional_value).result;
														}
														case 'number': {
															return resolve(context)
																.then(() => this.parent)
																.then(() => test.Coptional_value.Pnone).result;
														}
														case 'state group': {
															return resolve(context)
																.then(() => this.parent)
																.then(() => test.Coptional_value.Pnone).result;
														}
														case 'text': {
															return resolve(context)
																.then(() => this.parent)
																.then(() => test.Coptional_value.Pnone).result;
														}
														case undefined: return undefined;
														default: throw new Error(`Unexpected state '${(state_group!.state as any).name}'`);
													};}).result;
										}
										case 'reference set': {
											return resolve(context)
												.then(() => this.parent)
												.then(() => test.Coptional_value.Pnone).result;
										}
										case undefined: return undefined;
										default: throw new Error(`Unexpected state '${(state_group!.state as any).name}'`);
									};}).result;
						}
						case undefined: return undefined;
						default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
					};
				}).result!;
		})

	}
	constructor(init:Tnode__type__yes, public parent:Cyes__has_steps__node_type_path) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'node'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstate__type = {
	'state':string;
};
export class Cstate__type extends AlanNode {
	public readonly properties:{
		readonly state:Cstate__type.Dstate
	};
	public readonly output:{
		node: () => test.Cnode__test;
	} = {
		node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.state?.inferences.node()).result!)
	}
	public readonly inferences:{
		state_group: () => test.Cstate_group
	} = {
		state_group: cache((detach:boolean) => {
			const test__node_type_path__has_steps__yes__type__state_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.properties.attribute?.ref)
				.then(context => {
					switch (context?.variant.name) {
						case 'binding': {
							const test__node_type_path__has_steps__yes__type__state_inf___state_group___binding_nval = context.cast('binding');
							return resolve(context)
								.then(context => test__node_type_path__has_steps__yes__type__state_inf___state_group___binding_nval)
								.then(context => {
									if (context?.properties.type.state.name === 'state group') {
										return resolve(context.properties.type.state.node as client_bindings.Cstate_group__type__properties).result;
									} else {
										return undefined;
									}
								})
								.then(context => {
									const conv_context = resolve(context).result!;
									return new Cstate_group({name: 'binding', definition: conv_context});
								}).result;
						}
						case 'gui': {
							const test__node_type_path__has_steps__yes__type__state_inf___state_group___gui_nval = context.cast('gui');
							return resolve(context)
								.then(context => test__node_type_path__has_steps__yes__type__state_inf___state_group___gui_nval)
								.then(context => {
									if (context?.properties.type.state.name === 'property') {
										return resolve(context.properties.type.state.node as gui_model.Cproperty__type__attributes)
										.then(context => {
											if (context?.properties.type.state.name === 'state group') {
												return context.properties.type.state.node as gui_model.Cstate_group__type__property__type__attributes;
											} else {
												return undefined;
											}
										}).result;
									} else {
										return undefined;
									}
								})
								.then(context => {
									const conv_context = resolve(context).result!;
									return new Cstate_group({name: 'gui', definition: conv_context});
								}).result;
						}
						case undefined: return undefined;
						default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
					};
				}).result!;
		})

	}
	constructor(init:Tstate__type, public parent:Cyes__has_steps__node_type_path) {
		super();
		const $this = this;
		this.properties = {
			state: new Cstate__type.Dstate(init['state'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'state'`; }
	public get entity() { return this.parent.entity; }
}
type Vnode__test = { name: 'binding', definition: client_bindings.Cnode__client_bindings}|{ name: 'gui', definition: gui_model.Cgui_node}
export class Cnode__test extends AlanStruct {
	constructor(
		public readonly variant:Vnode__test) { super(); }
	public definitions:{
		optional_node: Coptional_node;
		value: Cvalue__test;
	} = {
		optional_node: new Coptional_node({name:'node', definition: this}),
		value: new Cvalue__test({name:'node', definition: this})
	}
	public cast<K extends Vnode__test['name']>(_variant:K):Extract<Vnode__test, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vnode__test['name']]:(($:Extract<Vnode__test, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'node'`; }
	public is(other:Cnode__test):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
export type Tinstruction_argument = {
	'next argument':['exists', Texists__next_argument]|'no'|['no', {}];
	'value expression':Tvalue_expression;
};
export class Cinstruction_argument extends AlanNode {
	public readonly properties:{
		readonly next_argument:Cinstruction_argument.Dnext_argument,
		readonly value_expression:Cvalue_expression & { readonly inferences: {
			correct_type: () => test.Cboolean;
			type_is_correct: () => test.Ctrue;
			value: () => test.Cvalue__test;
		} }
	};
	constructor(init:Tinstruction_argument, public location:AlanNode, public input: {
		argument: () => client_bindings.Cinstruction_argument,
		stack: () => test.Cstack
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			next_argument: new Cinstruction_argument.Dnext_argument(init['next argument'], $this),
			value_expression: new Cinstruction_argument.Dvalue_expression(init['value expression'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Texists__next_argument = {
	'argument':Tinstruction_argument;
};
export class Cexists__next_argument extends AlanNode {
	public readonly properties:{
		readonly argument:Cinstruction_argument
	};
	public readonly inferences:{
		exists: () => client_bindings.Cexists
	} = {
		exists: cache((detach:boolean) => {
			const test__instruction_argument__next_argument__exists_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.argument())
				.then(context => {
					if (context?.properties.next_argument.state.name === 'exists') {
						return resolve(context.properties.next_argument.state.node as client_bindings.Cexists).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Texists__next_argument, public parent:Cinstruction_argument) {
		super();
		const $this = this;
		this.properties = {
			argument: new Cexists__next_argument.Dargument(init['argument'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'next argument'?'exists'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__next_argument = {
};
export class Cno__next_argument extends AlanNode {
	public readonly inferences:{
		no: () => client_bindings.Cno__next_argument
	} = {
		no: cache((detach:boolean) => {
			const test__instruction_argument__next_argument__no_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.argument())
				.then(context => {
					if (context?.properties.next_argument.state.name === 'no') {
						return resolve(context.properties.next_argument.state.node as client_bindings.Cno__next_argument).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tno__next_argument, public parent:Cinstruction_argument) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'next argument'?'no'`; }
	public get entity() { return this.parent.entity; }
}
type Vimplicit_view = { name: 'view definition', definition: (typeof Cimplicit_view.Pview_definition)}|{ name: 'open view', definition: (typeof Cimplicit_view.Popen_view)}
export class Cimplicit_view extends AlanStruct {
	public static Pview_definition:Cimplicit_view = new class PrimitiveInstance extends Cimplicit_view {
		constructor () {
			super({name: 'view definition', definition: undefined as unknown as Cimplicit_view})
			this.variant.definition = this;
		}
	}
	public static Popen_view:Cimplicit_view = new class PrimitiveInstance extends Cimplicit_view {
		constructor () {
			super({name: 'open view', definition: undefined as unknown as Cimplicit_view})
			this.variant.definition = this;
		}
	}
	constructor(
		public readonly variant:Vimplicit_view) { super(); }
	public definitions:{
		view: Cview__test;
	} = {
		view: new Cview__test({name:'implicit', definition: this})
	}
	public cast<K extends Vimplicit_view['name']>(_variant:K):Extract<Vimplicit_view, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vimplicit_view['name']]:(($:Extract<Vimplicit_view, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'implicit view'`; }
	public is(other:Cimplicit_view):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
export import Copen_view__implicit_view = test.Cimplicit_view;
export import Cview_definition__implicit_view = test.Cimplicit_view;
type Vexplicit_view = { name: 'view definition', definition: views.Cviews__}|{ name: 'open view', definition: views.Cviews__}
export class Cexplicit_view extends AlanStruct {
	constructor(
		public readonly variant:Vexplicit_view) { super(); }
	public definitions:{
		view: Cview__test;
	} = {
		view: new Cview__test({name:'explicit', definition: this})
	}
	public readonly output:{
		definition: () => views.Cviews__;
	} = {
		definition: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => {
				switch (context?.variant.name) {
					case 'open view': {
						const test__explicit_view_out___definition___open_view_nval = context.cast('open view');
						return resolve(context)
							.then(context => test__explicit_view_out___definition___open_view_nval).result;
					}
					case 'view definition': {
						const test__explicit_view_out___definition___view_definition_nval = context.cast('view definition');
						return resolve(context)
							.then(context => test__explicit_view_out___definition___view_definition_nval).result;
					}
					case undefined: return undefined;
					default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
				};
			}).result!)
	};
	public cast<K extends Vexplicit_view['name']>(_variant:K):Extract<Vexplicit_view, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vexplicit_view['name']]:(($:Extract<Vexplicit_view, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'explicit view'`; }
	public is(other:Cexplicit_view):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
export type Tcondition = {
	'assign':'no'|['no', {}]|['yes', Tyes__assign__condition];
	'expression type':['binary', Tbinary]|'unary'|['unary', {}];
	'value condition':'exists'|['exists', {}]|'not exists'|['not exists', {}];
	'value expression':Tvalue_expression;
};
export class Ccondition extends AlanNode {
	public readonly properties:{
		readonly assign:Ccondition.Dassign,
		readonly expression_type:Ccondition.Dexpression_type,
		readonly value_condition:Ccondition.Dvalue_condition,
		readonly value_expression:Cvalue_expression
	};
	public readonly output:{
		stack: () => test.Cstack;
	} = {
		stack: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.assign?.state.node.output.stack()).result!)
	};
	constructor(init:Tcondition, public location:AlanNode, public input: {
		stack: () => test.Cstack
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			assign: new Ccondition.Dassign(init['assign'], $this),
			expression_type: new Ccondition.Dexpression_type(init['expression type'], $this),
			value_condition: new Ccondition.Dvalue_condition(init['value condition'], $this),
			value_expression: new Ccondition.Dvalue_expression(init['value expression'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tno__assign__condition = {
};
export class Cno__assign__condition extends AlanNode {
	public readonly output:{
		stack: () => test.Cstack;
	} = {
		stack: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.stack()).result!)
	}
	constructor(init:Tno__assign__condition, public parent:Ccondition) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'assign'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__assign__condition = {
	'stack frame':Tstack_frame;
};
export class Cyes__assign__condition extends AlanNode {
	public readonly properties:{
		readonly stack_frame:Cstack_frame
	};
	public readonly output:{
		stack: () => test.Cstack;
	} = {
		stack: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.stack_frame)
			.then(context => context?.definitions.stack).result!)
	}
	public readonly inferences:{
		exists: () => test.Cexists__value_condition,
		value: () => test.Cvalue__test
	} = {
		exists: cache((detach:boolean) => {
			const test__condition__assign__yes_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => {
					if (context?.properties.value_condition.state.name === 'exists') {
						return resolve(context.properties.value_condition.state.node as test.Cexists__value_condition).result;
					} else {
						return undefined;
					}
				}).result!;
		})
		,
		value: cache((detach:boolean) => {
			const test__condition__assign__yes_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.properties.value_expression)
				.then(context => context?.component_root.output.value())
				.then(context => context?.variant.name === 'value' ? context.variant.definition as test.Cvalue__test : undefined).result!;
		})

	}
	constructor(init:Tyes__assign__condition, public parent:Ccondition) {
		super();
		const $this = this;
		this.properties = {
			stack_frame: new Cyes__assign__condition.Dstack_frame(init['stack frame'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'assign'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tbinary = {
	'condition':'equals'|['equals', {}]|'not equals'|['not equals', {}];
	'target':Tvalue_expression;
};
export class Cbinary extends AlanNode {
	public readonly properties:{
		readonly condition:Cbinary.Dcondition,
		readonly target:Cvalue_expression & { readonly inferences: {
			correct_value: () => test.Cvalue__test;
			value: () => test.Cvalue__test;
		} }
	};
	public readonly inferences:{
		value: () => test.Cvalue__test,
		value_exists: () => test.Cexists__value_condition
	} = {
		value: cache((detach:boolean) => {
			const test__condition__expression_type__binary_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.properties.value_expression)
				.then(context => context?.component_root.output.value())
				.then(context => context?.variant.name === 'value' ? context.variant.definition as test.Cvalue__test : undefined).result!;
		})
		,
		value_exists: cache((detach:boolean) => {
			const test__condition__expression_type__binary_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => {
					if (context?.properties.value_condition.state.name === 'exists') {
						return resolve(context.properties.value_condition.state.node as test.Cexists__value_condition).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tbinary, public parent:Ccondition) {
		super();
		const $this = this;
		this.properties = {
			condition: new Cbinary.Dcondition(init['condition'], $this),
			target: new Cbinary.Dtarget(init['target'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'expression type'?'binary'`; }
	public get entity() { return this.parent.entity; }
}
export type Tequals = {
};
export class Cequals extends AlanNode {
	constructor(init:Tequals, public parent:Cbinary) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'condition'?'equals'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnot_equals = {
};
export class Cnot_equals extends AlanNode {
	constructor(init:Tnot_equals, public parent:Cbinary) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'condition'?'not equals'`; }
	public get entity() { return this.parent.entity; }
}
export type Tunary = {
};
export class Cunary extends AlanNode {
	constructor(init:Tunary, public parent:Ccondition) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'expression type'?'unary'`; }
	public get entity() { return this.parent.entity; }
}
export type Texists__value_condition = {
};
export class Cexists__value_condition extends AlanNode {
	constructor(init:Texists__value_condition, public parent:Ccondition) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'value condition'?'exists'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnot_exists = {
};
export class Cnot_exists extends AlanNode {
	constructor(init:Tnot_exists, public parent:Ccondition) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'value condition'?'not exists'`; }
	public get entity() { return this.parent.entity; }
}
function evaluate__compare_values(detach:boolean, test__compare_values_nval:test.Cvalue__test, input: {
	expected: () => test.Cvalue__test
}):test.Cvalue__test {
	const self = { component_root: { input: input } };
	return resolve(self)
		.then(context => test__compare_values_nval)
		.then(context => {
			switch (context?.variant.name) {
				case 'file': {
					return resolve(context)
						.then(context => {
							const left = resolve(context)
								.then(context => test__compare_values_nval).result;
							const right = resolve(context)
								.then(() => self)
								.then(context => context?.component_root.input.expected()).result;
							return left?.is(right!) ? left : undefined
						}).result;
				}
				case 'node': {
					const test__compare_values___node_nval = context.cast('node');
					return resolve(context)
						.then(context => test__compare_values___node_nval)
						.then(context => {
							switch (context?.variant.name) {
								case 'binding': {
									const test__compare_values___node___binding_nval = context.cast('binding');
									return resolve(context)
										.then(() => self)
										.then(context => context?.component_root.input.expected())
										.then(context => {
											switch (context?.variant.name) {
												case 'file': {
													return resolve(context)
														.then(context => {
															const left = resolve(context)
																.then(context => test__compare_values_nval).result;
															const right = resolve(context)
																.then(() => self)
																.then(context => context?.component_root.input.expected()).result;
															return left?.is(right!) ? left : undefined
														}).result;
												}
												case 'node': {
													const test__compare_values___node___binding___node_nval = context.cast('node');
													return resolve(context)
														.then(context => test__compare_values___node___binding___node_nval)
														.then(context => {
															switch (context?.variant.name) {
																case 'binding': {
																	const test__compare_values___node___binding___node___binding_nval = context.cast('binding');
																	return resolve(context)
																		.then(context => {
																			const left = resolve(context)
																				.then(context => test__compare_values___node___binding_nval).result;
																			const right = resolve(context)
																				.then(context => test__compare_values___node___binding___node___binding_nval).result;
																			return left?.is(right!) ? left : undefined
																		})
																		.then(context => {
																			const conv_context = resolve(context).result!;
																			return new Cnode__test({name: 'binding', definition: conv_context});
																		})
																		.then(context => context?.definitions.value).result;
																}
																case 'gui': {
																	return resolve(context)
																		.then(context => {
																			const left = resolve(context)
																				.then(context => test__compare_values_nval).result;
																			const right = resolve(context)
																				.then(() => self)
																				.then(context => context?.component_root.input.expected()).result;
																			return left?.is(right!) ? left : undefined
																		}).result;
																}
																case undefined: return undefined;
																default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
															};
														}).result;
												}
												case 'number': {
													return resolve(context)
														.then(context => {
															const left = resolve(context)
																.then(context => test__compare_values_nval).result;
															const right = resolve(context)
																.then(() => self)
																.then(context => context?.component_root.input.expected()).result;
															return left?.is(right!) ? left : undefined
														}).result;
												}
												case 'query': {
													return resolve(context)
														.then(context => {
															const left = resolve(context)
																.then(context => test__compare_values_nval).result;
															const right = resolve(context)
																.then(() => self)
																.then(context => context?.component_root.input.expected()).result;
															return left?.is(right!) ? left : undefined
														}).result;
												}
												case 'text': {
													return resolve(context)
														.then(context => {
															const left = resolve(context)
																.then(context => test__compare_values_nval).result;
															const right = resolve(context)
																.then(() => self)
																.then(context => context?.component_root.input.expected()).result;
															return left?.is(right!) ? left : undefined
														}).result;
												}
												case 'view': {
													return resolve(context)
														.then(context => {
															const left = resolve(context)
																.then(context => test__compare_values_nval).result;
															const right = resolve(context)
																.then(() => self)
																.then(context => context?.component_root.input.expected()).result;
															return left?.is(right!) ? left : undefined
														}).result;
												}
												case undefined: return undefined;
												default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
											};
										}).result;
								}
								case 'gui': {
									const test__compare_values___node___gui_nval = context.cast('gui');
									return resolve(context)
										.then(() => self)
										.then(context => context?.component_root.input.expected())
										.then(context => {
											switch (context?.variant.name) {
												case 'file': {
													return resolve(context)
														.then(context => {
															const left = resolve(context)
																.then(context => test__compare_values_nval).result;
															const right = resolve(context)
																.then(() => self)
																.then(context => context?.component_root.input.expected()).result;
															return left?.is(right!) ? left : undefined
														}).result;
												}
												case 'node': {
													const test__compare_values___node___gui___node_nval = context.cast('node');
													return resolve(context)
														.then(context => test__compare_values___node___gui___node_nval)
														.then(context => {
															switch (context?.variant.name) {
																case 'binding': {
																	return resolve(context)
																		.then(context => {
																			const left = resolve(context)
																				.then(context => test__compare_values_nval).result;
																			const right = resolve(context)
																				.then(() => self)
																				.then(context => context?.component_root.input.expected()).result;
																			return left?.is(right!) ? left : undefined
																		}).result;
																}
																case 'gui': {
																	const test__compare_values___node___gui___node___gui_nval = context.cast('gui');
																	return resolve(context)
																		.then(context => {
																			const left = resolve(context)
																				.then(context => test__compare_values___node___gui_nval).result;
																			const right = resolve(context)
																				.then(context => test__compare_values___node___gui___node___gui_nval).result;
																			return left?.is(right!) ? left : undefined
																		})
																		.then(context => {
																			const conv_context = resolve(context).result!;
																			return new Cnode__test({name: 'gui', definition: conv_context});
																		})
																		.then(context => context?.definitions.value).result;
																}
																case undefined: return undefined;
																default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
															};
														}).result;
												}
												case 'number': {
													return resolve(context)
														.then(context => {
															const left = resolve(context)
																.then(context => test__compare_values_nval).result;
															const right = resolve(context)
																.then(() => self)
																.then(context => context?.component_root.input.expected()).result;
															return left?.is(right!) ? left : undefined
														}).result;
												}
												case 'query': {
													return resolve(context)
														.then(context => {
															const left = resolve(context)
																.then(context => test__compare_values_nval).result;
															const right = resolve(context)
																.then(() => self)
																.then(context => context?.component_root.input.expected()).result;
															return left?.is(right!) ? left : undefined
														}).result;
												}
												case 'text': {
													return resolve(context)
														.then(context => {
															const left = resolve(context)
																.then(context => test__compare_values_nval).result;
															const right = resolve(context)
																.then(() => self)
																.then(context => context?.component_root.input.expected()).result;
															return left?.is(right!) ? left : undefined
														}).result;
												}
												case 'view': {
													return resolve(context)
														.then(context => {
															const left = resolve(context)
																.then(context => test__compare_values_nval).result;
															const right = resolve(context)
																.then(() => self)
																.then(context => context?.component_root.input.expected()).result;
															return left?.is(right!) ? left : undefined
														}).result;
												}
												case undefined: return undefined;
												default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
											};
										}).result;
								}
								case undefined: return undefined;
								default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
							};
						}).result;
				}
				case 'number': {
					return resolve(context)
						.then(context => {
							const left = resolve(context)
								.then(context => test__compare_values_nval).result;
							const right = resolve(context)
								.then(() => self)
								.then(context => context?.component_root.input.expected()).result;
							return left?.is(right!) ? left : undefined
						}).result;
				}
				case 'query': {
					const test__compare_values___query_nval = context.cast('query');
					return resolve(context)
						.then(() => self)
						.then(context => context?.component_root.input.expected())
						.then(context => {
							switch (context?.variant.name) {
								case 'file': {
									return resolve(context)
										.then(context => {
											const left = resolve(context)
												.then(context => test__compare_values_nval).result;
											const right = resolve(context)
												.then(() => self)
												.then(context => context?.component_root.input.expected()).result;
											return left?.is(right!) ? left : undefined
										}).result;
								}
								case 'node': {
									return resolve(context)
										.then(context => {
											const left = resolve(context)
												.then(context => test__compare_values_nval).result;
											const right = resolve(context)
												.then(() => self)
												.then(context => context?.component_root.input.expected()).result;
											return left?.is(right!) ? left : undefined
										}).result;
								}
								case 'number': {
									return resolve(context)
										.then(context => {
											const left = resolve(context)
												.then(context => test__compare_values_nval).result;
											const right = resolve(context)
												.then(() => self)
												.then(context => context?.component_root.input.expected()).result;
											return left?.is(right!) ? left : undefined
										}).result;
								}
								case 'query': {
									const test__compare_values___query___query_nval = context.cast('query');
									return resolve(context)
										.then(context => {
											const left = resolve(context)
												.then(context => test__compare_values___query_nval).result;
											const right = resolve(context)
												.then(context => test__compare_values___query___query_nval).result;
											return left?.is(right!) ? left : undefined
										})
										.then(context => {
											const conv_context = resolve(context).result!;
											return new Cvalue__test({name: 'query', definition: conv_context});
										}).result;
								}
								case 'text': {
									return resolve(context)
										.then(context => {
											const left = resolve(context)
												.then(context => test__compare_values_nval).result;
											const right = resolve(context)
												.then(() => self)
												.then(context => context?.component_root.input.expected()).result;
											return left?.is(right!) ? left : undefined
										}).result;
								}
								case 'view': {
									return resolve(context)
										.then(context => {
											const left = resolve(context)
												.then(context => test__compare_values_nval).result;
											const right = resolve(context)
												.then(() => self)
												.then(context => context?.component_root.input.expected()).result;
											return left?.is(right!) ? left : undefined
										}).result;
								}
								case undefined: return undefined;
								default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
							};
						}).result;
				}
				case 'text': {
					return resolve(context)
						.then(context => {
							const left = resolve(context)
								.then(context => test__compare_values_nval).result;
							const right = resolve(context)
								.then(() => self)
								.then(context => context?.component_root.input.expected()).result;
							return left?.is(right!) ? left : undefined
						}).result;
				}
				case 'view': {
					const test__compare_values___view_nval = context.cast('view');
					return resolve(context)
						.then(context => test__compare_values___view_nval)
						.then(context => {
							switch (context?.variant.name) {
								case 'explicit': {
									const test__compare_values___view___explicit_nval = context.cast('explicit');
									return resolve(context)
										.then(() => self)
										.then(context => context?.component_root.input.expected())
										.then(context => {
											switch (context?.variant.name) {
												case 'file': {
													return resolve(context)
														.then(context => {
															const left = resolve(context)
																.then(context => test__compare_values_nval).result;
															const right = resolve(context)
																.then(() => self)
																.then(context => context?.component_root.input.expected()).result;
															return left?.is(right!) ? left : undefined
														}).result;
												}
												case 'node': {
													const test__compare_values___view___explicit___node_nval = context.cast('node');
													return resolve(context)
														.then(context => test__compare_values___view___explicit___node_nval)
														.then(context => {
															switch (context?.variant.name) {
																case 'binding': {
																	return resolve(context)
																		.then(context => {
																			const left = resolve(context)
																				.then(context => test__compare_values_nval).result;
																			const right = resolve(context)
																				.then(() => self)
																				.then(context => context?.component_root.input.expected()).result;
																			return left?.is(right!) ? left : undefined
																		}).result;
																}
																case 'gui': {
																	const test__compare_values___view___explicit___node___gui_nval = context.cast('gui');
																	return resolve(context)
																		.then(context => test__compare_values___view___explicit_nval)
																		.then(context => {
																			switch (context?.variant.name) {
																				case 'open view': {
																					const test__compare_values___view___explicit___node___gui___open_view_nval = context.cast('open view');
																					return resolve(context)
																						.then(context => {
																							const left = resolve(context)
																								.then(context => test__compare_values___view___explicit___node___gui___open_view_nval)
																								.then(context => context?.properties.node_type)
																								.then(context => context?.component_root.output.result_gui_node()).result;
																							const right = resolve(context)
																								.then(context => test__compare_values___view___explicit___node___gui_nval).result;
																							return left?.is(right!) ? left : undefined
																						})
																						.then(context => {
																							const conv_context = resolve(context).result!;
																							return new Cnode__test({name: 'gui', definition: conv_context});
																						})
																						.then(context => context?.definitions.value).result;
																				}
																				case 'view definition': {
																					return resolve(context)
																						.then(context => {
																							const left = resolve(context)
																								.then(context => test__compare_values_nval).result;
																							const right = resolve(context)
																								.then(() => self)
																								.then(context => context?.component_root.input.expected()).result;
																							return left?.is(right!) ? left : undefined
																						}).result;
																				}
																				case undefined: return undefined;
																				default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
																			};
																		}).result;
																}
																case undefined: return undefined;
																default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
															};
														}).result;
												}
												case 'number': {
													return resolve(context)
														.then(context => {
															const left = resolve(context)
																.then(context => test__compare_values_nval).result;
															const right = resolve(context)
																.then(() => self)
																.then(context => context?.component_root.input.expected()).result;
															return left?.is(right!) ? left : undefined
														}).result;
												}
												case 'query': {
													return resolve(context)
														.then(context => {
															const left = resolve(context)
																.then(context => test__compare_values_nval).result;
															const right = resolve(context)
																.then(() => self)
																.then(context => context?.component_root.input.expected()).result;
															return left?.is(right!) ? left : undefined
														}).result;
												}
												case 'text': {
													return resolve(context)
														.then(context => {
															const left = resolve(context)
																.then(context => test__compare_values_nval).result;
															const right = resolve(context)
																.then(() => self)
																.then(context => context?.component_root.input.expected()).result;
															return left?.is(right!) ? left : undefined
														}).result;
												}
												case 'view': {
													const test__compare_values___view___explicit___view_nval = context.cast('view');
													return resolve(context)
														.then(context => test__compare_values___view___explicit___view_nval)
														.then(context => {
															switch (context?.variant.name) {
																case 'explicit': {
																	const test__compare_values___view___explicit___view___explicit_nval = context.cast('explicit');
																	return resolve(context)
																		.then(context => {
																			const left = resolve(context)
																				.then(context => test__compare_values___view___explicit_nval)
																				.then(context => context?.component_root.output.definition()).result;
																			const right = resolve(context)
																				.then(context => test__compare_values___view___explicit___view___explicit_nval)
																				.then(context => context?.component_root.output.definition()).result;
																			return left?.is(right!) ? left : undefined
																		})
																		.then(context => {
																			const conv_context = resolve(context).result!;
																			return new Cexplicit_view({name: 'view definition', definition: conv_context});
																		})
																		.then(context => context?.definitions.view)
																		.then(context => context?.definitions.value).result;
																}
																case 'implicit': {
																	return resolve(context)
																		.then(context => test__compare_values_nval).result;
																}
																case undefined: return undefined;
																default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
															};
														}).result;
												}
												case undefined: return undefined;
												default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
											};
										}).result;
								}
								case 'implicit': {
									return resolve(context)
										.then(() => self)
										.then(context => context?.component_root.input.expected())
										.then(context => {
											switch (context?.variant.name) {
												case 'file': {
													return resolve(context)
														.then(context => {
															const left = resolve(context)
																.then(context => test__compare_values_nval).result;
															const right = resolve(context)
																.then(() => self)
																.then(context => context?.component_root.input.expected()).result;
															return left?.is(right!) ? left : undefined
														}).result;
												}
												case 'node': {
													return resolve(context)
														.then(context => {
															const left = resolve(context)
																.then(context => test__compare_values_nval).result;
															const right = resolve(context)
																.then(() => self)
																.then(context => context?.component_root.input.expected()).result;
															return left?.is(right!) ? left : undefined
														}).result;
												}
												case 'number': {
													return resolve(context)
														.then(context => {
															const left = resolve(context)
																.then(context => test__compare_values_nval).result;
															const right = resolve(context)
																.then(() => self)
																.then(context => context?.component_root.input.expected()).result;
															return left?.is(right!) ? left : undefined
														}).result;
												}
												case 'query': {
													return resolve(context)
														.then(context => {
															const left = resolve(context)
																.then(context => test__compare_values_nval).result;
															const right = resolve(context)
																.then(() => self)
																.then(context => context?.component_root.input.expected()).result;
															return left?.is(right!) ? left : undefined
														}).result;
												}
												case 'text': {
													return resolve(context)
														.then(context => {
															const left = resolve(context)
																.then(context => test__compare_values_nval).result;
															const right = resolve(context)
																.then(() => self)
																.then(context => context?.component_root.input.expected()).result;
															return left?.is(right!) ? left : undefined
														}).result;
												}
												case 'view': {
													const test__compare_values___view___implicit___view_nval = context.cast('view');
													return resolve(context)
														.then(context => test__compare_values___view___implicit___view_nval)
														.then(context => {
															switch (context?.variant.name) {
																case 'explicit': {
																	return resolve(context)
																		.then(context => {
																			const left = resolve(context)
																				.then(context => test__compare_values_nval).result;
																			const right = resolve(context)
																				.then(() => self)
																				.then(context => context?.component_root.input.expected()).result;
																			return left?.is(right!) ? left : undefined
																		}).result;
																}
																case 'implicit': {
																	return resolve(context)
																		.then(context => test__compare_values_nval).result;
																}
																case undefined: return undefined;
																default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
															};
														}).result;
												}
												case undefined: return undefined;
												default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
											};
										}).result;
								}
								case undefined: return undefined;
								default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
							};
						}).result;
				}
				case undefined: return undefined;
				default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
			};
		}).result!
}
type Vcollection__test = { name: 'binding', definition: client_bindings.Ccollection__type__properties}|{ name: 'gui', definition: gui_model.Ccollection__type__property}
export class Ccollection__test extends AlanStruct {
	constructor(
		public readonly variant:Vcollection__test) { super(); }
	public definitions:{
		value_context: Cvalue_context;
	} = {
		value_context: new Cvalue_context({name:'entry', definition: this})
	}
	public cast<K extends Vcollection__test['name']>(_variant:K):Extract<Vcollection__test, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vcollection__test['name']]:(($:Extract<Vcollection__test, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'collection'`; }
	public is(other:Ccollection__test):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
type Vboolean = { name: 'false', definition: (typeof Cboolean.Pfalse_)}|{ name: 'true', definition: (typeof Cboolean.Ptrue_)}
export class Cboolean extends AlanStruct {
	public static Pfalse_:Cboolean = new class PrimitiveInstance extends Cboolean {
		constructor () {
			super({name: 'false', definition: undefined as unknown as Cboolean})
			this.variant.definition = this;
		}
	}
	public static Ptrue_:Cboolean = new class PrimitiveInstance extends Cboolean {
		constructor () {
			super({name: 'true', definition: undefined as unknown as Cboolean})
			this.variant.definition = this;
		}
	}
	constructor(
		public readonly variant:Vboolean) { super(); }
	public cast<K extends Vboolean['name']>(_variant:K):Extract<Vboolean, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vboolean['name']]:(($:Extract<Vboolean, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'boolean'`; }
	public is(other:Cboolean):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
export import Cfalse = test.Cboolean;
export import Ctrue = test.Cboolean;
type Vattribute__test = { name: 'binding', definition: client_bindings.Cproperties}|{ name: 'gui', definition: gui_model.Cattributes}
export class Cattribute__test extends AlanStruct {
	constructor(
		public readonly variant:Vattribute__test) { super(); }
	public definitions:{
		value_context: Cvalue_context;
	} = {
		value_context: new Cvalue_context({name:'attribute', definition: this})
	}
	public cast<K extends Vattribute__test['name']>(_variant:K):Extract<Vattribute__test, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vattribute__test['name']]:(($:Extract<Vattribute__test, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'attribute'`; }
	public is(other:Cattribute__test):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}

export type Ttest = {
	'background':'no'|['no', {}]|['yes', Tyes__background];
	'default views lib':'none'|['none', {}]|['set', Tset__default_views_lib];
	'default window':'none'|['none', {}]|['set', Tset__default_window];
	'procedures':Record<string, Tprocedures>;
	'scenarios':Record<string, Tscenarios>;
};
export class Ctest extends AlanNode {
	public key?:string;
	public symbols?: Symbols;
	public get root() { return this; }
	public readonly properties:{
		readonly background:Ctest.Dbackground,
		readonly default_views_lib:Ctest.Ddefault_views_lib,
		readonly default_window:Ctest.Ddefault_window,
		readonly procedures:Ctest.Dprocedures,
		readonly scenarios:Ctest.Dscenarios
	};
	constructor(init:Ttest, public readonly input: {
	'client_bindings':client_bindings.Cclient_bindings
	'gui_model':gui_model.Cgui_model
	'settings':settings.Csettings
	'views':{[name:string]:views.Cviews}}, symbols_file?: SymbolsFile) {
		super();
		if (symbols_file) {
			this.symbols = parseSymbolsFile(symbols_file);
		}
		const $this = this;
		this.properties = {
			background: new Ctest.Dbackground(init['background'], $this),
			default_views_lib: new Ctest.Ddefault_views_lib(init['default views lib'], $this),
			default_window: new Ctest.Ddefault_window(init['default window'], $this),
			procedures: new Ctest.Dprocedures(init['procedures'], $this),
			scenarios: new Ctest.Dscenarios(init['scenarios'], $this)
		};
	}
	public get path() { return ``; }
	public get entity() { return this; }
}
export type Tno__background = {
};
export class Cno__background extends AlanNode {
	public readonly output:{
		stack: () => test.Cstack;
	} = {
		stack: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => test.Cstack.Pempty).result!)
	}
	constructor(init:Tno__background, public parent:Ctest) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'background'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__background = {
	'statements':Tstatements;
	'timeout':'no'|['no', {}]|['yes', Tyes__timeout__yes];
};
export class Cyes__background extends AlanNode {
	public readonly properties:{
		readonly statements:Cstatements,
		readonly timeout:Cyes__background.Dtimeout
	};
	public readonly output:{
		stack: () => test.Cstack;
	} = {
		stack: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.statements)
			.then(context => context?.component_root.output.stack()).result!)
	}
	constructor(init:Tyes__background, public parent:Ctest) {
		super();
		const $this = this;
		this.properties = {
			statements: new Cyes__background.Dstatements(init['statements'], $this),
			timeout: new Cyes__background.Dtimeout(init['timeout'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'background'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__timeout__yes = {
};
export class Cno__timeout__yes extends AlanNode {
	constructor(init:Tno__timeout__yes, public parent:Cyes__background) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'timeout'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__timeout__yes = {
	'max test duration':number;
};
export class Cyes__timeout__yes extends AlanNode {
	public readonly properties:{
		readonly max_test_duration:Cyes__timeout__yes.Dmax_test_duration
	};
	constructor(init:Tyes__timeout__yes, public parent:Cyes__background) {
		super();
		const $this = this;
		this.properties = {
			max_test_duration: new Cyes__timeout__yes.Dmax_test_duration(init['max test duration'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'timeout'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnone__default_views_lib = {
};
export class Cnone__default_views_lib extends AlanNode {
	constructor(init:Tnone__default_views_lib, public parent:Ctest) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'default views lib'?'none'`; }
	public get entity() { return this.parent.entity; }
}
export type Tset__default_views_lib = {
	'views lib':string;
};
export class Cset__default_views_lib extends AlanNode {
	public readonly properties:{
		readonly views_lib:Cset__default_views_lib.Dviews_lib
	};
	constructor(init:Tset__default_views_lib, public parent:Ctest) {
		super();
		const $this = this;
		this.properties = {
			views_lib: new Cset__default_views_lib.Dviews_lib(init['views lib'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'default views lib'?'set'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnone__default_window = {
};
export class Cnone__default_window extends AlanNode {
	constructor(init:Tnone__default_window, public parent:Ctest) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'default window'?'none'`; }
	public get entity() { return this.parent.entity; }
}
export type Tset__default_window = {
	'window':string;
};
export class Cset__default_window extends AlanNode {
	public readonly properties:{
		readonly window:Cset__default_window.Dwindow
	};
	constructor(init:Tset__default_window, public parent:Ctest) {
		super();
		const $this = this;
		this.properties = {
			window: new Cset__default_window.Dwindow(init['window'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'default window'?'set'`; }
	public get entity() { return this.parent.entity; }
}
export type Tprocedures = {
	'body':Tbody;
	'has parameters':'no'|['no', {}]|['yes', Tyes__has_parameters__procedures];
};
export class Cprocedures extends AlanDictionaryEntry {
	public key:string;
	public get key_value() { return this.key; }
	public readonly properties:{
		readonly body:Cbody,
		readonly has_parameters:Cprocedures.Dhas_parameters
	};
	constructor(key:string, init:Tprocedures, public parent:Ctest) {
		super();
		const $this = this;
		this.key = key;
		this.properties = {
			body: new Cbody(init['body'], $this),
			has_parameters: new Cprocedures.Dhas_parameters(init['has parameters'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'procedures'['${this.key}']`; }
	public get entity() { return this; }
}
export type Tbody = {
	'output type':'none'|['none', {}]|['value', Tvalue__output_type];
	'statements':Tstatements;
};
export class Cbody extends AlanNode {
	public readonly properties:{
		readonly output_type:Cbody.Doutput_type,
		readonly statements:Cstatements
	};
	constructor(init:Tbody, public parent:Cprocedures) {
		super();
		const $this = this;
		this.properties = {
			output_type: new Cbody.Doutput_type(init['output type'], $this),
			statements: new Cbody.Dstatements(init['statements'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'body'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnone__output_type = {
};
export class Cnone__output_type extends AlanNode {
	public readonly output:{
		value: () => test.Coptional_value;
	} = {
		value: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => test.Coptional_value.Pnone).result!)
	}
	constructor(init:Tnone__output_type, public parent:Cbody) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'output type'?'none'`; }
	public get entity() { return this.parent.entity; }
}
export type Tvalue__output_type = {
	'return value':Tvalue_expression;
};
export class Cvalue__output_type extends AlanNode {
	public readonly properties:{
		readonly return_value:Cvalue_expression
	};
	public readonly output:{
		value: () => test.Coptional_value;
	} = {
		value: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.return_value)
			.then(context => context?.component_root.output.value()).result!)
	}
	constructor(init:Tvalue__output_type, public parent:Cbody) {
		super();
		const $this = this;
		this.properties = {
			return_value: new Cvalue__output_type.Dreturn_value(init['return value'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'output type'?'value'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__has_parameters__procedures = {
};
export class Cno__has_parameters__procedures extends AlanNode {
	public readonly output:{
		stack: () => test.Cstack;
	} = {
		stack: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => test.Cstack.Pempty).result!)
	}
	constructor(init:Tno__has_parameters__procedures, public parent:Cprocedures) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has parameters'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_parameters__procedures = {
	'parameters':Tparameter_definition;
};
export class Cyes__has_parameters__procedures extends AlanNode {
	public readonly properties:{
		readonly parameters:Cparameter_definition
	};
	public readonly output:{
		stack: () => test.Cstack;
	} = {
		stack: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.parameters)
			.then(context => context?.component_root.output.stack()).result!)
	}
	constructor(init:Tyes__has_parameters__procedures, public parent:Cprocedures) {
		super();
		const $this = this;
		this.properties = {
			parameters: new Cyes__has_parameters__procedures.Dparameters(init['parameters'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has parameters'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tscenarios = {
	'given':'no'|['no', {}]|['yes', Tyes__given];
	'then':'no'|['no', {}]|['yes', Tyes__then];
	'timeout':'no'|['no', {}]|['yes', Tyes__timeout__scenarios];
	'when':'no'|['no', {}]|['yes', Tyes__when];
};
export class Cscenarios extends AlanDictionaryEntry {
	public key:string;
	public get key_value() { return this.key; }
	public readonly properties:{
		readonly given:Cscenarios.Dgiven,
		readonly then:Cscenarios.Dthen,
		readonly timeout:Cscenarios.Dtimeout,
		readonly when:Cscenarios.Dwhen
	};
	constructor(key:string, init:Tscenarios, public parent:Ctest) {
		super();
		const $this = this;
		this.key = key;
		this.properties = {
			given: new Cscenarios.Dgiven(init['given'], $this),
			then: new Cscenarios.Dthen(init['then'], $this),
			timeout: new Cscenarios.Dtimeout(init['timeout'], $this),
			when: new Cscenarios.Dwhen(init['when'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'scenarios'['${this.key}']`; }
	public get entity() { return this; }
}
export type Tno__given = {
};
export class Cno__given extends AlanNode {
	public readonly output:{
		stack: () => test.Cstack;
	} = {
		stack: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.parent)
			.then(context => context?.parent)
			.then(context => context?.properties.background?.state.node.output.stack()).result!)
	}
	constructor(init:Tno__given, public parent:Cscenarios) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'given'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__given = {
	'statements':Tstatements;
};
export class Cyes__given extends AlanNode {
	public readonly properties:{
		readonly statements:Cstatements
	};
	public readonly output:{
		stack: () => test.Cstack;
	} = {
		stack: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.statements)
			.then(context => context?.component_root.output.stack()).result!)
	}
	constructor(init:Tyes__given, public parent:Cscenarios) {
		super();
		const $this = this;
		this.properties = {
			statements: new Cyes__given.Dstatements(init['statements'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'given'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__then = {
};
export class Cno__then extends AlanNode {
	constructor(init:Tno__then, public parent:Cscenarios) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'then'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__then = {
	'statements':Tstatements;
};
export class Cyes__then extends AlanNode {
	public readonly properties:{
		readonly statements:Cstatements
	};
	constructor(init:Tyes__then, public parent:Cscenarios) {
		super();
		const $this = this;
		this.properties = {
			statements: new Cyes__then.Dstatements(init['statements'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'then'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__timeout__scenarios = {
};
export class Cno__timeout__scenarios extends AlanNode {
	constructor(init:Tno__timeout__scenarios, public parent:Cscenarios) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'timeout'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__timeout__scenarios = {
	'max test duration':number;
};
export class Cyes__timeout__scenarios extends AlanNode {
	public readonly properties:{
		readonly max_test_duration:Cyes__timeout__scenarios.Dmax_test_duration
	};
	constructor(init:Tyes__timeout__scenarios, public parent:Cscenarios) {
		super();
		const $this = this;
		this.properties = {
			max_test_duration: new Cyes__timeout__scenarios.Dmax_test_duration(init['max test duration'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'timeout'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__when = {
};
export class Cno__when extends AlanNode {
	public readonly output:{
		stack: () => test.Cstack;
	} = {
		stack: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.parent)
			.then(context => context?.properties.given?.state.node.output.stack()).result!)
	}
	constructor(init:Tno__when, public parent:Cscenarios) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'when'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__when = {
	'statements':Tstatements;
};
export class Cyes__when extends AlanNode {
	public readonly properties:{
		readonly statements:Cstatements
	};
	public readonly output:{
		stack: () => test.Cstack;
	} = {
		stack: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.statements)
			.then(context => context?.component_root.output.stack()).result!)
	}
	constructor(init:Tyes__when, public parent:Cscenarios) {
		super();
		const $this = this;
		this.properties = {
			statements: new Cyes__when.Dstatements(init['statements'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'when'?'yes'`; }
	public get entity() { return this.parent.entity; }
}

/* property classes */
export namespace Ccondition {
	export class Dassign extends StateGroup<
		{ name: 'no', node:Cno__assign__condition, init:Tno__assign__condition}|
		{ name: 'yes', node:Cyes__assign__condition, init:Tyes__assign__condition}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__assign__condition, parent:Ccondition) => new Cno__assign__condition(init, parent);
				case 'yes': return (init:Tyes__assign__condition, parent:Ccondition) => new Cyes__assign__condition(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__assign__condition;
				case 'yes': return finalize_yes__assign__condition;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tcondition['assign'], parent:Ccondition) {
			super(data, parent, 'assign');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'assign'`; }
	}
	export class Dexpression_type extends StateGroup<
		{ name: 'binary', node:Cbinary, init:Tbinary}|
		{ name: 'unary', node:Cunary, init:Tunary}> {
		protected initializer(state: 'binary'|'unary') {
			switch (state) {
				case 'binary': return (init:Tbinary, parent:Ccondition) => new Cbinary(init, parent);
				case 'unary': return (init:Tunary, parent:Ccondition) => new Cunary(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'binary'|'unary') {
			switch (state) {
				case 'binary': return finalize_binary;
				case 'unary': return finalize_unary;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tcondition['expression type'], parent:Ccondition) {
			super(data, parent, 'expression type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'expression type'`; }
	}
	export class Dvalue_condition extends StateGroup<
		{ name: 'exists', node:Cexists__value_condition, init:Texists__value_condition}|
		{ name: 'not exists', node:Cnot_exists, init:Tnot_exists}> {
		protected initializer(state: 'exists'|'not exists') {
			switch (state) {
				case 'exists': return (init:Texists__value_condition, parent:Ccondition) => new Cexists__value_condition(init, parent);
				case 'not exists': return (init:Tnot_exists, parent:Ccondition) => new Cnot_exists(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'exists'|'not exists') {
			switch (state) {
				case 'exists': return finalize_exists__value_condition;
				case 'not exists': return finalize_not_exists;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tcondition['value condition'], parent:Ccondition) {
			super(data, parent, 'value condition');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'value condition'`; }
	}
	export class Dvalue_expression extends Cvalue_expression {
		constructor(data:Tcondition['value expression'], parent:Ccondition) {
			super(data, parent, {
				stack: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.stack()).result!)
			}, 'value expression')
		}
	}
}
export namespace Cyes__assign__condition {
	export class Dstack_frame extends Cstack_frame {
		constructor(data:Tyes__assign__condition['stack frame'], parent:Cyes__assign__condition) {
			super(data, parent, {
				parent: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.stack()).result!),
				value: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.inferences.value()).result!)
			}, 'stack frame')
		}
	}
}
export namespace Cbinary {
	export class Dcondition extends StateGroup<
		{ name: 'equals', node:Cequals, init:Tequals}|
		{ name: 'not equals', node:Cnot_equals, init:Tnot_equals}> {
		protected initializer(state: 'equals'|'not equals') {
			switch (state) {
				case 'equals': return (init:Tequals, parent:Cbinary) => new Cequals(init, parent);
				case 'not equals': return (init:Tnot_equals, parent:Cbinary) => new Cnot_equals(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'equals'|'not equals') {
			switch (state) {
				case 'equals': return finalize_equals;
				case 'not equals': return finalize_not_equals;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tbinary['condition'], parent:Cbinary) {
			super(data, parent, 'condition');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'condition'`; }
	}
	export class Dtarget extends Cvalue_expression {
		public readonly inferences:{
			correct_value: () => test.Cvalue__test,
			value: () => test.Cvalue__test
		}
		constructor(data:Tbinary['target'], parent:Cbinary) {
			super(data, parent, {
				stack: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.stack()).result!)
			}, 'target')
			this.inferences = {
				correct_value: cache((detach:boolean) => {
					const test__condition__expression_type__binary__target_nval = this;
					return resolve(this)
						.then(context => evaluate__compare_values(detach, 
							resolve(context)
								.then(() => parent)
								.then(context => context?.inferences.value()).result!, {
							expected: () => resolve(parent)
								.then(() => parent.properties.target.inferences.value()).result!

							})).result!;
				})
				,
				value: cache((detach:boolean) => {
					const test__condition__expression_type__binary__target_nval = this;
					return resolve(this)
						.then(context => test__condition__expression_type__binary__target_nval)
						.then(context => context?.component_root.output.value())
						.then(context => context?.variant.name === 'value' ? context.variant.definition as test.Cvalue__test : undefined).result!;
				})

			}
		}
	}
}
export namespace Cinstruction_argument {
	export class Dnext_argument extends StateGroup<
		{ name: 'exists', node:Cexists__next_argument, init:Texists__next_argument}|
		{ name: 'no', node:Cno__next_argument, init:Tno__next_argument}> {
		protected initializer(state: 'exists'|'no') {
			switch (state) {
				case 'exists': return (init:Texists__next_argument, parent:Cinstruction_argument) => new Cexists__next_argument(init, parent);
				case 'no': return (init:Tno__next_argument, parent:Cinstruction_argument) => new Cno__next_argument(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'exists'|'no') {
			switch (state) {
				case 'exists': return finalize_exists__next_argument;
				case 'no': return finalize_no__next_argument;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tinstruction_argument['next argument'], parent:Cinstruction_argument) {
			super(data, parent, 'next argument');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'next argument'`; }
	}
	export class Dvalue_expression extends Cvalue_expression {
		public readonly inferences:{
			correct_type: () => test.Cboolean,
			type_is_correct: () => test.Ctrue,
			value: () => test.Cvalue__test
		}
		constructor(data:Tinstruction_argument['value expression'], parent:Cinstruction_argument) {
			super(data, parent, {
				stack: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.stack()).result!)
			}, 'value expression')
			this.inferences = {
				correct_type: cache((detach:boolean) => {
					const test__instruction_argument__value_expression_nval = this;
					return resolve(this)
						.then(() => parent.properties.value_expression.inferences.value())
						.then(context => {
							switch (context?.variant.name) {
								case 'file': {
									return resolve(context)
										.then(() => parent)
										.then(context => context?.component_root.input.argument())
										.then(context => context?.properties.type)
										.then(context => {
											const state_group = context;
											switch (state_group?.state.name) {
												case 'file': {
													return resolve(context)
														.then(() => parent)
														.then(() => test.Cboolean.Ptrue_).result;
												}
												case 'number': {
													return resolve(context)
														.then(() => parent)
														.then(() => test.Cboolean.Pfalse_).result;
												}
												case 'text': {
													return resolve(context)
														.then(() => parent)
														.then(() => test.Cboolean.Pfalse_).result;
												}
												case 'view': {
													return resolve(context)
														.then(() => parent)
														.then(() => test.Cboolean.Pfalse_).result;
												}
												case undefined: return undefined;
												default: throw new Error(`Unexpected state '${(state_group!.state as any).name}'`);
											};}).result;
								}
								case 'node': {
									return resolve(context)
										.then(() => parent)
										.then(() => test.Cboolean.Pfalse_).result;
								}
								case 'number': {
									return resolve(context)
										.then(() => parent)
										.then(context => context?.component_root.input.argument())
										.then(context => context?.properties.type)
										.then(context => {
											const state_group = context;
											switch (state_group?.state.name) {
												case 'file': {
													return resolve(context)
														.then(() => parent)
														.then(() => test.Cboolean.Pfalse_).result;
												}
												case 'number': {
													return resolve(context)
														.then(() => parent)
														.then(() => test.Cboolean.Ptrue_).result;
												}
												case 'text': {
													return resolve(context)
														.then(() => parent)
														.then(() => test.Cboolean.Pfalse_).result;
												}
												case 'view': {
													return resolve(context)
														.then(() => parent)
														.then(() => test.Cboolean.Pfalse_).result;
												}
												case undefined: return undefined;
												default: throw new Error(`Unexpected state '${(state_group!.state as any).name}'`);
											};}).result;
								}
								case 'query': {
									return resolve(context)
										.then(() => parent)
										.then(() => test.Cboolean.Pfalse_).result;
								}
								case 'text': {
									return resolve(context)
										.then(() => parent)
										.then(context => context?.component_root.input.argument())
										.then(context => context?.properties.type)
										.then(context => {
											const state_group = context;
											switch (state_group?.state.name) {
												case 'file': {
													return resolve(context)
														.then(() => parent)
														.then(() => test.Cboolean.Pfalse_).result;
												}
												case 'number': {
													return resolve(context)
														.then(() => parent)
														.then(() => test.Cboolean.Pfalse_).result;
												}
												case 'text': {
													return resolve(context)
														.then(() => parent)
														.then(() => test.Cboolean.Ptrue_).result;
												}
												case 'view': {
													return resolve(context)
														.then(() => parent)
														.then(() => test.Cboolean.Pfalse_).result;
												}
												case undefined: return undefined;
												default: throw new Error(`Unexpected state '${(state_group!.state as any).name}'`);
											};}).result;
								}
								case 'view': {
									return resolve(context)
										.then(() => parent)
										.then(context => context?.component_root.input.argument())
										.then(context => context?.properties.type)
										.then(context => {
											const state_group = context;
											switch (state_group?.state.name) {
												case 'file': {
													return resolve(context)
														.then(() => parent)
														.then(() => test.Cboolean.Pfalse_).result;
												}
												case 'number': {
													return resolve(context)
														.then(() => parent)
														.then(() => test.Cboolean.Pfalse_).result;
												}
												case 'text': {
													return resolve(context)
														.then(() => parent)
														.then(() => test.Cboolean.Pfalse_).result;
												}
												case 'view': {
													return resolve(context)
														.then(() => parent)
														.then(() => test.Cboolean.Ptrue_).result;
												}
												case undefined: return undefined;
												default: throw new Error(`Unexpected state '${(state_group!.state as any).name}'`);
											};}).result;
								}
								case undefined: return undefined;
								default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
							};
						}).result!;
				})
				,
				type_is_correct: cache((detach:boolean) => {
					const test__instruction_argument__value_expression_nval = this;
					return resolve(this)
						.then(() => parent.properties.value_expression.inferences.correct_type())
						.then(context => context?.variant.name === 'true' ? context.variant.definition as test.Ctrue : undefined).result!;
				})
				,
				value: cache((detach:boolean) => {
					const test__instruction_argument__value_expression_nval = this;
					return resolve(this)
						.then(context => test__instruction_argument__value_expression_nval)
						.then(context => context?.component_root.output.value())
						.then(context => context?.variant.name === 'value' ? context.variant.definition as test.Cvalue__test : undefined).result!;
				})

			}
		}
	}
}
export namespace Cexists__next_argument {
	export class Dargument extends Cinstruction_argument {
		constructor(data:Texists__next_argument['argument'], parent:Cexists__next_argument) {
			super(data, parent, {
				argument: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.inferences.exists())
					.then(context => context?.properties.argument).result!),
				stack: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.stack()).result!)
			}, 'argument')
		}
	}
}
export namespace Cnode_type_path {
	export class Dhas_steps extends StateGroup<
		{ name: 'no', node:Cno__has_steps__node_type_path, init:Tno__has_steps__node_type_path}|
		{ name: 'yes', node:Cyes__has_steps__node_type_path, init:Tyes__has_steps__node_type_path}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_steps__node_type_path, parent:Cnode_type_path) => new Cno__has_steps__node_type_path(init, parent);
				case 'yes': return (init:Tyes__has_steps__node_type_path, parent:Cnode_type_path) => new Cyes__has_steps__node_type_path(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_steps__node_type_path;
				case 'yes': return finalize_yes__has_steps__node_type_path;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tnode_type_path['has steps'], parent:Cnode_type_path) {
			super(data, parent, 'has steps');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has steps'`; }
	}
}
export namespace Cyes__has_steps__node_type_path {
	export class Dattribute extends Reference<test.Cattribute__test,string> {

		constructor(data:string, $this:Cyes__has_steps__node_type_path) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.component_root.input.node())
				.then(context => {
					switch (context?.variant.name) {
						case 'binding': {
							const test__node_type_path__has_steps__yes__attribute___binding_nval = context.cast('binding');
							return resolve(context)
								.then(context => test__node_type_path__has_steps__yes__attribute___binding_nval)
								.then(context => {
									const entry = context?.properties.properties.get(this.entry)!;
									return resolve(entry)
									.then(context => {
										const conv_context = resolve(context).result!;
										return new Cattribute__test({name: 'binding', definition: conv_context});
									}).result;
								}).result;
						}
						case 'gui': {
							const test__node_type_path__has_steps__yes__attribute___gui_nval = context.cast('gui');
							return resolve(context)
								.then(context => test__node_type_path__has_steps__yes__attribute___gui_nval)
								.then(context => {
									const entry = context?.properties.attributes.get(this.entry)!;
									return resolve(entry)
									.then(context => {
										const conv_context = resolve(context).result!;
										return new Cattribute__test({name: 'gui', definition: conv_context});
									}).result;
								}).result;
						}
						case undefined: return undefined;
						default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
					};
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'attribute'`; }
	}
	export class Dtail extends Cnode_type_path {
		constructor(data:Tyes__has_steps__node_type_path['tail'], parent:Cyes__has_steps__node_type_path) {
			super(data, parent, {
				node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.type?.state.node.output.node()).result!)
			}, 'tail')
		}
	}
	export class Dtype extends StateGroup<
		{ name: 'entry', node:Centry__type, init:Tentry__type}|
		{ name: 'node', node:Cnode__type__yes, init:Tnode__type__yes}|
		{ name: 'state', node:Cstate__type, init:Tstate__type}> {
		protected initializer(state: 'entry'|'node'|'state') {
			switch (state) {
				case 'entry': return (init:Tentry__type, parent:Cyes__has_steps__node_type_path) => new Centry__type(init, parent);
				case 'node': return (init:Tnode__type__yes, parent:Cyes__has_steps__node_type_path) => new Cnode__type__yes(init, parent);
				case 'state': return (init:Tstate__type, parent:Cyes__has_steps__node_type_path) => new Cstate__type(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'entry'|'node'|'state') {
			switch (state) {
				case 'entry': return finalize_entry__type;
				case 'node': return finalize_node__type__yes;
				case 'state': return finalize_state__type;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__has_steps__node_type_path['type'], parent:Cyes__has_steps__node_type_path) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Cstate__type {
	export class Dstate extends Reference<test.Cstate__test,string> {
		public readonly inferences:{
			node: () => test.Cnode__test
		}

		constructor(data:string, $this:Cstate__type) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.inferences.state_group())
				.then(context => {
					switch (context?.variant.name) {
						case 'binding': {
							const test__node_type_path__has_steps__yes__type__state__state___binding_nval = context.cast('binding');
							return resolve(context)
								.then(context => test__node_type_path__has_steps__yes__type__state__state___binding_nval)
								.then(context => {
									const entry = context?.properties.states.get(this.entry)!;
									return resolve(entry)
									.then(context => {
										const conv_context = resolve(context).result!;
										return new Cstate__test({name: 'binding', definition: conv_context});
									}).result;
								}).result;
						}
						case 'gui': {
							const test__node_type_path__has_steps__yes__type__state__state___gui_nval = context.cast('gui');
							return resolve(context)
								.then(context => test__node_type_path__has_steps__yes__type__state__state___gui_nval)
								.then(context => {
									const entry = context?.properties.states.get(this.entry)!;
									return resolve(entry)
									.then(context => {
										const conv_context = resolve(context).result!;
										return new Cstate__test({name: 'gui', definition: conv_context});
									}).result;
								}).result;
						}
						case undefined: return undefined;
						default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
					};
				}).result!), $this)
			this.inferences = {
				node: cache((detach:boolean) => {
					const test__node_type_path__has_steps__yes__type__state__state_nval = $this.properties.state.ref;
					return resolve($this.properties.state.ref)
						.then(context => test__node_type_path__has_steps__yes__type__state__state_nval)
						.then(context => {
							switch (context?.variant.name) {
								case 'binding': {
									const test__node_type_path__has_steps__yes__type__state__state_inf___node___binding_nval = context.cast('binding');
									return resolve(context)
										.then(context => test__node_type_path__has_steps__yes__type__state__state_inf___node___binding_nval)
										.then(context => context?.properties.node)
										.then(context => {
											const conv_context = resolve(context).result!;
											return new Cnode__test({name: 'binding', definition: conv_context});
										}).result;
								}
								case 'gui': {
									const test__node_type_path__has_steps__yes__type__state__state_inf___node___gui_nval = context.cast('gui');
									return resolve(context)
										.then(context => test__node_type_path__has_steps__yes__type__state__state_inf___node___gui_nval)
										.then(context => context?.properties.gui_node)
										.then(context => {
											const conv_context = resolve(context).result!;
											return new Cnode__test({name: 'gui', definition: conv_context});
										}).result;
								}
								case undefined: return undefined;
								default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
							};
						}).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'state'`; }
	}
}
export namespace Coptional_assignment {
	export class Dassign extends StateGroup<
		{ name: 'no', node:Cno__assign__optional_assignment, init:Tno__assign__optional_assignment}|
		{ name: 'yes', node:Cyes__assign__optional_assignment, init:Tyes__assign__optional_assignment}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__assign__optional_assignment, parent:Coptional_assignment) => new Cno__assign__optional_assignment(init, parent);
				case 'yes': return (init:Tyes__assign__optional_assignment, parent:Coptional_assignment) => new Cyes__assign__optional_assignment(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__assign__optional_assignment;
				case 'yes': return finalize_yes__assign__optional_assignment;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Toptional_assignment['assign'], parent:Coptional_assignment) {
			super(data, parent, 'assign');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'assign'`; }
	}
}
export namespace Cyes__assign__optional_assignment {
	export class Dstack_frame extends Cstack_frame {
		constructor(data:Tyes__assign__optional_assignment['stack frame'], parent:Cyes__assign__optional_assignment) {
			super(data, parent, {
				parent: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.stack()).result!),
				value: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.inferences.value()).result!)
			}, 'stack frame')
		}
	}
}
export namespace Cparameter_definition {
	export class Dhas_next extends StateGroup<
		{ name: 'no', node:Cno__has_next__parameter_definition, init:Tno__has_next__parameter_definition}|
		{ name: 'yes', node:Cyes__has_next__parameter_definition, init:Tyes__has_next__parameter_definition}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_next__parameter_definition, parent:Cparameter_definition) => new Cno__has_next__parameter_definition(init, parent);
				case 'yes': return (init:Tyes__has_next__parameter_definition, parent:Cparameter_definition) => new Cyes__has_next__parameter_definition(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_next__parameter_definition;
				case 'yes': return finalize_yes__has_next__parameter_definition;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tparameter_definition['has next'], parent:Cparameter_definition) {
			super(data, parent, 'has next');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has next'`; }
	}
	export class Dstack_frame extends Cstack_frame {
		constructor(data:Tparameter_definition['stack frame'], parent:Cparameter_definition) {
			super(data, parent, {
				parent: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.stack()).result!),
				value: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.type)
					.then(context => context?.component_root.output.value()).result!)
			}, 'stack frame')
		}
	}
	export class Dtype extends Cvalue_type_definition {
		constructor(data:Tparameter_definition['type'], parent:Cparameter_definition) {
			super(data, parent, 'type')
		}
	}
}
export namespace Cyes__has_next__parameter_definition {
	export class Dparameters extends Cparameter_definition {
		constructor(data:Tyes__has_next__parameter_definition['parameters'], parent:Cyes__has_next__parameter_definition) {
			super(data, parent, {
				stack: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.properties.stack_frame)
					.then(context => context?.definitions.stack).result!)
			}, 'parameters')
		}
	}
}
export namespace Cparameter_implementation {
	export class Dhas_next extends StateGroup<
		{ name: 'no', node:Cno__has_next__parameter_implementation, init:Tno__has_next__parameter_implementation}|
		{ name: 'yes', node:Cyes__has_next__parameter_implementation, init:Tyes__has_next__parameter_implementation}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_next__parameter_implementation, parent:Cparameter_implementation) => new Cno__has_next__parameter_implementation(init, parent);
				case 'yes': return (init:Tyes__has_next__parameter_implementation, parent:Cparameter_implementation) => new Cyes__has_next__parameter_implementation(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_next__parameter_implementation;
				case 'yes': return finalize_yes__has_next__parameter_implementation;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tparameter_implementation['has next'], parent:Cparameter_implementation) {
			super(data, parent, 'has next');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has next'`; }
	}
	export class Dvalue_expression extends Cvalue_expression {
		public readonly inferences:{
			correct_value: () => test.Cvalue__test,
			value: () => test.Cvalue__test
		}
		constructor(data:Tparameter_implementation['value expression'], parent:Cparameter_implementation) {
			super(data, parent, {
				stack: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.stack()).result!)
			}, 'value expression')
			this.inferences = {
				correct_value: cache((detach:boolean) => {
					const test__parameter_implementation__value_expression_nval = this;
					return resolve(this)
						.then(context => evaluate__compare_values(detach, 
							resolve(context)
								.then(() => parent.properties.value_expression.inferences.value()).result!, {
							expected: () => resolve(parent)
								.then(() => parent)
								.then(context => context?.component_root.input.definition())
								.then(context => context?.properties.type)
								.then(context => context?.component_root.output.value()).result!

							})).result!;
				})
				,
				value: cache((detach:boolean) => {
					const test__parameter_implementation__value_expression_nval = this;
					return resolve(this)
						.then(context => test__parameter_implementation__value_expression_nval)
						.then(context => context?.component_root.output.value())
						.then(context => context?.variant.name === 'value' ? context.variant.definition as test.Cvalue__test : undefined).result!;
				})

			}
		}
	}
}
export namespace Cyes__has_next__parameter_implementation {
	export class Dparameters extends Cparameter_implementation {
		constructor(data:Tyes__has_next__parameter_implementation['parameters'], parent:Cyes__has_next__parameter_implementation) {
			super(data, parent, {
				definition: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.inferences.yes())
					.then(context => context?.properties.parameters).result!),
				stack: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.stack()).result!)
			}, 'parameters')
		}
	}
}
export namespace Cstack_frame {
	export class Dname extends Reference<test.Cnamed_values,string> {
		public readonly inferences:{
			single_name: () => test.Cno__has_successor
		}

		constructor(data:string, $this:Cstack_frame) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => {
					const entry = context?.properties.named_values.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
			this.inferences = {
				single_name: cache((detach:boolean) => {
					const test__stack_frame__name_nval = $this.properties.name.ref;
					return resolve($this.properties.name.ref)
						.then(context => test__stack_frame__name_nval)
						.then(context => {
							if (context?.properties.has_successor.state.name === 'no') {
								return resolve(context.properties.has_successor.state.node as test.Cno__has_successor).result;
							} else {
								return undefined;
							}
						}).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'name'`; }
	}
	export class Dnamed_values extends AlanDictionary<{ node:Cnamed_values, init:Tnamed_values},Cstack_frame> {
		protected initialize(parent:Cstack_frame, key:string, entry_init:Tnamed_values) { return new Cnamed_values(key, entry_init, parent); }
		protected finalize = finalize_named_values
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'named values'`; }
		constructor(data:Tstack_frame['named values'], parent:Cstack_frame) {
			super(data, parent);
		}
	}
}
export namespace Cnamed_values {
	export class Dhas_successor extends StateGroup<
		{ name: 'no', node:Cno__has_successor, init:Tno__has_successor}|
		{ name: 'yes', node:Cyes__has_successor, init:Tyes__has_successor}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_successor, parent:Cnamed_values) => new Cno__has_successor(init, parent);
				case 'yes': return (init:Tyes__has_successor, parent:Cnamed_values) => new Cyes__has_successor(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_successor;
				case 'yes': return finalize_yes__has_successor;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tnamed_values['has successor'], parent:Cnamed_values) {
			super(data, parent, 'has successor');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has successor'`; }
	}
}
export namespace Cstack_selector {
	export class Dselect extends StateGroup<
		{ name: 'parent frame', node:Cparent_frame, init:Tparent_frame}|
		{ name: 'this frame', node:Cthis_frame, init:Tthis_frame}> {
		protected initializer(state: 'parent frame'|'this frame') {
			switch (state) {
				case 'parent frame': return (init:Tparent_frame, parent:Cstack_selector) => new Cparent_frame(init, parent);
				case 'this frame': return (init:Tthis_frame, parent:Cstack_selector) => new Cthis_frame(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'parent frame'|'this frame') {
			switch (state) {
				case 'parent frame': return finalize_parent_frame;
				case 'this frame': return finalize_this_frame;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tstack_selector['select'], parent:Cstack_selector) {
			super(data, parent, 'select');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'select'`; }
	}
}
export namespace Cparent_frame {
	export class Dtail extends Cstack_selector {
		constructor(data:Tparent_frame['tail'], parent:Cparent_frame) {
			super(data, parent, {
				stack: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.inferences.frame())
					.then(context => context?.component_root.output.parent()).result!)
			}, 'tail')
		}
	}
}
export namespace Cthis_frame {
	export class Dname extends Reference<test.Cnamed_values,string> {

		constructor(data:string, $this:Cthis_frame) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.inferences.frame())
				.then(context => {
					const entry = context?.properties.named_values.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'name'`; }
	}
}
export namespace Cstatement {
	export class Dtype extends StateGroup<
		{ name: 'assertion', node:Cassertion, init:Tassertion}|
		{ name: 'expression', node:Cexpression__type, init:Texpression__type}|
		{ name: 'if', node:Cif, init:Tif}|
		{ name: 'instruction', node:Cinstruction, init:Tinstruction}|
		{ name: 'panic', node:Cpanic, init:Tpanic}|
		{ name: 'set network conditions', node:Cset_network_conditions, init:Tset_network_conditions}|
		{ name: 'set user', node:Cset_user, init:Tset_user}|
		{ name: 'unset user', node:Cunset_user, init:Tunset_user}|
		{ name: 'wait', node:Cwait, init:Twait}|
		{ name: 'walk', node:Cwalk, init:Twalk}> {
		protected initializer(state: 'assertion'|'expression'|'if'|'instruction'|'panic'|'set network conditions'|'set user'|'unset user'|'wait'|'walk') {
			switch (state) {
				case 'assertion': return (init:Tassertion, parent:Cstatement) => new Cassertion(init, parent);
				case 'expression': return (init:Texpression__type, parent:Cstatement) => new Cexpression__type(init, parent);
				case 'if': return (init:Tif, parent:Cstatement) => new Cif(init, parent);
				case 'instruction': return (init:Tinstruction, parent:Cstatement) => new Cinstruction(init, parent);
				case 'panic': return (init:Tpanic, parent:Cstatement) => new Cpanic(init, parent);
				case 'set network conditions': return (init:Tset_network_conditions, parent:Cstatement) => new Cset_network_conditions(init, parent);
				case 'set user': return (init:Tset_user, parent:Cstatement) => new Cset_user(init, parent);
				case 'unset user': return (init:Tunset_user, parent:Cstatement) => new Cunset_user(init, parent);
				case 'wait': return (init:Twait, parent:Cstatement) => new Cwait(init, parent);
				case 'walk': return (init:Twalk, parent:Cstatement) => new Cwalk(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'assertion'|'expression'|'if'|'instruction'|'panic'|'set network conditions'|'set user'|'unset user'|'wait'|'walk') {
			switch (state) {
				case 'assertion': return finalize_assertion;
				case 'expression': return finalize_expression__type;
				case 'if': return finalize_if;
				case 'instruction': return finalize_instruction;
				case 'panic': return finalize_panic;
				case 'set network conditions': return finalize_set_network_conditions;
				case 'set user': return finalize_set_user;
				case 'unset user': return finalize_unset_user;
				case 'wait': return finalize_wait;
				case 'walk': return finalize_walk;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tstatement['type'], parent:Cstatement) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Cassertion {
	export class Dcondition extends Ccondition {
		constructor(data:Tassertion['condition'], parent:Cassertion) {
			super(data, parent, {
				stack: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.stack()).result!)
			}, 'condition')
		}
	}
}
export namespace Cexpression__type {
	export class Dassignment extends Coptional_assignment {
		constructor(data:Texpression__type['assignment'], parent:Cexpression__type) {
			super(data, parent, {
				stack: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.stack()).result!),
				value: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.value_expression)
					.then(context => context?.component_root.output.value()).result!)
			}, 'assignment')
		}
	}
	export class Dvalue_expression extends Cvalue_expression {
		constructor(data:Texpression__type['value expression'], parent:Cexpression__type) {
			super(data, parent, {
				stack: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.stack()).result!)
			}, 'value expression')
		}
	}
}
export namespace Cif {
	export class Dcondition extends Ccondition {
		constructor(data:Tif['condition'], parent:Cif) {
			super(data, parent, {
				stack: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.stack()).result!)
			}, 'condition')
		}
	}
	export class Delse_ extends StateGroup<
		{ name: 'nothing', node:Cnothing, init:Tnothing}|
		{ name: 'something', node:Csomething, init:Tsomething}> {
		protected initializer(state: 'nothing'|'something') {
			switch (state) {
				case 'nothing': return (init:Tnothing, parent:Cif) => new Cnothing(init, parent);
				case 'something': return (init:Tsomething, parent:Cif) => new Csomething(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'nothing'|'something') {
			switch (state) {
				case 'nothing': return finalize_nothing;
				case 'something': return finalize_something;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tif['else'], parent:Cif) {
			super(data, parent, 'else');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'else'`; }
	}
	export class Dthen extends Cstatements {
		constructor(data:Tif['then'], parent:Cif) {
			super(data, parent, {
				stack: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.condition)
					.then(context => context?.component_root.output.stack()).result!)
			}, 'then')
		}
	}
}
export namespace Csomething {
	export class Dstatements extends Cstatements {
		constructor(data:Tsomething['statements'], parent:Csomething) {
			super(data, parent, {
				stack: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.stack()).result!)
			}, 'statements')
		}
	}
}
export namespace Cinstruction {
	export class Dhas_arguments extends StateGroup<
		{ name: 'no', node:Cno__has_arguments, init:Tno__has_arguments}|
		{ name: 'yes', node:Cyes__has_arguments, init:Tyes__has_arguments}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_arguments, parent:Cinstruction) => new Cno__has_arguments(init, parent);
				case 'yes': return (init:Tyes__has_arguments, parent:Cinstruction) => new Cyes__has_arguments(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_arguments;
				case 'yes': return finalize_yes__has_arguments;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tinstruction['has arguments'], parent:Cinstruction) {
			super(data, parent, 'has arguments');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has arguments'`; }
	}
	export class Dignore_errors extends StateGroup<
		{ name: 'no', node:Cno__ignore_errors, init:Tno__ignore_errors}|
		{ name: 'yes', node:Cyes__ignore_errors, init:Tyes__ignore_errors}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__ignore_errors, parent:Cinstruction) => new Cno__ignore_errors(init, parent);
				case 'yes': return (init:Tyes__ignore_errors, parent:Cinstruction) => new Cyes__ignore_errors(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__ignore_errors;
				case 'yes': return finalize_yes__ignore_errors;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tinstruction['ignore errors'], parent:Cinstruction) {
			super(data, parent, 'ignore errors');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'ignore errors'`; }
	}
	export class Dinstruction extends Reference<client_bindings.Cinstructions,string> {

		constructor(data:string, $this:Cinstruction) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.properties.value_expression?.inferences.binding_node())
				.then(context => {
					const entry = context?.properties.instructions.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'instruction'`; }
	}
	export class Dvalue_expression extends Cvalue_expression {
		public readonly inferences:{
			binding_node: () => client_bindings.Cnode__client_bindings
		}
		constructor(data:Tinstruction['value expression'], parent:Cinstruction) {
			super(data, parent, {
				stack: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.stack()).result!)
			}, 'value expression')
			this.inferences = {
				binding_node: cache((detach:boolean) => {
					const test__statement__type__instruction__value_expression_nval = this;
					return resolve(this)
						.then(() => parent)
						.then(context => context?.properties.value_expression)
						.then(context => context?.component_root.output.value())
						.then(context => context?.variant.name === 'value' ? context.variant.definition as test.Cvalue__test : undefined)
						.then(context => context?.variant.name === 'node' ? context.variant.definition as test.Cnode__test : undefined)
						.then(context => context?.variant.name === 'binding' ? context.variant.definition as client_bindings.Cnode__client_bindings : undefined).result!;
				})

			}
		}
	}
	export class Dwait_on_finish extends StateGroup<
		{ name: 'no', node:Cno__wait_on_finish, init:Tno__wait_on_finish}|
		{ name: 'yes', node:Cyes__wait_on_finish, init:Tyes__wait_on_finish}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__wait_on_finish, parent:Cinstruction) => new Cno__wait_on_finish(init, parent);
				case 'yes': return (init:Tyes__wait_on_finish, parent:Cinstruction) => new Cyes__wait_on_finish(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__wait_on_finish;
				case 'yes': return finalize_yes__wait_on_finish;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tinstruction['wait on finish'], parent:Cinstruction) {
			super(data, parent, 'wait on finish');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'wait on finish'`; }
	}
}
export namespace Cyes__has_arguments {
	export class Dfirst_argument extends Cinstruction_argument {
		constructor(data:Tyes__has_arguments['first argument'], parent:Cyes__has_arguments) {
			super(data, parent, {
				argument: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.inferences.yes())
					.then(context => context?.properties.first_argument).result!),
				stack: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.stack()).result!)
			}, 'first argument')
		}
	}
}
export namespace Cpanic {
}
export namespace Cset_network_conditions {
	export class Dpreset extends StateGroup<
		{ name: 'DSL', node:CDSL, init:TDSL}|
		{ name: 'GPRS', node:CGPRS, init:TGPRS}|
		{ name: 'WiFi', node:CWiFi, init:TWiFi}|
		{ name: 'good 2G', node:Cgood_2G, init:Tgood_2G}|
		{ name: 'good 3G', node:Cgood_3G, init:Tgood_3G}|
		{ name: 'no throttling', node:Cno_throttling, init:Tno_throttling}|
		{ name: 'offline', node:Coffline, init:Toffline}|
		{ name: 'regular 2G', node:Cregular_2G, init:Tregular_2G}|
		{ name: 'regular 3G', node:Cregular_3G, init:Tregular_3G}|
		{ name: 'regular 4G', node:Cregular_4G, init:Tregular_4G}> {
		protected initializer(state: 'DSL'|'GPRS'|'WiFi'|'good 2G'|'good 3G'|'no throttling'|'offline'|'regular 2G'|'regular 3G'|'regular 4G') {
			switch (state) {
				case 'DSL': return (init:TDSL, parent:Cset_network_conditions) => new CDSL(init, parent);
				case 'GPRS': return (init:TGPRS, parent:Cset_network_conditions) => new CGPRS(init, parent);
				case 'WiFi': return (init:TWiFi, parent:Cset_network_conditions) => new CWiFi(init, parent);
				case 'good 2G': return (init:Tgood_2G, parent:Cset_network_conditions) => new Cgood_2G(init, parent);
				case 'good 3G': return (init:Tgood_3G, parent:Cset_network_conditions) => new Cgood_3G(init, parent);
				case 'no throttling': return (init:Tno_throttling, parent:Cset_network_conditions) => new Cno_throttling(init, parent);
				case 'offline': return (init:Toffline, parent:Cset_network_conditions) => new Coffline(init, parent);
				case 'regular 2G': return (init:Tregular_2G, parent:Cset_network_conditions) => new Cregular_2G(init, parent);
				case 'regular 3G': return (init:Tregular_3G, parent:Cset_network_conditions) => new Cregular_3G(init, parent);
				case 'regular 4G': return (init:Tregular_4G, parent:Cset_network_conditions) => new Cregular_4G(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'DSL'|'GPRS'|'WiFi'|'good 2G'|'good 3G'|'no throttling'|'offline'|'regular 2G'|'regular 3G'|'regular 4G') {
			switch (state) {
				case 'DSL': return finalize_DSL;
				case 'GPRS': return finalize_GPRS;
				case 'WiFi': return finalize_WiFi;
				case 'good 2G': return finalize_good_2G;
				case 'good 3G': return finalize_good_3G;
				case 'no throttling': return finalize_no_throttling;
				case 'offline': return finalize_offline;
				case 'regular 2G': return finalize_regular_2G;
				case 'regular 3G': return finalize_regular_3G;
				case 'regular 4G': return finalize_regular_4G;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tset_network_conditions['preset'], parent:Cset_network_conditions) {
			super(data, parent, 'preset');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'preset'`; }
	}
}
export namespace Cset_user {
}
export namespace Cwait {
	export class Dtimeout extends AlanInteger {
		constructor(data:Twait['timeout'], parent:Cwait) {
			number__is_positive(data);
			super(data, parent);}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'timeout'`; }
	}
}
export namespace Cwalk {
	export class Dassignment extends Coptional_assignment {
		constructor(data:Twalk['assignment'], parent:Cwalk) {
			super(data, parent, {
				stack: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.stack()).result!),
				value: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.collection?.ref)
					.then(context => {
						switch (context?.variant.name) {
							case 'binding': {
								const test__statement__type__walk__assignment_in___value___binding_nval = context.cast('binding');
								return resolve(context)
									.then(context => test__statement__type__walk__assignment_in___value___binding_nval)
									.then(context => context?.properties.node)
									.then(context => {
										const conv_context = resolve(context).result!;
										return new Cnode__test({name: 'binding', definition: conv_context});
									})
									.then(context => context?.definitions.value)
									.then(context => context?.definitions.optional_value).result;
							}
							case 'gui': {
								const test__statement__type__walk__assignment_in___value___gui_nval = context.cast('gui');
								return resolve(context)
									.then(context => test__statement__type__walk__assignment_in___value___gui_nval)
									.then(context => context?.properties.node)
									.then(context => {
										const conv_context = resolve(context).result!;
										return new Cnode__test({name: 'gui', definition: conv_context});
									})
									.then(context => context?.definitions.value)
									.then(context => context?.definitions.optional_value).result;
							}
							case undefined: return undefined;
							default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
						};
					}).result!)
			}, 'assignment')
		}
	}
	export class Dcollection extends Reference<test.Ccollection__test,string> {

		constructor(data:string, $this:Cwalk) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.properties.value_expression?.inferences.node())
				.then(context => {
					switch (context?.variant.name) {
						case 'binding': {
							const test__statement__type__walk__collection___binding_nval = context.cast('binding');
							return resolve(context)
								.then(context => test__statement__type__walk__collection___binding_nval)
								.then(context => {
									const entry = context?.properties.properties.get(this.entry)!;
									return resolve(entry)
									.then(context => {
										if (context?.properties.type.state.name === 'collection') {
											return context.properties.type.state.node as client_bindings.Ccollection__type__properties;
										} else {
											return undefined;
										}
									})
									.then(context => {
										const conv_context = resolve(context).result!;
										return new Ccollection__test({name: 'binding', definition: conv_context});
									}).result;
								}).result;
						}
						case 'gui': {
							const test__statement__type__walk__collection___gui_nval = context.cast('gui');
							return resolve(context)
								.then(context => test__statement__type__walk__collection___gui_nval)
								.then(context => {
									const entry = context?.properties.attributes.get(this.entry)!;
									return resolve(entry)
									.then(context => {
										if (context?.properties.type.state.name === 'property') {
											return context.properties.type.state.node as gui_model.Cproperty__type__attributes;
										} else {
											return undefined;
										}
									})
									.then(context => {
										if (context?.properties.type.state.name === 'collection') {
											return context.properties.type.state.node as gui_model.Ccollection__type__property;
										} else {
											return undefined;
										}
									})
									.then(context => {
										const conv_context = resolve(context).result!;
										return new Ccollection__test({name: 'gui', definition: conv_context});
									}).result;
								}).result;
						}
						case undefined: return undefined;
						default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
					};
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'collection'`; }
	}
	export class Dstatements extends Cstatements {
		constructor(data:Twalk['statements'], parent:Cwalk) {
			super(data, parent, {
				stack: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.assignment)
					.then(context => context?.component_root.output.stack()).result!)
			}, 'statements')
		}
	}
	export class Dvalue_expression extends Cvalue_expression {
		public readonly inferences:{
			node: () => test.Cnode__test,
			value: () => test.Cvalue__test
		}
		constructor(data:Twalk['value expression'], parent:Cwalk) {
			super(data, parent, {
				stack: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.stack()).result!)
			}, 'value expression')
			this.inferences = {
				node: cache((detach:boolean) => {
					const test__statement__type__walk__value_expression_nval = this;
					return resolve(this)
						.then(() => parent.properties.value_expression.inferences.value())
						.then(context => context?.component_root.output.node())
						.then(context => context?.variant.name === 'node' ? context.variant.definition as test.Cnode__test : undefined).result!;
				})
				,
				value: cache((detach:boolean) => {
					const test__statement__type__walk__value_expression_nval = this;
					return resolve(this)
						.then(() => parent)
						.then(context => context?.properties.value_expression)
						.then(context => context?.component_root.output.value())
						.then(context => context?.variant.name === 'value' ? context.variant.definition as test.Cvalue__test : undefined).result!;
				})

			}
		}
	}
}
export namespace Cstatements {
	export class Dhas_next extends StateGroup<
		{ name: 'no', node:Cno__has_next__statements, init:Tno__has_next__statements}|
		{ name: 'yes', node:Cyes__has_next__statements, init:Tyes__has_next__statements}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_next__statements, parent:Cstatements) => new Cno__has_next__statements(init, parent);
				case 'yes': return (init:Tyes__has_next__statements, parent:Cstatements) => new Cyes__has_next__statements(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_next__statements;
				case 'yes': return finalize_yes__has_next__statements;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tstatements['has next'], parent:Cstatements) {
			super(data, parent, 'has next');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has next'`; }
	}
	export class Dstatement extends Cstatement {
		constructor(data:Tstatements['statement'], parent:Cstatements) {
			super(data, parent, {
				stack: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.stack()).result!)
			}, 'statement')
		}
	}
}
export namespace Cyes__has_next__statements {
	export class Dstatements extends Cstatements {
		constructor(data:Tyes__has_next__statements['statements'], parent:Cyes__has_next__statements) {
			super(data, parent, {
				stack: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.properties.statement)
					.then(context => context?.component_root.output.stack()).result!)
			}, 'statements')
		}
	}
}
export namespace Cvalue_expression {
	export class Dtype extends StateGroup<
		{ name: 'select', node:Cselect, init:Tselect}|
		{ name: 'static', node:Cstatic, init:Tstatic}> {
		protected initializer(state: 'select'|'static') {
			switch (state) {
				case 'select': return (init:Tselect, parent:Cvalue_expression) => new Cselect(init, parent);
				case 'static': return (init:Tstatic, parent:Cvalue_expression) => new Cstatic(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'select'|'static') {
			switch (state) {
				case 'select': return finalize_select;
				case 'static': return finalize_static;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tvalue_expression['type'], parent:Cvalue_expression) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Cselect {
	export class Dselection extends Cvalue_selection_path {
		constructor(data:Tselect['selection'], parent:Cselect) {
			super(data, parent, {
				context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.start_from?.state.node.output.value())
					.then(context => context?.definitions.value_context).result!),
				stack: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.stack()).result!)
			}, 'selection')
		}
	}
	export class Dstart_from extends StateGroup<
		{ name: 'named value', node:Cnamed_value, init:Tnamed_value}|
		{ name: 'root node', node:Croot_node, init:Troot_node}|
		{ name: 'run procedure', node:Crun_procedure, init:Trun_procedure}> {
		protected initializer(state: 'named value'|'root node'|'run procedure') {
			switch (state) {
				case 'named value': return (init:Tnamed_value, parent:Cselect) => new Cnamed_value(init, parent);
				case 'root node': return (init:Troot_node, parent:Cselect) => new Croot_node(init, parent);
				case 'run procedure': return (init:Trun_procedure, parent:Cselect) => new Crun_procedure(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'named value'|'root node'|'run procedure') {
			switch (state) {
				case 'named value': return finalize_named_value;
				case 'root node': return finalize_root_node;
				case 'run procedure': return finalize_run_procedure;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tselect['start from'], parent:Cselect) {
			super(data, parent, 'start from');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'start from'`; }
	}
}
export namespace Cnamed_value {
	export class Dselector extends Cstack_selector {
		constructor(data:Tnamed_value['selector'], parent:Cnamed_value) {
			super(data, parent, {
				stack: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.stack()).result!)
			}, 'selector')
		}
	}
}
export namespace Crun_procedure {
	export class Dhas_parameters extends StateGroup<
		{ name: 'no', node:Cno__has_parameters__run_procedure, init:Tno__has_parameters__run_procedure}|
		{ name: 'yes', node:Cyes__has_parameters__run_procedure, init:Tyes__has_parameters__run_procedure}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_parameters__run_procedure, parent:Crun_procedure) => new Cno__has_parameters__run_procedure(init, parent);
				case 'yes': return (init:Tyes__has_parameters__run_procedure, parent:Crun_procedure) => new Cyes__has_parameters__run_procedure(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_parameters__run_procedure;
				case 'yes': return finalize_yes__has_parameters__run_procedure;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Trun_procedure['has parameters'], parent:Crun_procedure) {
			super(data, parent, 'has parameters');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has parameters'`; }
	}
	export class Dprocedure extends Reference<test.Cprocedures,string> {

		constructor(data:string, $this:Crun_procedure) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.root)
				.then(context => {
					const entry = context?.properties.procedures.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'procedure'`; }
	}
}
export namespace Cyes__has_parameters__run_procedure {
	export class Dparameters extends Cparameter_implementation {
		constructor(data:Tyes__has_parameters__run_procedure['parameters'], parent:Cyes__has_parameters__run_procedure) {
			super(data, parent, {
				definition: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.inferences.yes())
					.then(context => context?.properties.parameters).result!),
				stack: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.stack()).result!)
			}, 'parameters')
		}
	}
}
export namespace Cstatic {
	export class Dtype extends StateGroup<
		{ name: 'file', node:Cfile__type__static, init:Tfile__type__static}|
		{ name: 'number', node:Cnumber__type__static, init:Tnumber__type__static}|
		{ name: 'open view', node:Copen_view__type, init:Topen_view__type}|
		{ name: 'text', node:Ctext__type__static, init:Ttext__type__static}|
		{ name: 'view definition', node:Cview_definition__type, init:Tview_definition__type}> {
		protected initializer(state: 'file'|'number'|'open view'|'text'|'view definition') {
			switch (state) {
				case 'file': return (init:Tfile__type__static, parent:Cstatic) => new Cfile__type__static(init, parent);
				case 'number': return (init:Tnumber__type__static, parent:Cstatic) => new Cnumber__type__static(init, parent);
				case 'open view': return (init:Topen_view__type, parent:Cstatic) => new Copen_view__type(init, parent);
				case 'text': return (init:Ttext__type__static, parent:Cstatic) => new Ctext__type__static(init, parent);
				case 'view definition': return (init:Tview_definition__type, parent:Cstatic) => new Cview_definition__type(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'file'|'number'|'open view'|'text'|'view definition') {
			switch (state) {
				case 'file': return finalize_file__type__static;
				case 'number': return finalize_number__type__static;
				case 'open view': return finalize_open_view__type;
				case 'text': return finalize_text__type__static;
				case 'view definition': return finalize_view_definition__type;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tstatic['type'], parent:Cstatic) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Cfile__type__static {
}
export namespace Cnumber__type__static {
	export class Dvalue extends AlanInteger {
		constructor(data:Tnumber__type__static['value'], parent:Cnumber__type__static) {
			super(data, parent);}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'value'`; }
	}
}
export namespace Copen_view__type {
	export class Don_node extends Cvalue_expression {
		public readonly inferences:{
			equal_view_context: () => gui_model.Cgui_node,
			gui_node: () => gui_model.Cgui_node,
			value_exists: () => test.Cvalue__test
		}
		constructor(data:Topen_view__type['on node'], parent:Copen_view__type) {
			super(data, parent, {
				stack: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.stack()).result!)
			}, 'on node')
			this.inferences = {
				equal_view_context: cache((detach:boolean) => {
					const test__value_expression__type__static__type__open_view__on_node_nval = this;
					return resolve(this)
						.then(() => parent)
						.then(context => context?.properties.view_selection?.inferences.view())
						.then(context => {
							switch (context?.variant.name) {
								case 'explicit': {
									const test__value_expression__type__static__type__open_view__on_node_inf___equal_view_context___explicit_nval = context.cast('explicit');
									return resolve(context)
										.then(context => {
											const left = resolve(context)
												.then(() => parent.properties.on_node.inferences.gui_node()).result;
											const right = resolve(context)
												.then(context => test__value_expression__type__static__type__open_view__on_node_inf___equal_view_context___explicit_nval)
												.then(context => context?.component_root.output.definition())
												.then(context => context?.properties.node_type)
												.then(context => context?.component_root.output.result_gui_node()).result;
											return left?.is(right!) ? left : undefined
										}).result;
								}
								case 'implicit': {
									return resolve(context)
										.then(() => parent.properties.on_node.inferences.gui_node()).result;
								}
								case undefined: return undefined;
								default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
							};
						}).result!;
				})
				,
				gui_node: cache((detach:boolean) => {
					const test__value_expression__type__static__type__open_view__on_node_nval = this;
					return resolve(this)
						.then(() => parent.properties.on_node.inferences.value_exists())
						.then(context => context?.component_root.output.node())
						.then(context => context?.variant.name === 'node' ? context.variant.definition as test.Cnode__test : undefined)
						.then(context => context?.variant.name === 'gui' ? context.variant.definition as gui_model.Cgui_node : undefined).result!;
				})
				,
				value_exists: cache((detach:boolean) => {
					const test__value_expression__type__static__type__open_view__on_node_nval = this;
					return resolve(this)
						.then(context => test__value_expression__type__static__type__open_view__on_node_nval)
						.then(context => context?.component_root.output.value())
						.then(context => context?.variant.name === 'value' ? context.variant.definition as test.Cvalue__test : undefined).result!;
				})

			}
		}
	}
	export class Dview_selection extends Cvalue_expression {
		public readonly inferences:{
			view: () => test.Cview__test
		}
		constructor(data:Topen_view__type['view selection'], parent:Copen_view__type) {
			super(data, parent, {
				stack: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.stack()).result!)
			}, 'view selection')
			this.inferences = {
				view: cache((detach:boolean) => {
					const test__value_expression__type__static__type__open_view__view_selection_nval = this;
					return resolve(this)
						.then(context => test__value_expression__type__static__type__open_view__view_selection_nval)
						.then(context => context?.component_root.output.value())
						.then(context => context?.variant.name === 'value' ? context.variant.definition as test.Cvalue__test : undefined)
						.then(context => context?.variant.name === 'view' ? context.variant.definition as test.Cview__test : undefined).result!;
				})

			}
		}
	}
}
export namespace Ctext__type__static {
}
export namespace Cview_definition__type {
	export class Dselect_views extends StateGroup<
		{ name: 'choose', node:Cchoose__select_views__view_definition, init:Tchoose__select_views__view_definition}|
		{ name: 'default', node:Cdefault__select_views__view_definition, init:Tdefault__select_views__view_definition}> {
		protected initializer(state: 'choose'|'default') {
			switch (state) {
				case 'choose': return (init:Tchoose__select_views__view_definition, parent:Cview_definition__type) => new Cchoose__select_views__view_definition(init, parent);
				case 'default': return (init:Tdefault__select_views__view_definition, parent:Cview_definition__type) => new Cdefault__select_views__view_definition(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'choose'|'default') {
			switch (state) {
				case 'choose': return finalize_choose__select_views__view_definition;
				case 'default': return finalize_default__select_views__view_definition;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tview_definition__type['select views'], parent:Cview_definition__type) {
			super(data, parent, 'select views');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'select views'`; }
	}
	export class Dselect_window extends StateGroup<
		{ name: 'choose', node:Cchoose__select_window, init:Tchoose__select_window}|
		{ name: 'default', node:Cdefault__select_window, init:Tdefault__select_window}> {
		protected initializer(state: 'choose'|'default') {
			switch (state) {
				case 'choose': return (init:Tchoose__select_window, parent:Cview_definition__type) => new Cchoose__select_window(init, parent);
				case 'default': return (init:Tdefault__select_window, parent:Cview_definition__type) => new Cdefault__select_window(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'choose'|'default') {
			switch (state) {
				case 'choose': return finalize_choose__select_window;
				case 'default': return finalize_default__select_window;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tview_definition__type['select window'], parent:Cview_definition__type) {
			super(data, parent, 'select window');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'select window'`; }
	}
	export class Dview extends Reference<views.Cviews__,string> {

		constructor(data:string, $this:Cview_definition__type) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.properties.select_views?.state.node.output.views())
				.then(context => {
					const entry = context?.properties.views.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'view'`; }
	}
}
export namespace Cchoose__select_views__view_definition {
	export class Dviews extends Reference<views.Cviews,string> {

		constructor(data:string, $this:Cchoose__select_views__view_definition) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this.root.input.views[this.entry]).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'views'`; }
	}
}
export namespace Cchoose__select_window {
	export class Dwindow extends Reference<settings.Cwindows,string> {

		constructor(data:string, $this:Cchoose__select_window) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.root.input.settings)
				.then(context => {
					const entry = context?.properties.windows.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'window'`; }
	}
}
export namespace Cvalue_selection_path {
	export class Dhas_steps extends StateGroup<
		{ name: 'no', node:Cno__has_steps__value_selection_path, init:Tno__has_steps__value_selection_path}|
		{ name: 'yes', node:Cyes__has_steps__value_selection_path, init:Tyes__has_steps__value_selection_path}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_steps__value_selection_path, parent:Cvalue_selection_path) => new Cno__has_steps__value_selection_path(init, parent);
				case 'yes': return (init:Tyes__has_steps__value_selection_path, parent:Cvalue_selection_path) => new Cyes__has_steps__value_selection_path(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_steps__value_selection_path;
				case 'yes': return finalize_yes__has_steps__value_selection_path;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tvalue_selection_path['has steps'], parent:Cvalue_selection_path) {
			super(data, parent, 'has steps');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has steps'`; }
	}
}
export namespace Cno__has_steps__value_selection_path {
	export class Dselect extends StateGroup<
		{ name: 'context', node:Ccontext, init:Tcontext}|
		{ name: 'key', node:Ckey, init:Tkey}> {
		protected initializer(state: 'context'|'key') {
			switch (state) {
				case 'context': return (init:Tcontext, parent:Cno__has_steps__value_selection_path) => new Ccontext(init, parent);
				case 'key': return (init:Tkey, parent:Cno__has_steps__value_selection_path) => new Ckey(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'context'|'key') {
			switch (state) {
				case 'context': return finalize_context;
				case 'key': return finalize_key;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tno__has_steps__value_selection_path['select'], parent:Cno__has_steps__value_selection_path) {
			super(data, parent, 'select');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'select'`; }
	}
}
export namespace Cyes__has_steps__value_selection_path {
	export class Dselect extends StateGroup<
		{ name: 'attribute', node:Cattribute__select__yes, init:Tattribute__select__yes}|
		{ name: 'binding', node:Cbinding__select, init:Tbinding__select}|
		{ name: 'collection', node:Ccollection__select, init:Tcollection__select}|
		{ name: 'query', node:Cquery__select, init:Tquery__select}> {
		protected initializer(state: 'attribute'|'binding'|'collection'|'query') {
			switch (state) {
				case 'attribute': return (init:Tattribute__select__yes, parent:Cyes__has_steps__value_selection_path) => new Cattribute__select__yes(init, parent);
				case 'binding': return (init:Tbinding__select, parent:Cyes__has_steps__value_selection_path) => new Cbinding__select(init, parent);
				case 'collection': return (init:Tcollection__select, parent:Cyes__has_steps__value_selection_path) => new Ccollection__select(init, parent);
				case 'query': return (init:Tquery__select, parent:Cyes__has_steps__value_selection_path) => new Cquery__select(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'attribute'|'binding'|'collection'|'query') {
			switch (state) {
				case 'attribute': return finalize_attribute__select__yes;
				case 'binding': return finalize_binding__select;
				case 'collection': return finalize_collection__select;
				case 'query': return finalize_query__select;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__has_steps__value_selection_path['select'], parent:Cyes__has_steps__value_selection_path) {
			super(data, parent, 'select');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'select'`; }
	}
	export class Dtail extends Cvalue_selection_path {
		constructor(data:Tyes__has_steps__value_selection_path['tail'], parent:Cyes__has_steps__value_selection_path) {
			super(data, parent, {
				context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.select?.state.node.output.context()).result!),
				stack: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.stack()).result!)
			}, 'tail')
		}
	}
}
export namespace Cattribute__select__yes {
	export class Dattribute extends Reference<test.Cattribute__test,string> {

		constructor(data:string, $this:Cattribute__select__yes) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.inferences.node())
				.then(context => {
					switch (context?.variant.name) {
						case 'binding': {
							const test__value_selection_path__has_steps__yes__select__attribute__attribute___binding_nval = context.cast('binding');
							return resolve(context)
								.then(context => test__value_selection_path__has_steps__yes__select__attribute__attribute___binding_nval)
								.then(context => {
									const entry = context?.properties.properties.get(this.entry)!;
									return resolve(entry)
									.then(context => {
										const conv_context = resolve(context).result!;
										return new Cattribute__test({name: 'binding', definition: conv_context});
									}).result;
								}).result;
						}
						case 'gui': {
							const test__value_selection_path__has_steps__yes__select__attribute__attribute___gui_nval = context.cast('gui');
							return resolve(context)
								.then(context => test__value_selection_path__has_steps__yes__select__attribute__attribute___gui_nval)
								.then(context => {
									const entry = context?.properties.attributes.get(this.entry)!;
									return resolve(entry)
									.then(context => {
										const conv_context = resolve(context).result!;
										return new Cattribute__test({name: 'gui', definition: conv_context});
									}).result;
								}).result;
						}
						case undefined: return undefined;
						default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
					};
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'attribute'`; }
	}
	export class Dselect extends StateGroup<
		{ name: 'attribute', node:Cattribute__select__attribute, init:Tattribute__select__attribute}|
		{ name: 'ordered graph', node:Cordered_graph, init:Tordered_graph}|
		{ name: 'state', node:Cstate__select__attribute, init:Tstate__select__attribute}|
		{ name: 'text rule', node:Ctext_rule, init:Ttext_rule}> {
		protected initializer(state: 'attribute'|'ordered graph'|'state'|'text rule') {
			switch (state) {
				case 'attribute': return (init:Tattribute__select__attribute, parent:Cattribute__select__yes) => new Cattribute__select__attribute(init, parent);
				case 'ordered graph': return (init:Tordered_graph, parent:Cattribute__select__yes) => new Cordered_graph(init, parent);
				case 'state': return (init:Tstate__select__attribute, parent:Cattribute__select__yes) => new Cstate__select__attribute(init, parent);
				case 'text rule': return (init:Ttext_rule, parent:Cattribute__select__yes) => new Ctext_rule(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'attribute'|'ordered graph'|'state'|'text rule') {
			switch (state) {
				case 'attribute': return finalize_attribute__select__attribute;
				case 'ordered graph': return finalize_ordered_graph;
				case 'state': return finalize_state__select__attribute;
				case 'text rule': return finalize_text_rule;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tattribute__select__yes['select'], parent:Cattribute__select__yes) {
			super(data, parent, 'select');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'select'`; }
	}
}
export namespace Cordered_graph {
	export class Dordered_graph extends Reference<gui_model.Cordered_graphs,string> {

		constructor(data:string, $this:Cordered_graph) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.inferences.ordered_collection())
				.then(context => {
					const entry = context?.properties.ordered_graphs.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'ordered graph'`; }
	}
}
export namespace Cstate__select__attribute {
	export class Dselect extends StateGroup<
		{ name: 'state', node:Cstate__select__state, init:Tstate__select__state}|
		{ name: 'state rule', node:Cstate_rule, init:Tstate_rule}> {
		protected initializer(state: 'state'|'state rule') {
			switch (state) {
				case 'state': return (init:Tstate__select__state, parent:Cstate__select__attribute) => new Cstate__select__state(init, parent);
				case 'state rule': return (init:Tstate_rule, parent:Cstate__select__attribute) => new Cstate_rule(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'state'|'state rule') {
			switch (state) {
				case 'state': return finalize_state__select__state;
				case 'state rule': return finalize_state_rule;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tstate__select__attribute['select'], parent:Cstate__select__attribute) {
			super(data, parent, 'select');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'select'`; }
	}
	export class Dstate extends Reference<test.Cstate__test,string> {

		constructor(data:string, $this:Cstate__select__attribute) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.inferences.state_group())
				.then(context => {
					switch (context?.variant.name) {
						case 'binding': {
							const test__value_selection_path__has_steps__yes__select__attribute__select__state__state___binding_nval = context.cast('binding');
							return resolve(context)
								.then(context => test__value_selection_path__has_steps__yes__select__attribute__select__state__state___binding_nval)
								.then(context => {
									const entry = context?.properties.states.get(this.entry)!;
									return resolve(entry)
									.then(context => {
										const conv_context = resolve(context).result!;
										return new Cstate__test({name: 'binding', definition: conv_context});
									}).result;
								}).result;
						}
						case 'gui': {
							const test__value_selection_path__has_steps__yes__select__attribute__select__state__state___gui_nval = context.cast('gui');
							return resolve(context)
								.then(context => test__value_selection_path__has_steps__yes__select__attribute__select__state__state___gui_nval)
								.then(context => {
									const entry = context?.properties.states.get(this.entry)!;
									return resolve(entry)
									.then(context => {
										const conv_context = resolve(context).result!;
										return new Cstate__test({name: 'gui', definition: conv_context});
									}).result;
								}).result;
						}
						case undefined: return undefined;
						default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
					};
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'state'`; }
	}
}
export namespace Cstate_rule {
	export class Drule extends Reference<gui_model.Crules,string> {

		constructor(data:string, $this:Cstate_rule) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.inferences.gui_state())
				.then(context => context?.properties.rules)
				.then(context => {
					const entry = context?.properties.rules.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'rule'`; }
	}
}
export namespace Ctext_rule {
	export class Drule extends Reference<gui_model.Crules,string> {

		constructor(data:string, $this:Ctext_rule) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.inferences.reference())
				.then(context => context?.properties.rules)
				.then(context => {
					const entry = context?.properties.rules.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'rule'`; }
	}
}
export namespace Cbinding__select {
	export class Dcontext extends StateGroup<
		{ name: 'binding node', node:Cbinding_node, init:Tbinding_node}|
		{ name: 'other', node:Cother, init:Tother}> {
		protected initializer(state: 'binding node'|'other') {
			switch (state) {
				case 'binding node': return (init:Tbinding_node, parent:Cbinding__select) => new Cbinding_node(init, parent);
				case 'other': return (init:Tother, parent:Cbinding__select) => new Cother(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'binding node'|'other') {
			switch (state) {
				case 'binding node': return finalize_binding_node;
				case 'other': return finalize_other;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tbinding__select['context'], parent:Cbinding__select) {
			super(data, parent, 'context');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'context'`; }
	}
}
export namespace Cbinding_node {
	export class Dbinding extends Reference<client_bindings.Cbindings__node,string> {
		public readonly inferences:{
			node: () => test.Cnode__test,
			switchable: () => client_bindings.Cyes__switchable_in_configuration_context_path
		}

		constructor(data:string, $this:Cbinding_node) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.inferences.binding_node())
				.then(context => {
					const entry = context?.properties.bindings.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
			this.inferences = {
				node: cache((detach:boolean) => {
					const test__value_selection_path__has_steps__yes__select__binding__context__binding_node__binding_nval = $this.properties.binding.ref;
					return resolve($this.properties.binding.ref)
						.then(context => test__value_selection_path__has_steps__yes__select__binding__context__binding_node__binding_nval)
						.then(context => context?.properties.binding?.ref)
						.then(context => context?.properties.binding)
						.then(context => {
							const conv_context = resolve(context).result!;
							return new Cnode__test({name: 'binding', definition: conv_context});
						}).result!;
				})
				,
				switchable: cache((detach:boolean) => {
					const test__value_selection_path__has_steps__yes__select__binding__context__binding_node__binding_nval = $this.properties.binding.ref;
					return resolve($this.properties.binding.ref)
						.then(context => test__value_selection_path__has_steps__yes__select__binding__context__binding_node__binding_nval)
						.then(context => {
							if (context?.properties.switchable_in_configuration_context_path.state.name === 'yes') {
								return resolve(context.properties.switchable_in_configuration_context_path.state.node as client_bindings.Cyes__switchable_in_configuration_context_path).result;
							} else {
								return undefined;
							}
						}).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'binding'`; }
	}
}
export namespace Cother {
	export class Dclient_binding extends Reference<client_bindings.Cbindings__client_bindings,string> {
		public readonly inferences:{
			binding_type_valid: () => test.Ctrue,
			binding_type_validity: () => test.Cboolean,
			gui_context_valid: () => test.Ctrue,
			gui_context_validity: () => test.Cboolean
		}

		constructor(data:string, $this:Cother) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.root.input.client_bindings)
				.then(context => {
					const entry = context?.properties.bindings.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
			this.inferences = {
				binding_type_valid: cache((detach:boolean) => {
					const test__value_selection_path__has_steps__yes__select__binding__context__other__client_binding_nval = $this.properties.client_binding.ref;
					return resolve($this.properties.client_binding.ref)
						.then(() => $this.properties.client_binding.inferences.binding_type_validity())
						.then(context => context?.variant.name === 'true' ? context.variant.definition as test.Ctrue : undefined).result!;
				})
				,
				binding_type_validity: cache((detach:boolean) => {
					const test__value_selection_path__has_steps__yes__select__binding__context__other__client_binding_nval = $this.properties.client_binding.ref;
					return resolve($this.properties.client_binding.ref)
						.then(context => test__value_selection_path__has_steps__yes__select__binding__context__other__client_binding_nval)
						.then(context => context?.properties.type?.state.node.output.binding_type())
						.then(context => {
							switch (context?.variant.name) {
								case 'engine state': {
									return resolve(context)
										.then(() => $this)
										.then(() => test.Cboolean.Ptrue_).result;
								}
								case 'node': {
									const test__value_selection_path__has_steps__yes__select__binding__context__other__client_binding_inf___binding_type_validity___node_nval = context.cast('node');
									return resolve(context)
										.then(context => test__value_selection_path__has_steps__yes__select__binding__context__other__client_binding_inf___binding_type_validity___node_nval)
										.then(context => {
											switch (context?.variant.name) {
												case 'collection': {
													return resolve(context)
														.then(() => $this)
														.then(context => context?.component_root.input.context())
														.then(context => {
															switch (context?.variant.name) {
																case 'attribute': {
																	const test__value_selection_path__has_steps__yes__select__binding__context__other__client_binding_inf___binding_type_validity___node___collection___attribute_nval = context.cast('attribute');
																	return resolve(context)
																		.then(context => test__value_selection_path__has_steps__yes__select__binding__context__other__client_binding_inf___binding_type_validity___node___collection___attribute_nval)
																		.then(context => {
																			switch (context?.variant.name) {
																				case 'binding': {
																					return resolve(context)
																						.then(() => $this)
																						.then(() => test.Cboolean.Pfalse_).result;
																				}
																				case 'gui': {
																					const test__value_selection_path__has_steps__yes__select__binding__context__other__client_binding_inf___binding_type_validity___node___collection___attribute___gui_nval = context.cast('gui');
																					return resolve(context)
																						.then(context => test__value_selection_path__has_steps__yes__select__binding__context__other__client_binding_inf___binding_type_validity___node___collection___attribute___gui_nval)
																						.then(context => context?.properties.type)
																						.then(context => {
																							const state_group = context;
																							switch (state_group?.state.name) {
																								case 'action': {
																									return resolve(context)
																										.then(() => $this)
																										.then(() => test.Cboolean.Pfalse_).result;
																								}
																								case 'command': {
																									return resolve(context)
																										.then(() => $this)
																										.then(() => test.Cboolean.Pfalse_).result;
																								}
																								case 'property': {
																									const test__value_selection_path__has_steps__yes__select__binding__context__other__client_binding_inf___binding_type_validity___node___collection___attribute___gui___property_nval = state_group.cast('property');
																									return resolve(context)
																										.then(context => test__value_selection_path__has_steps__yes__select__binding__context__other__client_binding_inf___binding_type_validity___node___collection___attribute___gui___property_nval)
																										.then(context => context?.properties.type)
																										.then(context => {
																											const state_group = context;
																											switch (state_group?.state.name) {
																												case 'collection': {
																													return resolve(context)
																														.then(() => $this)
																														.then(() => test.Cboolean.Ptrue_).result;
																												}
																												case 'file': {
																													return resolve(context)
																														.then(() => $this)
																														.then(() => test.Cboolean.Pfalse_).result;
																												}
																												case 'group': {
																													return resolve(context)
																														.then(() => $this)
																														.then(() => test.Cboolean.Pfalse_).result;
																												}
																												case 'number': {
																													return resolve(context)
																														.then(() => $this)
																														.then(() => test.Cboolean.Pfalse_).result;
																												}
																												case 'state group': {
																													return resolve(context)
																														.then(() => $this)
																														.then(() => test.Cboolean.Pfalse_).result;
																												}
																												case 'text': {
																													return resolve(context)
																														.then(() => $this)
																														.then(() => test.Cboolean.Pfalse_).result;
																												}
																												case undefined: return undefined;
																												default: throw new Error(`Unexpected state '${(state_group!.state as any).name}'`);
																											};}).result;
																								}
																								case 'reference set': {
																									return resolve(context)
																										.then(() => $this)
																										.then(() => test.Cboolean.Pfalse_).result;
																								}
																								case undefined: return undefined;
																								default: throw new Error(`Unexpected state '${(state_group!.state as any).name}'`);
																							};}).result;
																				}
																				case undefined: return undefined;
																				default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
																			};
																		}).result;
																}
																case 'entry': {
																	return resolve(context)
																		.then(() => $this)
																		.then(() => test.Cboolean.Pfalse_).result;
																}
																case 'ordered graph': {
																	return resolve(context)
																		.then(() => $this)
																		.then(() => test.Cboolean.Pfalse_).result;
																}
																case 'query entry': {
																	return resolve(context)
																		.then(() => $this)
																		.then(() => test.Cboolean.Pfalse_).result;
																}
																case 'rule': {
																	return resolve(context)
																		.then(() => $this)
																		.then(() => test.Cboolean.Pfalse_).result;
																}
																case 'state': {
																	return resolve(context)
																		.then(() => $this)
																		.then(() => test.Cboolean.Pfalse_).result;
																}
																case 'value': {
																	return resolve(context)
																		.then(() => $this)
																		.then(() => test.Cboolean.Pfalse_).result;
																}
																case undefined: return undefined;
																default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
															};
														}).result;
												}
												case 'collection entry': {
													return resolve(context)
														.then(() => $this)
														.then(context => context?.component_root.input.context())
														.then(context => {
															switch (context?.variant.name) {
																case 'attribute': {
																	return resolve(context)
																		.then(() => $this)
																		.then(() => test.Cboolean.Pfalse_).result;
																}
																case 'entry': {
																	const test__value_selection_path__has_steps__yes__select__binding__context__other__client_binding_inf___binding_type_validity___node___collection_entry___entry_nval = context.cast('entry');
																	return resolve(context)
																		.then(context => test__value_selection_path__has_steps__yes__select__binding__context__other__client_binding_inf___binding_type_validity___node___collection_entry___entry_nval)
																		.then(context => {
																			switch (context?.variant.name) {
																				case 'binding': {
																					return resolve(context)
																						.then(() => $this)
																						.then(() => test.Cboolean.Pfalse_).result;
																				}
																				case 'gui': {
																					return resolve(context)
																						.then(() => $this)
																						.then(() => test.Cboolean.Ptrue_).result;
																				}
																				case undefined: return undefined;
																				default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
																			};
																		}).result;
																}
																case 'ordered graph': {
																	return resolve(context)
																		.then(() => $this)
																		.then(() => test.Cboolean.Pfalse_).result;
																}
																case 'query entry': {
																	return resolve(context)
																		.then(() => $this)
																		.then(() => test.Cboolean.Pfalse_).result;
																}
																case 'rule': {
																	return resolve(context)
																		.then(() => $this)
																		.then(() => test.Cboolean.Pfalse_).result;
																}
																case 'state': {
																	return resolve(context)
																		.then(() => $this)
																		.then(() => test.Cboolean.Pfalse_).result;
																}
																case 'value': {
																	return resolve(context)
																		.then(() => $this)
																		.then(() => test.Cboolean.Pfalse_).result;
																}
																case undefined: return undefined;
																default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
															};
														}).result;
												}
												case 'entity': {
													return resolve(context)
														.then(() => $this)
														.then(() => test.Cboolean.Pfalse_).result;
												}
												case 'file': {
													return resolve(context)
														.then(() => $this)
														.then(context => context?.component_root.input.context())
														.then(context => {
															switch (context?.variant.name) {
																case 'attribute': {
																	const test__value_selection_path__has_steps__yes__select__binding__context__other__client_binding_inf___binding_type_validity___node___file___attribute_nval = context.cast('attribute');
																	return resolve(context)
																		.then(context => test__value_selection_path__has_steps__yes__select__binding__context__other__client_binding_inf___binding_type_validity___node___file___attribute_nval)
																		.then(context => {
																			switch (context?.variant.name) {
																				case 'binding': {
																					return resolve(context)
																						.then(() => $this)
																						.then(() => test.Cboolean.Pfalse_).result;
																				}
																				case 'gui': {
																					const test__value_selection_path__has_steps__yes__select__binding__context__other__client_binding_inf___binding_type_validity___node___file___attribute___gui_nval = context.cast('gui');
																					return resolve(context)
																						.then(context => test__value_selection_path__has_steps__yes__select__binding__context__other__client_binding_inf___binding_type_validity___node___file___attribute___gui_nval)
																						.then(context => context?.properties.type)
																						.then(context => {
																							const state_group = context;
																							switch (state_group?.state.name) {
																								case 'action': {
																									return resolve(context)
																										.then(() => $this)
																										.then(() => test.Cboolean.Pfalse_).result;
																								}
																								case 'command': {
																									return resolve(context)
																										.then(() => $this)
																										.then(() => test.Cboolean.Pfalse_).result;
																								}
																								case 'property': {
																									const test__value_selection_path__has_steps__yes__select__binding__context__other__client_binding_inf___binding_type_validity___node___file___attribute___gui___property_nval = state_group.cast('property');
																									return resolve(context)
																										.then(context => test__value_selection_path__has_steps__yes__select__binding__context__other__client_binding_inf___binding_type_validity___node___file___attribute___gui___property_nval)
																										.then(context => context?.properties.type)
																										.then(context => {
																											const state_group = context;
																											switch (state_group?.state.name) {
																												case 'collection': {
																													return resolve(context)
																														.then(() => $this)
																														.then(() => test.Cboolean.Pfalse_).result;
																												}
																												case 'file': {
																													return resolve(context)
																														.then(() => $this)
																														.then(() => test.Cboolean.Ptrue_).result;
																												}
																												case 'group': {
																													return resolve(context)
																														.then(() => $this)
																														.then(() => test.Cboolean.Pfalse_).result;
																												}
																												case 'number': {
																													return resolve(context)
																														.then(() => $this)
																														.then(() => test.Cboolean.Pfalse_).result;
																												}
																												case 'state group': {
																													return resolve(context)
																														.then(() => $this)
																														.then(() => test.Cboolean.Pfalse_).result;
																												}
																												case 'text': {
																													return resolve(context)
																														.then(() => $this)
																														.then(() => test.Cboolean.Pfalse_).result;
																												}
																												case undefined: return undefined;
																												default: throw new Error(`Unexpected state '${(state_group!.state as any).name}'`);
																											};}).result;
																								}
																								case 'reference set': {
																									return resolve(context)
																										.then(() => $this)
																										.then(() => test.Cboolean.Pfalse_).result;
																								}
																								case undefined: return undefined;
																								default: throw new Error(`Unexpected state '${(state_group!.state as any).name}'`);
																							};}).result;
																				}
																				case undefined: return undefined;
																				default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
																			};
																		}).result;
																}
																case 'entry': {
																	return resolve(context)
																		.then(() => $this)
																		.then(() => test.Cboolean.Pfalse_).result;
																}
																case 'ordered graph': {
																	return resolve(context)
																		.then(() => $this)
																		.then(() => test.Cboolean.Pfalse_).result;
																}
																case 'query entry': {
																	return resolve(context)
																		.then(() => $this)
																		.then(() => test.Cboolean.Pfalse_).result;
																}
																case 'rule': {
																	return resolve(context)
																		.then(() => $this)
																		.then(() => test.Cboolean.Pfalse_).result;
																}
																case 'state': {
																	return resolve(context)
																		.then(() => $this)
																		.then(() => test.Cboolean.Pfalse_).result;
																}
																case 'value': {
																	return resolve(context)
																		.then(() => $this)
																		.then(() => test.Cboolean.Pfalse_).result;
																}
																case undefined: return undefined;
																default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
															};
														}).result;
												}
												case 'group': {
													return resolve(context)
														.then(() => $this)
														.then(context => context?.component_root.input.context())
														.then(context => {
															switch (context?.variant.name) {
																case 'attribute': {
																	const test__value_selection_path__has_steps__yes__select__binding__context__other__client_binding_inf___binding_type_validity___node___group___attribute_nval = context.cast('attribute');
																	return resolve(context)
																		.then(context => test__value_selection_path__has_steps__yes__select__binding__context__other__client_binding_inf___binding_type_validity___node___group___attribute_nval)
																		.then(context => {
																			switch (context?.variant.name) {
																				case 'binding': {
																					return resolve(context)
																						.then(() => $this)
																						.then(() => test.Cboolean.Pfalse_).result;
																				}
																				case 'gui': {
																					const test__value_selection_path__has_steps__yes__select__binding__context__other__client_binding_inf___binding_type_validity___node___group___attribute___gui_nval = context.cast('gui');
																					return resolve(context)
																						.then(context => test__value_selection_path__has_steps__yes__select__binding__context__other__client_binding_inf___binding_type_validity___node___group___attribute___gui_nval)
																						.then(context => context?.properties.type)
																						.then(context => {
																							const state_group = context;
																							switch (state_group?.state.name) {
																								case 'action': {
																									return resolve(context)
																										.then(() => $this)
																										.then(() => test.Cboolean.Pfalse_).result;
																								}
																								case 'command': {
																									return resolve(context)
																										.then(() => $this)
																										.then(() => test.Cboolean.Pfalse_).result;
																								}
																								case 'property': {
																									const test__value_selection_path__has_steps__yes__select__binding__context__other__client_binding_inf___binding_type_validity___node___group___attribute___gui___property_nval = state_group.cast('property');
																									return resolve(context)
																										.then(context => test__value_selection_path__has_steps__yes__select__binding__context__other__client_binding_inf___binding_type_validity___node___group___attribute___gui___property_nval)
																										.then(context => context?.properties.type)
																										.then(context => {
																											const state_group = context;
																											switch (state_group?.state.name) {
																												case 'collection': {
																													return resolve(context)
																														.then(() => $this)
																														.then(() => test.Cboolean.Pfalse_).result;
																												}
																												case 'file': {
																													return resolve(context)
																														.then(() => $this)
																														.then(() => test.Cboolean.Pfalse_).result;
																												}
																												case 'group': {
																													return resolve(context)
																														.then(() => $this)
																														.then(() => test.Cboolean.Ptrue_).result;
																												}
																												case 'number': {
																													return resolve(context)
																														.then(() => $this)
																														.then(() => test.Cboolean.Pfalse_).result;
																												}
																												case 'state group': {
																													return resolve(context)
																														.then(() => $this)
																														.then(() => test.Cboolean.Pfalse_).result;
																												}
																												case 'text': {
																													return resolve(context)
																														.then(() => $this)
																														.then(() => test.Cboolean.Pfalse_).result;
																												}
																												case undefined: return undefined;
																												default: throw new Error(`Unexpected state '${(state_group!.state as any).name}'`);
																											};}).result;
																								}
																								case 'reference set': {
																									return resolve(context)
																										.then(() => $this)
																										.then(() => test.Cboolean.Pfalse_).result;
																								}
																								case undefined: return undefined;
																								default: throw new Error(`Unexpected state '${(state_group!.state as any).name}'`);
																							};}).result;
																				}
																				case undefined: return undefined;
																				default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
																			};
																		}).result;
																}
																case 'entry': {
																	return resolve(context)
																		.then(() => $this)
																		.then(() => test.Cboolean.Pfalse_).result;
																}
																case 'ordered graph': {
																	return resolve(context)
																		.then(() => $this)
																		.then(() => test.Cboolean.Pfalse_).result;
																}
																case 'query entry': {
																	return resolve(context)
																		.then(() => $this)
																		.then(() => test.Cboolean.Pfalse_).result;
																}
																case 'rule': {
																	return resolve(context)
																		.then(() => $this)
																		.then(() => test.Cboolean.Pfalse_).result;
																}
																case 'state': {
																	return resolve(context)
																		.then(() => $this)
																		.then(() => test.Cboolean.Pfalse_).result;
																}
																case 'value': {
																	return resolve(context)
																		.then(() => $this)
																		.then(() => test.Cboolean.Pfalse_).result;
																}
																case undefined: return undefined;
																default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
															};
														}).result;
												}
												case 'join': {
													return resolve(context)
														.then(() => $this)
														.then(() => test.Cboolean.Pfalse_).result;
												}
												case 'joined entry': {
													return resolve(context)
														.then(() => $this)
														.then(() => test.Cboolean.Pfalse_).result;
												}
												case 'node': {
													return resolve(context)
														.then(() => $this)
														.then(context => context?.component_root.input.context())
														.then(context => {
															switch (context?.variant.name) {
																case 'attribute': {
																	const test__value_selection_path__has_steps__yes__select__binding__context__other__client_binding_inf___binding_type_validity___node___node___attribute_nval = context.cast('attribute');
																	return resolve(context)
																		.then(context => test__value_selection_path__has_steps__yes__select__binding__context__other__client_binding_inf___binding_type_validity___node___node___attribute_nval)
																		.then(context => {
																			switch (context?.variant.name) {
																				case 'binding': {
																					return resolve(context)
																						.then(() => $this)
																						.then(() => test.Cboolean.Pfalse_).result;
																				}
																				case 'gui': {
																					const test__value_selection_path__has_steps__yes__select__binding__context__other__client_binding_inf___binding_type_validity___node___node___attribute___gui_nval = context.cast('gui');
																					return resolve(context)
																						.then(context => test__value_selection_path__has_steps__yes__select__binding__context__other__client_binding_inf___binding_type_validity___node___node___attribute___gui_nval)
																						.then(context => context?.properties.type)
																						.then(context => {
																							const state_group = context;
																							switch (state_group?.state.name) {
																								case 'action': {
																									return resolve(context)
																										.then(() => $this)
																										.then(() => test.Cboolean.Ptrue_).result;
																								}
																								case 'command': {
																									const test__value_selection_path__has_steps__yes__select__binding__context__other__client_binding_inf___binding_type_validity___node___node___attribute___gui___command_nval = state_group.cast('command');
																									return resolve(context)
																										.then(context => test__value_selection_path__has_steps__yes__select__binding__context__other__client_binding_inf___binding_type_validity___node___node___attribute___gui___command_nval)
																										.then(context => context?.properties.type)
																										.then(context => {
																											const state_group = context;
																											switch (state_group?.state.name) {
																												case 'component': {
																													return resolve(context)
																														.then(() => $this)
																														.then(() => test.Cboolean.Pfalse_).result;
																												}
																												case 'global': {
																													return resolve(context)
																														.then(() => $this)
																														.then(() => test.Cboolean.Ptrue_).result;
																												}
																												case undefined: return undefined;
																												default: throw new Error(`Unexpected state '${(state_group!.state as any).name}'`);
																											};}).result;
																								}
																								case 'property': {
																									const test__value_selection_path__has_steps__yes__select__binding__context__other__client_binding_inf___binding_type_validity___node___node___attribute___gui___property_nval = state_group.cast('property');
																									return resolve(context)
																										.then(context => test__value_selection_path__has_steps__yes__select__binding__context__other__client_binding_inf___binding_type_validity___node___node___attribute___gui___property_nval)
																										.then(context => context?.properties.type)
																										.then(context => {
																											const state_group = context;
																											switch (state_group?.state.name) {
																												case 'collection': {
																													return resolve(context)
																														.then(() => $this)
																														.then(() => test.Cboolean.Pfalse_).result;
																												}
																												case 'file': {
																													return resolve(context)
																														.then(() => $this)
																														.then(() => test.Cboolean.Pfalse_).result;
																												}
																												case 'group': {
																													return resolve(context)
																														.then(() => $this)
																														.then(() => test.Cboolean.Ptrue_).result;
																												}
																												case 'number': {
																													return resolve(context)
																														.then(() => $this)
																														.then(() => test.Cboolean.Pfalse_).result;
																												}
																												case 'state group': {
																													return resolve(context)
																														.then(() => $this)
																														.then(() => test.Cboolean.Pfalse_).result;
																												}
																												case 'text': {
																													return resolve(context)
																														.then(() => $this)
																														.then(() => test.Cboolean.Pfalse_).result;
																												}
																												case undefined: return undefined;
																												default: throw new Error(`Unexpected state '${(state_group!.state as any).name}'`);
																											};}).result;
																								}
																								case 'reference set': {
																									return resolve(context)
																										.then(() => $this)
																										.then(() => test.Cboolean.Pfalse_).result;
																								}
																								case undefined: return undefined;
																								default: throw new Error(`Unexpected state '${(state_group!.state as any).name}'`);
																							};}).result;
																				}
																				case undefined: return undefined;
																				default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
																			};
																		}).result;
																}
																case 'entry': {
																	const test__value_selection_path__has_steps__yes__select__binding__context__other__client_binding_inf___binding_type_validity___node___node___entry_nval = context.cast('entry');
																	return resolve(context)
																		.then(context => test__value_selection_path__has_steps__yes__select__binding__context__other__client_binding_inf___binding_type_validity___node___node___entry_nval)
																		.then(context => {
																			switch (context?.variant.name) {
																				case 'binding': {
																					return resolve(context)
																						.then(() => $this)
																						.then(() => test.Cboolean.Pfalse_).result;
																				}
																				case 'gui': {
																					return resolve(context)
																						.then(() => $this)
																						.then(() => test.Cboolean.Ptrue_).result;
																				}
																				case undefined: return undefined;
																				default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
																			};
																		}).result;
																}
																case 'ordered graph': {
																	return resolve(context)
																		.then(() => $this)
																		.then(() => test.Cboolean.Pfalse_).result;
																}
																case 'query entry': {
																	return resolve(context)
																		.then(() => $this)
																		.then(() => test.Cboolean.Pfalse_).result;
																}
																case 'rule': {
																	return resolve(context)
																		.then(() => $this)
																		.then(() => test.Cboolean.Pfalse_).result;
																}
																case 'state': {
																	const test__value_selection_path__has_steps__yes__select__binding__context__other__client_binding_inf___binding_type_validity___node___node___state_nval = context.cast('state');
																	return resolve(context)
																		.then(context => test__value_selection_path__has_steps__yes__select__binding__context__other__client_binding_inf___binding_type_validity___node___node___state_nval)
																		.then(context => {
																			switch (context?.variant.name) {
																				case 'binding': {
																					return resolve(context)
																						.then(() => $this)
																						.then(() => test.Cboolean.Pfalse_).result;
																				}
																				case 'gui': {
																					return resolve(context)
																						.then(() => $this)
																						.then(() => test.Cboolean.Ptrue_).result;
																				}
																				case undefined: return undefined;
																				default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
																			};
																		}).result;
																}
																case 'value': {
																	const test__value_selection_path__has_steps__yes__select__binding__context__other__client_binding_inf___binding_type_validity___node___node___value_nval = context.cast('value');
																	return resolve(context)
																		.then(context => test__value_selection_path__has_steps__yes__select__binding__context__other__client_binding_inf___binding_type_validity___node___node___value_nval)
																		.then(context => {
																			switch (context?.variant.name) {
																				case 'none': {
																					return resolve(context)
																						.then(() => $this)
																						.then(() => test.Cboolean.Pfalse_).result;
																				}
																				case 'value': {
																					const test__value_selection_path__has_steps__yes__select__binding__context__other__client_binding_inf___binding_type_validity___node___node___value___value_nval = context.cast('value');
																					return resolve(context)
																						.then(context => test__value_selection_path__has_steps__yes__select__binding__context__other__client_binding_inf___binding_type_validity___node___node___value___value_nval)
																						.then(context => {
																							switch (context?.variant.name) {
																								case 'file': {
																									return resolve(context)
																										.then(() => $this)
																										.then(() => test.Cboolean.Pfalse_).result;
																								}
																								case 'node': {
																									return resolve(context)
																										.then(() => $this)
																										.then(() => test.Cboolean.Ptrue_).result;
																								}
																								case 'number': {
																									return resolve(context)
																										.then(() => $this)
																										.then(() => test.Cboolean.Pfalse_).result;
																								}
																								case 'query': {
																									return resolve(context)
																										.then(() => $this)
																										.then(() => test.Cboolean.Pfalse_).result;
																								}
																								case 'text': {
																									return resolve(context)
																										.then(() => $this)
																										.then(() => test.Cboolean.Pfalse_).result;
																								}
																								case 'view': {
																									const test__value_selection_path__has_steps__yes__select__binding__context__other__client_binding_inf___binding_type_validity___node___node___value___value___view_nval = context.cast('view');
																									return resolve(context)
																										.then(context => test__value_selection_path__has_steps__yes__select__binding__context__other__client_binding_inf___binding_type_validity___node___node___value___value___view_nval)
																										.then(context => context?.component_root.output.open()).result;
																								}
																								case undefined: return undefined;
																								default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
																							};
																						}).result;
																				}
																				case undefined: return undefined;
																				default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
																			};
																		}).result;
																}
																case undefined: return undefined;
																default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
															};
														}).result;
												}
												case 'number': {
													return resolve(context)
														.then(() => $this)
														.then(context => context?.component_root.input.context())
														.then(context => {
															switch (context?.variant.name) {
																case 'attribute': {
																	const test__value_selection_path__has_steps__yes__select__binding__context__other__client_binding_inf___binding_type_validity___node___number___attribute_nval = context.cast('attribute');
																	return resolve(context)
																		.then(context => test__value_selection_path__has_steps__yes__select__binding__context__other__client_binding_inf___binding_type_validity___node___number___attribute_nval)
																		.then(context => {
																			switch (context?.variant.name) {
																				case 'binding': {
																					return resolve(context)
																						.then(() => $this)
																						.then(() => test.Cboolean.Pfalse_).result;
																				}
																				case 'gui': {
																					const test__value_selection_path__has_steps__yes__select__binding__context__other__client_binding_inf___binding_type_validity___node___number___attribute___gui_nval = context.cast('gui');
																					return resolve(context)
																						.then(context => test__value_selection_path__has_steps__yes__select__binding__context__other__client_binding_inf___binding_type_validity___node___number___attribute___gui_nval)
																						.then(context => context?.properties.type)
																						.then(context => {
																							const state_group = context;
																							switch (state_group?.state.name) {
																								case 'action': {
																									return resolve(context)
																										.then(() => $this)
																										.then(() => test.Cboolean.Pfalse_).result;
																								}
																								case 'command': {
																									return resolve(context)
																										.then(() => $this)
																										.then(() => test.Cboolean.Pfalse_).result;
																								}
																								case 'property': {
																									const test__value_selection_path__has_steps__yes__select__binding__context__other__client_binding_inf___binding_type_validity___node___number___attribute___gui___property_nval = state_group.cast('property');
																									return resolve(context)
																										.then(context => test__value_selection_path__has_steps__yes__select__binding__context__other__client_binding_inf___binding_type_validity___node___number___attribute___gui___property_nval)
																										.then(context => context?.properties.type)
																										.then(context => {
																											const state_group = context;
																											switch (state_group?.state.name) {
																												case 'collection': {
																													return resolve(context)
																														.then(() => $this)
																														.then(() => test.Cboolean.Pfalse_).result;
																												}
																												case 'file': {
																													return resolve(context)
																														.then(() => $this)
																														.then(() => test.Cboolean.Pfalse_).result;
																												}
																												case 'group': {
																													return resolve(context)
																														.then(() => $this)
																														.then(() => test.Cboolean.Pfalse_).result;
																												}
																												case 'number': {
																													return resolve(context)
																														.then(() => $this)
																														.then(() => test.Cboolean.Ptrue_).result;
																												}
																												case 'state group': {
																													return resolve(context)
																														.then(() => $this)
																														.then(() => test.Cboolean.Pfalse_).result;
																												}
																												case 'text': {
																													return resolve(context)
																														.then(() => $this)
																														.then(() => test.Cboolean.Pfalse_).result;
																												}
																												case undefined: return undefined;
																												default: throw new Error(`Unexpected state '${(state_group!.state as any).name}'`);
																											};}).result;
																								}
																								case 'reference set': {
																									return resolve(context)
																										.then(() => $this)
																										.then(() => test.Cboolean.Pfalse_).result;
																								}
																								case undefined: return undefined;
																								default: throw new Error(`Unexpected state '${(state_group!.state as any).name}'`);
																							};}).result;
																				}
																				case undefined: return undefined;
																				default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
																			};
																		}).result;
																}
																case 'entry': {
																	return resolve(context)
																		.then(() => $this)
																		.then(() => test.Cboolean.Pfalse_).result;
																}
																case 'ordered graph': {
																	return resolve(context)
																		.then(() => $this)
																		.then(() => test.Cboolean.Pfalse_).result;
																}
																case 'query entry': {
																	return resolve(context)
																		.then(() => $this)
																		.then(() => test.Cboolean.Pfalse_).result;
																}
																case 'rule': {
																	return resolve(context)
																		.then(() => $this)
																		.then(() => test.Cboolean.Pfalse_).result;
																}
																case 'state': {
																	return resolve(context)
																		.then(() => $this)
																		.then(() => test.Cboolean.Pfalse_).result;
																}
																case 'value': {
																	return resolve(context)
																		.then(() => $this)
																		.then(() => test.Cboolean.Pfalse_).result;
																}
																case undefined: return undefined;
																default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
															};
														}).result;
												}
												case 'operation': {
													return resolve(context)
														.then(() => $this)
														.then(context => context?.component_root.input.context())
														.then(context => {
															switch (context?.variant.name) {
																case 'attribute': {
																	const test__value_selection_path__has_steps__yes__select__binding__context__other__client_binding_inf___binding_type_validity___node___operation___attribute_nval = context.cast('attribute');
																	return resolve(context)
																		.then(context => test__value_selection_path__has_steps__yes__select__binding__context__other__client_binding_inf___binding_type_validity___node___operation___attribute_nval)
																		.then(context => {
																			switch (context?.variant.name) {
																				case 'binding': {
																					return resolve(context)
																						.then(() => $this)
																						.then(() => test.Cboolean.Pfalse_).result;
																				}
																				case 'gui': {
																					const test__value_selection_path__has_steps__yes__select__binding__context__other__client_binding_inf___binding_type_validity___node___operation___attribute___gui_nval = context.cast('gui');
																					return resolve(context)
																						.then(context => test__value_selection_path__has_steps__yes__select__binding__context__other__client_binding_inf___binding_type_validity___node___operation___attribute___gui_nval)
																						.then(context => context?.properties.type)
																						.then(context => {
																							const state_group = context;
																							switch (state_group?.state.name) {
																								case 'action': {
																									return resolve(context)
																										.then(() => $this)
																										.then(() => test.Cboolean.Ptrue_).result;
																								}
																								case 'command': {
																									return resolve(context)
																										.then(() => $this)
																										.then(() => test.Cboolean.Ptrue_).result;
																								}
																								case 'property': {
																									return resolve(context)
																										.then(() => $this)
																										.then(() => test.Cboolean.Pfalse_).result;
																								}
																								case 'reference set': {
																									return resolve(context)
																										.then(() => $this)
																										.then(() => test.Cboolean.Pfalse_).result;
																								}
																								case undefined: return undefined;
																								default: throw new Error(`Unexpected state '${(state_group!.state as any).name}'`);
																							};}).result;
																				}
																				case undefined: return undefined;
																				default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
																			};
																		}).result;
																}
																case 'entry': {
																	return resolve(context)
																		.then(() => $this)
																		.then(() => test.Cboolean.Pfalse_).result;
																}
																case 'ordered graph': {
																	return resolve(context)
																		.then(() => $this)
																		.then(() => test.Cboolean.Pfalse_).result;
																}
																case 'query entry': {
																	return resolve(context)
																		.then(() => $this)
																		.then(() => test.Cboolean.Pfalse_).result;
																}
																case 'rule': {
																	return resolve(context)
																		.then(() => $this)
																		.then(() => test.Cboolean.Pfalse_).result;
																}
																case 'state': {
																	return resolve(context)
																		.then(() => $this)
																		.then(() => test.Cboolean.Pfalse_).result;
																}
																case 'value': {
																	return resolve(context)
																		.then(() => $this)
																		.then(() => test.Cboolean.Pfalse_).result;
																}
																case undefined: return undefined;
																default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
															};
														}).result;
												}
												case 'ordered graph': {
													return resolve(context)
														.then(() => $this)
														.then(context => context?.component_root.input.context())
														.then(context => {
															switch (context?.variant.name) {
																case 'attribute': {
																	return resolve(context)
																		.then(() => $this)
																		.then(() => test.Cboolean.Pfalse_).result;
																}
																case 'entry': {
																	return resolve(context)
																		.then(() => $this)
																		.then(() => test.Cboolean.Pfalse_).result;
																}
																case 'ordered graph': {
																	return resolve(context)
																		.then(() => $this)
																		.then(() => test.Cboolean.Ptrue_).result;
																}
																case 'query entry': {
																	return resolve(context)
																		.then(() => $this)
																		.then(() => test.Cboolean.Pfalse_).result;
																}
																case 'rule': {
																	return resolve(context)
																		.then(() => $this)
																		.then(() => test.Cboolean.Pfalse_).result;
																}
																case 'state': {
																	return resolve(context)
																		.then(() => $this)
																		.then(() => test.Cboolean.Pfalse_).result;
																}
																case 'value': {
																	return resolve(context)
																		.then(() => $this)
																		.then(() => test.Cboolean.Pfalse_).result;
																}
																case undefined: return undefined;
																default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
															};
														}).result;
												}
												case 'query': {
													return resolve(context)
														.then(() => $this)
														.then(context => context?.component_root.input.context())
														.then(context => {
															switch (context?.variant.name) {
																case 'attribute': {
																	return resolve(context)
																		.then(() => $this)
																		.then(() => test.Cboolean.Pfalse_).result;
																}
																case 'entry': {
																	return resolve(context)
																		.then(() => $this)
																		.then(() => test.Cboolean.Pfalse_).result;
																}
																case 'ordered graph': {
																	return resolve(context)
																		.then(() => $this)
																		.then(() => test.Cboolean.Pfalse_).result;
																}
																case 'query entry': {
																	return resolve(context)
																		.then(() => $this)
																		.then(() => test.Cboolean.Pfalse_).result;
																}
																case 'rule': {
																	return resolve(context)
																		.then(() => $this)
																		.then(() => test.Cboolean.Pfalse_).result;
																}
																case 'state': {
																	return resolve(context)
																		.then(() => $this)
																		.then(() => test.Cboolean.Pfalse_).result;
																}
																case 'value': {
																	const test__value_selection_path__has_steps__yes__select__binding__context__other__client_binding_inf___binding_type_validity___node___query___value_nval = context.cast('value');
																	return resolve(context)
																		.then(context => test__value_selection_path__has_steps__yes__select__binding__context__other__client_binding_inf___binding_type_validity___node___query___value_nval)
																		.then(context => {
																			switch (context?.variant.name) {
																				case 'none': {
																					return resolve(context)
																						.then(() => $this)
																						.then(() => test.Cboolean.Pfalse_).result;
																				}
																				case 'value': {
																					const test__value_selection_path__has_steps__yes__select__binding__context__other__client_binding_inf___binding_type_validity___node___query___value___value_nval = context.cast('value');
																					return resolve(context)
																						.then(context => test__value_selection_path__has_steps__yes__select__binding__context__other__client_binding_inf___binding_type_validity___node___query___value___value_nval)
																						.then(context => {
																							switch (context?.variant.name) {
																								case 'file': {
																									return resolve(context)
																										.then(() => $this)
																										.then(() => test.Cboolean.Pfalse_).result;
																								}
																								case 'node': {
																									return resolve(context)
																										.then(() => $this)
																										.then(() => test.Cboolean.Pfalse_).result;
																								}
																								case 'number': {
																									return resolve(context)
																										.then(() => $this)
																										.then(() => test.Cboolean.Pfalse_).result;
																								}
																								case 'query': {
																									return resolve(context)
																										.then(() => $this)
																										.then(() => test.Cboolean.Ptrue_).result;
																								}
																								case 'text': {
																									return resolve(context)
																										.then(() => $this)
																										.then(() => test.Cboolean.Pfalse_).result;
																								}
																								case 'view': {
																									return resolve(context)
																										.then(() => $this)
																										.then(() => test.Cboolean.Pfalse_).result;
																								}
																								case undefined: return undefined;
																								default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
																							};
																						}).result;
																				}
																				case undefined: return undefined;
																				default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
																			};
																		}).result;
																}
																case undefined: return undefined;
																default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
															};
														}).result;
												}
												case 'sort property': {
													return resolve(context)
														.then(() => $this)
														.then(() => test.Cboolean.Pfalse_).result;
												}
												case 'state': {
													return resolve(context)
														.then(() => $this)
														.then(context => context?.component_root.input.context())
														.then(context => {
															switch (context?.variant.name) {
																case 'attribute': {
																	return resolve(context)
																		.then(() => $this)
																		.then(() => test.Cboolean.Pfalse_).result;
																}
																case 'entry': {
																	return resolve(context)
																		.then(() => $this)
																		.then(() => test.Cboolean.Pfalse_).result;
																}
																case 'ordered graph': {
																	return resolve(context)
																		.then(() => $this)
																		.then(() => test.Cboolean.Pfalse_).result;
																}
																case 'query entry': {
																	return resolve(context)
																		.then(() => $this)
																		.then(() => test.Cboolean.Pfalse_).result;
																}
																case 'rule': {
																	return resolve(context)
																		.then(() => $this)
																		.then(() => test.Cboolean.Pfalse_).result;
																}
																case 'state': {
																	const test__value_selection_path__has_steps__yes__select__binding__context__other__client_binding_inf___binding_type_validity___node___state___state_nval = context.cast('state');
																	return resolve(context)
																		.then(context => test__value_selection_path__has_steps__yes__select__binding__context__other__client_binding_inf___binding_type_validity___node___state___state_nval)
																		.then(context => {
																			switch (context?.variant.name) {
																				case 'binding': {
																					return resolve(context)
																						.then(() => $this)
																						.then(() => test.Cboolean.Pfalse_).result;
																				}
																				case 'gui': {
																					return resolve(context)
																						.then(() => $this)
																						.then(() => test.Cboolean.Ptrue_).result;
																				}
																				case undefined: return undefined;
																				default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
																			};
																		}).result;
																}
																case 'value': {
																	return resolve(context)
																		.then(() => $this)
																		.then(() => test.Cboolean.Pfalse_).result;
																}
																case undefined: return undefined;
																default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
															};
														}).result;
												}
												case 'state group': {
													return resolve(context)
														.then(() => $this)
														.then(context => context?.component_root.input.context())
														.then(context => {
															switch (context?.variant.name) {
																case 'attribute': {
																	const test__value_selection_path__has_steps__yes__select__binding__context__other__client_binding_inf___binding_type_validity___node___state_group___attribute_nval = context.cast('attribute');
																	return resolve(context)
																		.then(context => test__value_selection_path__has_steps__yes__select__binding__context__other__client_binding_inf___binding_type_validity___node___state_group___attribute_nval)
																		.then(context => {
																			switch (context?.variant.name) {
																				case 'binding': {
																					return resolve(context)
																						.then(() => $this)
																						.then(() => test.Cboolean.Pfalse_).result;
																				}
																				case 'gui': {
																					const test__value_selection_path__has_steps__yes__select__binding__context__other__client_binding_inf___binding_type_validity___node___state_group___attribute___gui_nval = context.cast('gui');
																					return resolve(context)
																						.then(context => test__value_selection_path__has_steps__yes__select__binding__context__other__client_binding_inf___binding_type_validity___node___state_group___attribute___gui_nval)
																						.then(context => context?.properties.type)
																						.then(context => {
																							const state_group = context;
																							switch (state_group?.state.name) {
																								case 'action': {
																									return resolve(context)
																										.then(() => $this)
																										.then(() => test.Cboolean.Pfalse_).result;
																								}
																								case 'command': {
																									return resolve(context)
																										.then(() => $this)
																										.then(() => test.Cboolean.Pfalse_).result;
																								}
																								case 'property': {
																									const test__value_selection_path__has_steps__yes__select__binding__context__other__client_binding_inf___binding_type_validity___node___state_group___attribute___gui___property_nval = state_group.cast('property');
																									return resolve(context)
																										.then(context => test__value_selection_path__has_steps__yes__select__binding__context__other__client_binding_inf___binding_type_validity___node___state_group___attribute___gui___property_nval)
																										.then(context => context?.properties.type)
																										.then(context => {
																											const state_group = context;
																											switch (state_group?.state.name) {
																												case 'collection': {
																													return resolve(context)
																														.then(() => $this)
																														.then(() => test.Cboolean.Pfalse_).result;
																												}
																												case 'file': {
																													return resolve(context)
																														.then(() => $this)
																														.then(() => test.Cboolean.Pfalse_).result;
																												}
																												case 'group': {
																													return resolve(context)
																														.then(() => $this)
																														.then(() => test.Cboolean.Pfalse_).result;
																												}
																												case 'number': {
																													return resolve(context)
																														.then(() => $this)
																														.then(() => test.Cboolean.Pfalse_).result;
																												}
																												case 'state group': {
																													return resolve(context)
																														.then(() => $this)
																														.then(() => test.Cboolean.Ptrue_).result;
																												}
																												case 'text': {
																													return resolve(context)
																														.then(() => $this)
																														.then(() => test.Cboolean.Pfalse_).result;
																												}
																												case undefined: return undefined;
																												default: throw new Error(`Unexpected state '${(state_group!.state as any).name}'`);
																											};}).result;
																								}
																								case 'reference set': {
																									return resolve(context)
																										.then(() => $this)
																										.then(() => test.Cboolean.Pfalse_).result;
																								}
																								case undefined: return undefined;
																								default: throw new Error(`Unexpected state '${(state_group!.state as any).name}'`);
																							};}).result;
																				}
																				case undefined: return undefined;
																				default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
																			};
																		}).result;
																}
																case 'entry': {
																	return resolve(context)
																		.then(() => $this)
																		.then(() => test.Cboolean.Pfalse_).result;
																}
																case 'ordered graph': {
																	return resolve(context)
																		.then(() => $this)
																		.then(() => test.Cboolean.Pfalse_).result;
																}
																case 'query entry': {
																	return resolve(context)
																		.then(() => $this)
																		.then(() => test.Cboolean.Pfalse_).result;
																}
																case 'rule': {
																	return resolve(context)
																		.then(() => $this)
																		.then(() => test.Cboolean.Pfalse_).result;
																}
																case 'state': {
																	return resolve(context)
																		.then(() => $this)
																		.then(() => test.Cboolean.Pfalse_).result;
																}
																case 'value': {
																	return resolve(context)
																		.then(() => $this)
																		.then(() => test.Cboolean.Pfalse_).result;
																}
																case undefined: return undefined;
																default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
															};
														}).result;
												}
												case 'text': {
													return resolve(context)
														.then(() => $this)
														.then(context => context?.component_root.input.context())
														.then(context => {
															switch (context?.variant.name) {
																case 'attribute': {
																	const test__value_selection_path__has_steps__yes__select__binding__context__other__client_binding_inf___binding_type_validity___node___text___attribute_nval = context.cast('attribute');
																	return resolve(context)
																		.then(context => test__value_selection_path__has_steps__yes__select__binding__context__other__client_binding_inf___binding_type_validity___node___text___attribute_nval)
																		.then(context => {
																			switch (context?.variant.name) {
																				case 'binding': {
																					return resolve(context)
																						.then(() => $this)
																						.then(() => test.Cboolean.Pfalse_).result;
																				}
																				case 'gui': {
																					const test__value_selection_path__has_steps__yes__select__binding__context__other__client_binding_inf___binding_type_validity___node___text___attribute___gui_nval = context.cast('gui');
																					return resolve(context)
																						.then(context => test__value_selection_path__has_steps__yes__select__binding__context__other__client_binding_inf___binding_type_validity___node___text___attribute___gui_nval)
																						.then(context => context?.properties.type)
																						.then(context => {
																							const state_group = context;
																							switch (state_group?.state.name) {
																								case 'action': {
																									return resolve(context)
																										.then(() => $this)
																										.then(() => test.Cboolean.Pfalse_).result;
																								}
																								case 'command': {
																									return resolve(context)
																										.then(() => $this)
																										.then(() => test.Cboolean.Pfalse_).result;
																								}
																								case 'property': {
																									const test__value_selection_path__has_steps__yes__select__binding__context__other__client_binding_inf___binding_type_validity___node___text___attribute___gui___property_nval = state_group.cast('property');
																									return resolve(context)
																										.then(context => test__value_selection_path__has_steps__yes__select__binding__context__other__client_binding_inf___binding_type_validity___node___text___attribute___gui___property_nval)
																										.then(context => context?.properties.type)
																										.then(context => {
																											const state_group = context;
																											switch (state_group?.state.name) {
																												case 'collection': {
																													return resolve(context)
																														.then(() => $this)
																														.then(() => test.Cboolean.Pfalse_).result;
																												}
																												case 'file': {
																													return resolve(context)
																														.then(() => $this)
																														.then(() => test.Cboolean.Pfalse_).result;
																												}
																												case 'group': {
																													return resolve(context)
																														.then(() => $this)
																														.then(() => test.Cboolean.Pfalse_).result;
																												}
																												case 'number': {
																													return resolve(context)
																														.then(() => $this)
																														.then(() => test.Cboolean.Pfalse_).result;
																												}
																												case 'state group': {
																													return resolve(context)
																														.then(() => $this)
																														.then(() => test.Cboolean.Pfalse_).result;
																												}
																												case 'text': {
																													return resolve(context)
																														.then(() => $this)
																														.then(() => test.Cboolean.Ptrue_).result;
																												}
																												case undefined: return undefined;
																												default: throw new Error(`Unexpected state '${(state_group!.state as any).name}'`);
																											};}).result;
																								}
																								case 'reference set': {
																									return resolve(context)
																										.then(() => $this)
																										.then(() => test.Cboolean.Pfalse_).result;
																								}
																								case undefined: return undefined;
																								default: throw new Error(`Unexpected state '${(state_group!.state as any).name}'`);
																							};}).result;
																				}
																				case undefined: return undefined;
																				default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
																			};
																		}).result;
																}
																case 'entry': {
																	return resolve(context)
																		.then(() => $this)
																		.then(() => test.Cboolean.Pfalse_).result;
																}
																case 'ordered graph': {
																	return resolve(context)
																		.then(() => $this)
																		.then(() => test.Cboolean.Pfalse_).result;
																}
																case 'query entry': {
																	return resolve(context)
																		.then(() => $this)
																		.then(() => test.Cboolean.Pfalse_).result;
																}
																case 'rule': {
																	return resolve(context)
																		.then(() => $this)
																		.then(() => test.Cboolean.Pfalse_).result;
																}
																case 'state': {
																	return resolve(context)
																		.then(() => $this)
																		.then(() => test.Cboolean.Pfalse_).result;
																}
																case 'value': {
																	return resolve(context)
																		.then(() => $this)
																		.then(() => test.Cboolean.Pfalse_).result;
																}
																case undefined: return undefined;
																default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
															};
														}).result;
												}
												case 'where rule': {
													return resolve(context)
														.then(() => $this)
														.then(context => context?.component_root.input.context())
														.then(context => {
															switch (context?.variant.name) {
																case 'attribute': {
																	return resolve(context)
																		.then(() => $this)
																		.then(() => test.Cboolean.Pfalse_).result;
																}
																case 'entry': {
																	return resolve(context)
																		.then(() => $this)
																		.then(() => test.Cboolean.Pfalse_).result;
																}
																case 'ordered graph': {
																	return resolve(context)
																		.then(() => $this)
																		.then(() => test.Cboolean.Pfalse_).result;
																}
																case 'query entry': {
																	return resolve(context)
																		.then(() => $this)
																		.then(() => test.Cboolean.Pfalse_).result;
																}
																case 'rule': {
																	return resolve(context)
																		.then(() => $this)
																		.then(() => test.Cboolean.Ptrue_).result;
																}
																case 'state': {
																	return resolve(context)
																		.then(() => $this)
																		.then(() => test.Cboolean.Pfalse_).result;
																}
																case 'value': {
																	return resolve(context)
																		.then(() => $this)
																		.then(() => test.Cboolean.Pfalse_).result;
																}
																case undefined: return undefined;
																default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
															};
														}).result;
												}
												case undefined: return undefined;
												default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
											};
										}).result;
								}
								case 'query': {
									const test__value_selection_path__has_steps__yes__select__binding__context__other__client_binding_inf___binding_type_validity___query_nval = context.cast('query');
									return resolve(context)
										.then(context => test__value_selection_path__has_steps__yes__select__binding__context__other__client_binding_inf___binding_type_validity___query_nval)
										.then(context => {
											switch (context?.variant.name) {
												case 'entry': {
													return resolve(context)
														.then(() => $this)
														.then(context => context?.component_root.input.context())
														.then(context => {
															switch (context?.variant.name) {
																case 'attribute': {
																	return resolve(context)
																		.then(() => $this)
																		.then(() => test.Cboolean.Pfalse_).result;
																}
																case 'entry': {
																	return resolve(context)
																		.then(() => $this)
																		.then(() => test.Cboolean.Pfalse_).result;
																}
																case 'ordered graph': {
																	return resolve(context)
																		.then(() => $this)
																		.then(() => test.Cboolean.Pfalse_).result;
																}
																case 'query entry': {
																	return resolve(context)
																		.then(() => $this)
																		.then(() => test.Cboolean.Ptrue_).result;
																}
																case 'rule': {
																	return resolve(context)
																		.then(() => $this)
																		.then(() => test.Cboolean.Pfalse_).result;
																}
																case 'state': {
																	return resolve(context)
																		.then(() => $this)
																		.then(() => test.Cboolean.Pfalse_).result;
																}
																case 'value': {
																	return resolve(context)
																		.then(() => $this)
																		.then(() => test.Cboolean.Pfalse_).result;
																}
																case undefined: return undefined;
																default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
															};
														}).result;
												}
												case 'file': {
													return resolve(context)
														.then(() => $this)
														.then(() => test.Cboolean.Pfalse_).result;
												}
												case 'number column': {
													return resolve(context)
														.then(() => $this)
														.then(() => test.Cboolean.Pfalse_).result;
												}
												case 'number entry': {
													return resolve(context)
														.then(() => $this)
														.then(() => test.Cboolean.Pfalse_).result;
												}
												case 'stategroup column': {
													return resolve(context)
														.then(() => $this)
														.then(() => test.Cboolean.Pfalse_).result;
												}
												case 'stategroup entry': {
													return resolve(context)
														.then(() => $this)
														.then(() => test.Cboolean.Pfalse_).result;
												}
												case 'text column': {
													return resolve(context)
														.then(() => $this)
														.then(() => test.Cboolean.Pfalse_).result;
												}
												case 'text entry': {
													return resolve(context)
														.then(() => $this)
														.then(() => test.Cboolean.Pfalse_).result;
												}
												case undefined: return undefined;
												default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
											};
										}).result;
								}
								case 'report': {
									const test__value_selection_path__has_steps__yes__select__binding__context__other__client_binding_inf___binding_type_validity___report_nval = context.cast('report');
									return resolve(context)
										.then(context => test__value_selection_path__has_steps__yes__select__binding__context__other__client_binding_inf___binding_type_validity___report_nval)
										.then(context => {
											switch (context?.variant.name) {
												case 'node': {
													return resolve(context)
														.then(() => $this)
														.then(() => test.Cboolean.Pfalse_).result;
												}
												case 'number': {
													return resolve(context)
														.then(() => $this)
														.then(() => test.Cboolean.Pfalse_).result;
												}
												case 'text': {
													return resolve(context)
														.then(() => $this)
														.then(() => test.Cboolean.Pfalse_).result;
												}
												case undefined: return undefined;
												default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
											};
										}).result;
								}
								case 'role': {
									return resolve(context)
										.then(() => $this)
										.then(() => test.Cboolean.Pfalse_).result;
								}
								case 'user': {
									return resolve(context)
										.then(() => $this)
										.then(() => test.Cboolean.Ptrue_).result;
								}
								case 'view': {
									return resolve(context)
										.then(() => $this)
										.then(context => context?.component_root.input.context())
										.then(context => {
											switch (context?.variant.name) {
												case 'attribute': {
													return resolve(context)
														.then(() => $this)
														.then(() => test.Cboolean.Pfalse_).result;
												}
												case 'entry': {
													return resolve(context)
														.then(() => $this)
														.then(() => test.Cboolean.Pfalse_).result;
												}
												case 'ordered graph': {
													return resolve(context)
														.then(() => $this)
														.then(() => test.Cboolean.Pfalse_).result;
												}
												case 'query entry': {
													return resolve(context)
														.then(() => $this)
														.then(() => test.Cboolean.Pfalse_).result;
												}
												case 'rule': {
													return resolve(context)
														.then(() => $this)
														.then(() => test.Cboolean.Pfalse_).result;
												}
												case 'state': {
													return resolve(context)
														.then(() => $this)
														.then(() => test.Cboolean.Pfalse_).result;
												}
												case 'value': {
													const test__value_selection_path__has_steps__yes__select__binding__context__other__client_binding_inf___binding_type_validity___view___value_nval = context.cast('value');
													return resolve(context)
														.then(context => test__value_selection_path__has_steps__yes__select__binding__context__other__client_binding_inf___binding_type_validity___view___value_nval)
														.then(context => {
															switch (context?.variant.name) {
																case 'none': {
																	return resolve(context)
																		.then(() => $this)
																		.then(() => test.Cboolean.Pfalse_).result;
																}
																case 'value': {
																	const test__value_selection_path__has_steps__yes__select__binding__context__other__client_binding_inf___binding_type_validity___view___value___value_nval = context.cast('value');
																	return resolve(context)
																		.then(context => test__value_selection_path__has_steps__yes__select__binding__context__other__client_binding_inf___binding_type_validity___view___value___value_nval)
																		.then(context => {
																			switch (context?.variant.name) {
																				case 'file': {
																					return resolve(context)
																						.then(() => $this)
																						.then(() => test.Cboolean.Pfalse_).result;
																				}
																				case 'node': {
																					return resolve(context)
																						.then(() => $this)
																						.then(() => test.Cboolean.Pfalse_).result;
																				}
																				case 'number': {
																					return resolve(context)
																						.then(() => $this)
																						.then(() => test.Cboolean.Pfalse_).result;
																				}
																				case 'query': {
																					return resolve(context)
																						.then(() => $this)
																						.then(() => test.Cboolean.Pfalse_).result;
																				}
																				case 'text': {
																					return resolve(context)
																						.then(() => $this)
																						.then(() => test.Cboolean.Pfalse_).result;
																				}
																				case 'view': {
																					const test__value_selection_path__has_steps__yes__select__binding__context__other__client_binding_inf___binding_type_validity___view___value___value___view_nval = context.cast('view');
																					return resolve(context)
																						.then(context => test__value_selection_path__has_steps__yes__select__binding__context__other__client_binding_inf___binding_type_validity___view___value___value___view_nval)
																						.then(context => context?.component_root.output.open()).result;
																				}
																				case undefined: return undefined;
																				default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
																			};
																		}).result;
																}
																case undefined: return undefined;
																default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
															};
														}).result;
												}
												case undefined: return undefined;
												default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
											};
										}).result;
								}
								case 'window': {
									return resolve(context)
										.then(() => $this)
										.then(context => context?.component_root.input.context())
										.then(context => {
											switch (context?.variant.name) {
												case 'attribute': {
													return resolve(context)
														.then(() => $this)
														.then(() => test.Cboolean.Pfalse_).result;
												}
												case 'entry': {
													return resolve(context)
														.then(() => $this)
														.then(() => test.Cboolean.Pfalse_).result;
												}
												case 'ordered graph': {
													return resolve(context)
														.then(() => $this)
														.then(() => test.Cboolean.Pfalse_).result;
												}
												case 'query entry': {
													return resolve(context)
														.then(() => $this)
														.then(() => test.Cboolean.Pfalse_).result;
												}
												case 'rule': {
													return resolve(context)
														.then(() => $this)
														.then(() => test.Cboolean.Pfalse_).result;
												}
												case 'state': {
													return resolve(context)
														.then(() => $this)
														.then(() => test.Cboolean.Pfalse_).result;
												}
												case 'value': {
													const test__value_selection_path__has_steps__yes__select__binding__context__other__client_binding_inf___binding_type_validity___window___value_nval = context.cast('value');
													return resolve(context)
														.then(context => test__value_selection_path__has_steps__yes__select__binding__context__other__client_binding_inf___binding_type_validity___window___value_nval)
														.then(context => {
															switch (context?.variant.name) {
																case 'none': {
																	return resolve(context)
																		.then(() => $this)
																		.then(() => test.Cboolean.Pfalse_).result;
																}
																case 'value': {
																	const test__value_selection_path__has_steps__yes__select__binding__context__other__client_binding_inf___binding_type_validity___window___value___value_nval = context.cast('value');
																	return resolve(context)
																		.then(context => test__value_selection_path__has_steps__yes__select__binding__context__other__client_binding_inf___binding_type_validity___window___value___value_nval)
																		.then(context => {
																			switch (context?.variant.name) {
																				case 'file': {
																					return resolve(context)
																						.then(() => $this)
																						.then(() => test.Cboolean.Pfalse_).result;
																				}
																				case 'node': {
																					return resolve(context)
																						.then(() => $this)
																						.then(() => test.Cboolean.Pfalse_).result;
																				}
																				case 'number': {
																					return resolve(context)
																						.then(() => $this)
																						.then(() => test.Cboolean.Pfalse_).result;
																				}
																				case 'query': {
																					return resolve(context)
																						.then(() => $this)
																						.then(() => test.Cboolean.Pfalse_).result;
																				}
																				case 'text': {
																					return resolve(context)
																						.then(() => $this)
																						.then(() => test.Cboolean.Pfalse_).result;
																				}
																				case 'view': {
																					const test__value_selection_path__has_steps__yes__select__binding__context__other__client_binding_inf___binding_type_validity___window___value___value___view_nval = context.cast('view');
																					return resolve(context)
																						.then(context => test__value_selection_path__has_steps__yes__select__binding__context__other__client_binding_inf___binding_type_validity___window___value___value___view_nval)
																						.then(context => context?.component_root.output.open()).result;
																				}
																				case undefined: return undefined;
																				default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
																			};
																		}).result;
																}
																case undefined: return undefined;
																default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
															};
														}).result;
												}
												case undefined: return undefined;
												default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
											};
										}).result;
								}
								case undefined: return undefined;
								default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
							};
						}).result!;
				})
				,
				gui_context_valid: cache((detach:boolean) => {
					const test__value_selection_path__has_steps__yes__select__binding__context__other__client_binding_nval = $this.properties.client_binding.ref;
					return resolve($this.properties.client_binding.ref)
						.then(() => $this.properties.client_binding.inferences.gui_context_validity())
						.then(context => context?.variant.name === 'true' ? context.variant.definition as test.Ctrue : undefined).result!;
				})
				,
				gui_context_validity: cache((detach:boolean) => {
					const test__value_selection_path__has_steps__yes__select__binding__context__other__client_binding_nval = $this.properties.client_binding.ref;
					return resolve($this.properties.client_binding.ref)
						.then(() => $this)
						.then(context => context?.component_root.input.context())
						.then(context => {
							switch (context?.variant.name) {
								case 'attribute': {
									const test__value_selection_path__has_steps__yes__select__binding__context__other__client_binding_inf___gui_context_validity___attribute_nval = context.cast('attribute');
									return resolve(context)
										.then(context => test__value_selection_path__has_steps__yes__select__binding__context__other__client_binding_inf___gui_context_validity___attribute_nval)
										.then(context => {
											switch (context?.variant.name) {
												case 'binding': {
													return resolve(context)
														.then(() => $this)
														.then(() => test.Cboolean.Pfalse_).result;
												}
												case 'gui': {
													return resolve(context)
														.then(() => $this)
														.then(() => test.Cboolean.Ptrue_).result;
												}
												case undefined: return undefined;
												default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
											};
										}).result;
								}
								case 'entry': {
									const test__value_selection_path__has_steps__yes__select__binding__context__other__client_binding_inf___gui_context_validity___entry_nval = context.cast('entry');
									return resolve(context)
										.then(context => test__value_selection_path__has_steps__yes__select__binding__context__other__client_binding_inf___gui_context_validity___entry_nval)
										.then(context => {
											switch (context?.variant.name) {
												case 'binding': {
													return resolve(context)
														.then(() => $this)
														.then(() => test.Cboolean.Pfalse_).result;
												}
												case 'gui': {
													return resolve(context)
														.then(() => $this)
														.then(() => test.Cboolean.Ptrue_).result;
												}
												case undefined: return undefined;
												default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
											};
										}).result;
								}
								case 'ordered graph': {
									return resolve(context)
										.then(() => $this)
										.then(() => test.Cboolean.Ptrue_).result;
								}
								case 'query entry': {
									return resolve(context)
										.then(() => $this)
										.then(() => test.Cboolean.Ptrue_).result;
								}
								case 'rule': {
									return resolve(context)
										.then(() => $this)
										.then(() => test.Cboolean.Ptrue_).result;
								}
								case 'state': {
									const test__value_selection_path__has_steps__yes__select__binding__context__other__client_binding_inf___gui_context_validity___state_nval = context.cast('state');
									return resolve(context)
										.then(context => test__value_selection_path__has_steps__yes__select__binding__context__other__client_binding_inf___gui_context_validity___state_nval)
										.then(context => {
											switch (context?.variant.name) {
												case 'binding': {
													return resolve(context)
														.then(() => $this)
														.then(() => test.Cboolean.Pfalse_).result;
												}
												case 'gui': {
													return resolve(context)
														.then(() => $this)
														.then(() => test.Cboolean.Ptrue_).result;
												}
												case undefined: return undefined;
												default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
											};
										}).result;
								}
								case 'value': {
									const test__value_selection_path__has_steps__yes__select__binding__context__other__client_binding_inf___gui_context_validity___value_nval = context.cast('value');
									return resolve(context)
										.then(context => test__value_selection_path__has_steps__yes__select__binding__context__other__client_binding_inf___gui_context_validity___value_nval)
										.then(context => {
											switch (context?.variant.name) {
												case 'none': {
													return resolve(context)
														.then(() => $this)
														.then(() => test.Cboolean.Pfalse_).result;
												}
												case 'value': {
													const test__value_selection_path__has_steps__yes__select__binding__context__other__client_binding_inf___gui_context_validity___value___value_nval = context.cast('value');
													return resolve(context)
														.then(context => test__value_selection_path__has_steps__yes__select__binding__context__other__client_binding_inf___gui_context_validity___value___value_nval)
														.then(context => {
															switch (context?.variant.name) {
																case 'file': {
																	return resolve(context)
																		.then(() => $this)
																		.then(() => test.Cboolean.Pfalse_).result;
																}
																case 'node': {
																	const test__value_selection_path__has_steps__yes__select__binding__context__other__client_binding_inf___gui_context_validity___value___value___node_nval = context.cast('node');
																	return resolve(context)
																		.then(context => test__value_selection_path__has_steps__yes__select__binding__context__other__client_binding_inf___gui_context_validity___value___value___node_nval)
																		.then(context => {
																			switch (context?.variant.name) {
																				case 'binding': {
																					return resolve(context)
																						.then(() => $this)
																						.then(() => test.Cboolean.Pfalse_).result;
																				}
																				case 'gui': {
																					return resolve(context)
																						.then(() => $this)
																						.then(() => test.Cboolean.Ptrue_).result;
																				}
																				case undefined: return undefined;
																				default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
																			};
																		}).result;
																}
																case 'number': {
																	return resolve(context)
																		.then(() => $this)
																		.then(() => test.Cboolean.Pfalse_).result;
																}
																case 'query': {
																	return resolve(context)
																		.then(() => $this)
																		.then(() => test.Cboolean.Ptrue_).result;
																}
																case 'text': {
																	return resolve(context)
																		.then(() => $this)
																		.then(() => test.Cboolean.Pfalse_).result;
																}
																case 'view': {
																	const test__value_selection_path__has_steps__yes__select__binding__context__other__client_binding_inf___gui_context_validity___value___value___view_nval = context.cast('view');
																	return resolve(context)
																		.then(context => test__value_selection_path__has_steps__yes__select__binding__context__other__client_binding_inf___gui_context_validity___value___value___view_nval)
																		.then(context => context?.component_root.output.open()).result;
																}
																case undefined: return undefined;
																default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
															};
														}).result;
												}
												case undefined: return undefined;
												default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
											};
										}).result;
								}
								case undefined: return undefined;
								default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
							};
						}).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'client binding'`; }
	}
}
export namespace Ccollection__select {
	export class Dselect extends StateGroup<
		{ name: 'entry', node:Centry__select, init:Tentry__select}|
		{ name: 'size', node:Csize, init:Tsize}> {
		protected initializer(state: 'entry'|'size') {
			switch (state) {
				case 'entry': return (init:Tentry__select, parent:Ccollection__select) => new Centry__select(init, parent);
				case 'size': return (init:Tsize, parent:Ccollection__select) => new Csize(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'entry'|'size') {
			switch (state) {
				case 'entry': return finalize_entry__select;
				case 'size': return finalize_size;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tcollection__select['select'], parent:Ccollection__select) {
			super(data, parent, 'select');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'select'`; }
	}
}
export namespace Centry__select {
	export class Dkey extends Cvalue_expression {
		public readonly inferences:{
			text: () => test.Ctext__value
		}
		constructor(data:Tentry__select['key'], parent:Centry__select) {
			super(data, parent, {
				stack: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.stack()).result!)
			}, 'key')
			this.inferences = {
				text: cache((detach:boolean) => {
					const test__value_selection_path__has_steps__yes__select__collection__select__entry__key_nval = this;
					return resolve(this)
						.then(context => test__value_selection_path__has_steps__yes__select__collection__select__entry__key_nval)
						.then(context => context?.component_root.output.value())
						.then(context => context?.variant.name === 'value' ? context.variant.definition as test.Cvalue__test : undefined)
						.then(context => context?.variant.name === 'text' ? context.variant.definition as test.Ctext__value : undefined).result!;
				})

			}
		}
	}
}
export namespace Cquery__select {
	export class Dquery extends Reference<views.Cqueries,string> {
		public readonly inferences:{
			node_valid: () => test.Cnode__test,
			optional_node: () => test.Coptional_node
		}

		constructor(data:string, $this:Cquery__select) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.properties.select_view?.state.node.output.view())
				.then(context => context?.component_root.output.definition())
				.then(context => {
					const entry = context?.properties.queries.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
			this.inferences = {
				node_valid: cache((detach:boolean) => {
					const test__value_selection_path__has_steps__yes__select__query__query_nval = $this.properties.query.ref;
					return resolve($this.properties.query.ref)
						.then(() => $this.properties.query.inferences.optional_node())
						.then(context => context?.variant.name === 'node' ? context.variant.definition as test.Cnode__test : undefined).result!;
				})
				,
				optional_node: cache((detach:boolean) => {
					const test__value_selection_path__has_steps__yes__select__query__query_nval = $this.properties.query.ref;
					return resolve($this.properties.query.ref)
						.then(context => test__value_selection_path__has_steps__yes__select__query__query_nval)
						.then(context => context?.properties.context)
						.then(context => {
							const state_group = context;
							switch (state_group?.state.name) {
								case 'candidates': {
									const test__value_selection_path__has_steps__yes__select__query__query_inf___optional_node___candidates_nval = state_group.cast('candidates');
									return resolve(context)
										.then(() => $this)
										.then(context => context?.component_root.input.context())
										.then(context => {
											switch (context?.variant.name) {
												case 'attribute': {
													const test__value_selection_path__has_steps__yes__select__query__query_inf___optional_node___candidates___attribute_nval = context.cast('attribute');
													return resolve(context)
														.then(context => test__value_selection_path__has_steps__yes__select__query__query_inf___optional_node___candidates___attribute_nval)
														.then(context => {
															switch (context?.variant.name) {
																case 'binding': {
																	return resolve(context)
																		.then(() => $this)
																		.then(() => test.Coptional_node.Pnone).result;
																}
																case 'gui': {
																	const test__value_selection_path__has_steps__yes__select__query__query_inf___optional_node___candidates___attribute___gui_nval = context.cast('gui');
																	return resolve(context)
																		.then(context => test__value_selection_path__has_steps__yes__select__query__query_inf___optional_node___candidates___attribute___gui_nval)
																		.then(context => context?.properties.type)
																		.then(context => {
																			const state_group = context;
																			switch (state_group?.state.name) {
																				case 'action': {
																					return resolve(context)
																						.then(() => $this)
																						.then(() => test.Coptional_node.Pnone).result;
																				}
																				case 'command': {
																					return resolve(context)
																						.then(() => $this)
																						.then(() => test.Coptional_node.Pnone).result;
																				}
																				case 'property': {
																					const test__value_selection_path__has_steps__yes__select__query__query_inf___optional_node___candidates___attribute___gui___property_nval = state_group.cast('property');
																					return resolve(context)
																						.then(context => test__value_selection_path__has_steps__yes__select__query__query_inf___optional_node___candidates___attribute___gui___property_nval)
																						.then(context => context?.properties.type)
																						.then(context => {
																							const state_group = context;
																							switch (state_group?.state.name) {
																								case 'collection': {
																									return resolve(context)
																										.then(() => $this)
																										.then(() => test.Coptional_node.Pnone).result;
																								}
																								case 'file': {
																									return resolve(context)
																										.then(() => $this)
																										.then(() => test.Coptional_node.Pnone).result;
																								}
																								case 'group': {
																									return resolve(context)
																										.then(() => $this)
																										.then(() => test.Coptional_node.Pnone).result;
																								}
																								case 'number': {
																									return resolve(context)
																										.then(() => $this)
																										.then(() => test.Coptional_node.Pnone).result;
																								}
																								case 'state group': {
																									return resolve(context)
																										.then(() => $this)
																										.then(() => test.Coptional_node.Pnone).result;
																								}
																								case 'text': {
																									const test__value_selection_path__has_steps__yes__select__query__query_inf___optional_node___candidates___attribute___gui___property___text_nval = state_group.cast('text');
																									return resolve(context)
																										.then(context => test__value_selection_path__has_steps__yes__select__query__query_inf___optional_node___candidates___attribute___gui___property___text_nval)
																										.then(context => context?.properties.navigable)
																										.then(context => {
																											const state_group = context;
																											switch (state_group?.state.name) {
																												case 'no': {
																													return resolve(context)
																														.then(() => $this)
																														.then(() => test.Coptional_node.Pnone).result;
																												}
																												case 'yes': {
																													const test__value_selection_path__has_steps__yes__select__query__query_inf___optional_node___candidates___attribute___gui___property___text___yes_nval = state_group.cast('yes');
																													return resolve(context)
																														.then(context => {
																															const left = resolve(context)
																																.then(context => test__value_selection_path__has_steps__yes__select__query__query_inf___optional_node___candidates_nval)
																																.then(context => context?.properties.property?.ref)
																																.then(context => context?.properties.referenced_node)
																																.then(context => context?.component_root.output.result_gui_node()).result;
																															const right = resolve(context)
																																.then(context => test__value_selection_path__has_steps__yes__select__query__query_inf___optional_node___candidates___attribute___gui___property___text___yes_nval)
																																.then(context => context?.properties.referenced_node)
																																.then(context => context?.component_root.output.result_gui_node()).result;
																															return left?.is(right!) ? left : undefined
																														})
																														.then(context => {
																															const conv_context = resolve(context).result!;
																															return new Cnode__test({name: 'gui', definition: conv_context});
																														})
																														.then(context => context?.definitions.optional_node).result;
																												}
																												case undefined: return undefined;
																												default: throw new Error(`Unexpected state '${(state_group!.state as any).name}'`);
																											};}).result;
																								}
																								case undefined: return undefined;
																								default: throw new Error(`Unexpected state '${(state_group!.state as any).name}'`);
																							};}).result;
																				}
																				case 'reference set': {
																					return resolve(context)
																						.then(() => $this)
																						.then(() => test.Coptional_node.Pnone).result;
																				}
																				case undefined: return undefined;
																				default: throw new Error(`Unexpected state '${(state_group!.state as any).name}'`);
																			};}).result;
																}
																case undefined: return undefined;
																default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
															};
														}).result;
												}
												case 'entry': {
													return resolve(context)
														.then(() => $this)
														.then(() => test.Coptional_node.Pnone).result;
												}
												case 'ordered graph': {
													return resolve(context)
														.then(() => $this)
														.then(() => test.Coptional_node.Pnone).result;
												}
												case 'query entry': {
													return resolve(context)
														.then(() => $this)
														.then(() => test.Coptional_node.Pnone).result;
												}
												case 'rule': {
													return resolve(context)
														.then(() => $this)
														.then(() => test.Coptional_node.Pnone).result;
												}
												case 'state': {
													return resolve(context)
														.then(() => $this)
														.then(() => test.Coptional_node.Pnone).result;
												}
												case 'value': {
													return resolve(context)
														.then(() => $this)
														.then(() => test.Coptional_node.Pnone).result;
												}
												case undefined: return undefined;
												default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
											};
										}).result;
								}
								case 'node': {
									const test__value_selection_path__has_steps__yes__select__query__query_inf___optional_node___node_nval = state_group.cast('node');
									return resolve(context)
										.then(context => test__value_selection_path__has_steps__yes__select__query__query_inf___optional_node___node_nval)
										.then(context => context?.properties.path)
										.then(context => context?.component_root.output.result_node())
										.then(context => {
											const conv_context = resolve(context).result!;
											return new Cnode__test({name: 'gui', definition: conv_context});
										})
										.then(context => context?.definitions.optional_node).result;
								}
								case undefined: return undefined;
								default: throw new Error(`Unexpected state '${(state_group!.state as any).name}'`);
							};}).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'query'`; }
	}
	export class Dselect_view extends StateGroup<
		{ name: 'expression', node:Cexpression__select_view, init:Texpression__select_view}|
		{ name: 'from context', node:Cfrom_context, init:Tfrom_context}> {
		protected initializer(state: 'expression'|'from context') {
			switch (state) {
				case 'expression': return (init:Texpression__select_view, parent:Cquery__select) => new Cexpression__select_view(init, parent);
				case 'from context': return (init:Tfrom_context, parent:Cquery__select) => new Cfrom_context(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'expression'|'from context') {
			switch (state) {
				case 'expression': return finalize_expression__select_view;
				case 'from context': return finalize_from_context;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tquery__select['select view'], parent:Cquery__select) {
			super(data, parent, 'select view');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'select view'`; }
	}
}
export namespace Cexpression__select_view {
	export class Dview_selection extends Cvalue_expression {
		public readonly inferences:{
			view: () => test.Cexplicit_view
		}
		constructor(data:Texpression__select_view['view selection'], parent:Cexpression__select_view) {
			super(data, parent, {
				stack: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.stack()).result!)
			}, 'view selection')
			this.inferences = {
				view: cache((detach:boolean) => {
					const test__value_selection_path__has_steps__yes__select__query__select_view__expression__view_selection_nval = this;
					return resolve(this)
						.then(context => test__value_selection_path__has_steps__yes__select__query__select_view__expression__view_selection_nval)
						.then(context => context?.component_root.output.value())
						.then(context => context?.variant.name === 'value' ? context.variant.definition as test.Cvalue__test : undefined)
						.then(context => context?.variant.name === 'view' ? context.variant.definition as test.Cview__test : undefined)
						.then(context => context?.variant.name === 'explicit' ? context.variant.definition as test.Cexplicit_view : undefined).result!;
				})

			}
		}
	}
}
export namespace Cvalue_type_definition {
	export class Dtype extends StateGroup<
		{ name: 'binding', node:Cbinding__type, init:Tbinding__type}|
		{ name: 'file', node:Cfile__type__value_type_definition, init:Tfile__type__value_type_definition}|
		{ name: 'node', node:Cnode__type__value_type_definition, init:Tnode__type__value_type_definition}|
		{ name: 'number', node:Cnumber__type__value_type_definition, init:Tnumber__type__value_type_definition}|
		{ name: 'query', node:Cquery__type, init:Tquery__type}|
		{ name: 'text', node:Ctext__type__value_type_definition, init:Ttext__type__value_type_definition}|
		{ name: 'view', node:Cview__type, init:Tview__type}> {
		protected initializer(state: 'binding'|'file'|'node'|'number'|'query'|'text'|'view') {
			switch (state) {
				case 'binding': return (init:Tbinding__type, parent:Cvalue_type_definition) => new Cbinding__type(init, parent);
				case 'file': return (init:Tfile__type__value_type_definition, parent:Cvalue_type_definition) => new Cfile__type__value_type_definition(init, parent);
				case 'node': return (init:Tnode__type__value_type_definition, parent:Cvalue_type_definition) => new Cnode__type__value_type_definition(init, parent);
				case 'number': return (init:Tnumber__type__value_type_definition, parent:Cvalue_type_definition) => new Cnumber__type__value_type_definition(init, parent);
				case 'query': return (init:Tquery__type, parent:Cvalue_type_definition) => new Cquery__type(init, parent);
				case 'text': return (init:Ttext__type__value_type_definition, parent:Cvalue_type_definition) => new Ctext__type__value_type_definition(init, parent);
				case 'view': return (init:Tview__type, parent:Cvalue_type_definition) => new Cview__type(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'binding'|'file'|'node'|'number'|'query'|'text'|'view') {
			switch (state) {
				case 'binding': return finalize_binding__type;
				case 'file': return finalize_file__type__value_type_definition;
				case 'node': return finalize_node__type__value_type_definition;
				case 'number': return finalize_number__type__value_type_definition;
				case 'query': return finalize_query__type;
				case 'text': return finalize_text__type__value_type_definition;
				case 'view': return finalize_view__type;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tvalue_type_definition['type'], parent:Cvalue_type_definition) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Cbinding__type {
	export class Dclient_binding extends Reference<client_bindings.Cbindings__client_bindings,string> {
		public readonly inferences:{
			node: () => test.Cnode__test
		}

		constructor(data:string, $this:Cbinding__type) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.root.input.client_bindings)
				.then(context => {
					const entry = context?.properties.bindings.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
			this.inferences = {
				node: cache((detach:boolean) => {
					const test__value_type_definition__type__binding__client_binding_nval = $this.properties.client_binding.ref;
					return resolve($this.properties.client_binding.ref)
						.then(context => test__value_type_definition__type__binding__client_binding_nval)
						.then(context => context?.properties.binding)
						.then(context => {
							const conv_context = resolve(context).result!;
							return new Cnode__test({name: 'binding', definition: conv_context});
						}).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'client binding'`; }
	}
	export class Dtype extends Cnode_type_path {
		constructor(data:Tbinding__type['type'], parent:Cbinding__type) {
			super(data, parent, {
				node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.client_binding?.inferences.node()).result!)
			}, 'type')
		}
	}
}
export namespace Cnode__type__value_type_definition {
	export class Dtype extends Cnode_type_path {
		constructor(data:Tnode__type__value_type_definition['type'], parent:Cnode__type__value_type_definition) {
			super(data, parent, {
				node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.root.input.gui_model)
					.then(context => context?.properties.root)
					.then(context => {
						const conv_context = resolve(context).result!;
						return new Cnode__test({name: 'gui', definition: conv_context});
					}).result!)
			}, 'type')
		}
	}
}
export namespace Cquery__type {
	export class Dquery extends Reference<views.Cqueries,string> {

		constructor(data:string, $this:Cquery__type) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.properties.view?.ref)
				.then(context => {
					const entry = context?.properties.queries.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'query'`; }
	}
	export class Dselect_views extends StateGroup<
		{ name: 'choose', node:Cchoose__select_views__query, init:Tchoose__select_views__query}|
		{ name: 'default', node:Cdefault__select_views__query, init:Tdefault__select_views__query}> {
		protected initializer(state: 'choose'|'default') {
			switch (state) {
				case 'choose': return (init:Tchoose__select_views__query, parent:Cquery__type) => new Cchoose__select_views__query(init, parent);
				case 'default': return (init:Tdefault__select_views__query, parent:Cquery__type) => new Cdefault__select_views__query(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'choose'|'default') {
			switch (state) {
				case 'choose': return finalize_choose__select_views__query;
				case 'default': return finalize_default__select_views__query;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tquery__type['select views'], parent:Cquery__type) {
			super(data, parent, 'select views');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'select views'`; }
	}
	export class Dview extends Reference<views.Cviews__,string> {

		constructor(data:string, $this:Cquery__type) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.properties.select_views?.state.node.output.views())
				.then(context => {
					const entry = context?.properties.views.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'view'`; }
	}
}
export namespace Cchoose__select_views__query {
	export class Dviews extends Reference<views.Cviews,string> {

		constructor(data:string, $this:Cchoose__select_views__query) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this.root.input.views[this.entry]).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'views'`; }
	}
}
export namespace Ctest {
	export class Dbackground extends StateGroup<
		{ name: 'no', node:Cno__background, init:Tno__background}|
		{ name: 'yes', node:Cyes__background, init:Tyes__background}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__background, parent:Ctest) => new Cno__background(init, parent);
				case 'yes': return (init:Tyes__background, parent:Ctest) => new Cyes__background(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__background;
				case 'yes': return finalize_yes__background;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Ttest['background'], parent:Ctest) {
			super(data, parent, 'background');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'background'`; }
	}
	export class Ddefault_views_lib extends StateGroup<
		{ name: 'none', node:Cnone__default_views_lib, init:Tnone__default_views_lib}|
		{ name: 'set', node:Cset__default_views_lib, init:Tset__default_views_lib}> {
		protected initializer(state: 'none'|'set') {
			switch (state) {
				case 'none': return (init:Tnone__default_views_lib, parent:Ctest) => new Cnone__default_views_lib(init, parent);
				case 'set': return (init:Tset__default_views_lib, parent:Ctest) => new Cset__default_views_lib(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'none'|'set') {
			switch (state) {
				case 'none': return finalize_none__default_views_lib;
				case 'set': return finalize_set__default_views_lib;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Ttest['default views lib'], parent:Ctest) {
			super(data, parent, 'default views lib');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'default views lib'`; }
	}
	export class Ddefault_window extends StateGroup<
		{ name: 'none', node:Cnone__default_window, init:Tnone__default_window}|
		{ name: 'set', node:Cset__default_window, init:Tset__default_window}> {
		protected initializer(state: 'none'|'set') {
			switch (state) {
				case 'none': return (init:Tnone__default_window, parent:Ctest) => new Cnone__default_window(init, parent);
				case 'set': return (init:Tset__default_window, parent:Ctest) => new Cset__default_window(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'none'|'set') {
			switch (state) {
				case 'none': return finalize_none__default_window;
				case 'set': return finalize_set__default_window;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Ttest['default window'], parent:Ctest) {
			super(data, parent, 'default window');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'default window'`; }
	}
	export class Dprocedures extends AlanDictionary<{ node:Cprocedures, init:Tprocedures},Ctest> {
		protected initialize(parent:Ctest, key:string, entry_init:Tprocedures) { return new Cprocedures(key, entry_init, parent); }
		protected finalize = finalize_procedures
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'procedures'`; }
		constructor(data:Ttest['procedures'], parent:Ctest) {
			super(data, parent);
		}
	}
	export class Dscenarios extends AlanDictionary<{ node:Cscenarios, init:Tscenarios},Ctest> {
		protected initialize(parent:Ctest, key:string, entry_init:Tscenarios) { return new Cscenarios(key, entry_init, parent); }
		protected finalize = finalize_scenarios
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'scenarios'`; }
		constructor(data:Ttest['scenarios'], parent:Ctest) {
			super(data, parent);
		}
	}
}
export namespace Cyes__background {
	export class Dstatements extends Cstatements {
		constructor(data:Tyes__background['statements'], parent:Cyes__background) {
			super(data, parent, {
				stack: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => test.Cstack.Pempty).result!)
			}, 'statements')
		}
	}
	export class Dtimeout extends StateGroup<
		{ name: 'no', node:Cno__timeout__yes, init:Tno__timeout__yes}|
		{ name: 'yes', node:Cyes__timeout__yes, init:Tyes__timeout__yes}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__timeout__yes, parent:Cyes__background) => new Cno__timeout__yes(init, parent);
				case 'yes': return (init:Tyes__timeout__yes, parent:Cyes__background) => new Cyes__timeout__yes(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__timeout__yes;
				case 'yes': return finalize_yes__timeout__yes;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__background['timeout'], parent:Cyes__background) {
			super(data, parent, 'timeout');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'timeout'`; }
	}
}
export namespace Cyes__timeout__yes {
	export class Dmax_test_duration extends AlanInteger {
		constructor(data:Tyes__timeout__yes['max test duration'], parent:Cyes__timeout__yes) {
			number__is_positive(data);
			super(data, parent);}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'max test duration'`; }
	}
}
export namespace Cset__default_views_lib {
	export class Dviews_lib extends Reference<views.Cviews,string> {

		constructor(data:string, $this:Cset__default_views_lib) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this.root.input.views[this.entry]).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'views lib'`; }
	}
}
export namespace Cset__default_window {
	export class Dwindow extends Reference<settings.Cwindows,string> {

		constructor(data:string, $this:Cset__default_window) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.root.input.settings)
				.then(context => {
					const entry = context?.properties.windows.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'window'`; }
	}
}
export namespace Cprocedures {
	export class Dhas_parameters extends StateGroup<
		{ name: 'no', node:Cno__has_parameters__procedures, init:Tno__has_parameters__procedures}|
		{ name: 'yes', node:Cyes__has_parameters__procedures, init:Tyes__has_parameters__procedures}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_parameters__procedures, parent:Cprocedures) => new Cno__has_parameters__procedures(init, parent);
				case 'yes': return (init:Tyes__has_parameters__procedures, parent:Cprocedures) => new Cyes__has_parameters__procedures(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_parameters__procedures;
				case 'yes': return finalize_yes__has_parameters__procedures;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tprocedures['has parameters'], parent:Cprocedures) {
			super(data, parent, 'has parameters');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has parameters'`; }
	}
}
export namespace Cbody {
	export class Doutput_type extends StateGroup<
		{ name: 'none', node:Cnone__output_type, init:Tnone__output_type}|
		{ name: 'value', node:Cvalue__output_type, init:Tvalue__output_type}> {
		protected initializer(state: 'none'|'value') {
			switch (state) {
				case 'none': return (init:Tnone__output_type, parent:Cbody) => new Cnone__output_type(init, parent);
				case 'value': return (init:Tvalue__output_type, parent:Cbody) => new Cvalue__output_type(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'none'|'value') {
			switch (state) {
				case 'none': return finalize_none__output_type;
				case 'value': return finalize_value__output_type;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tbody['output type'], parent:Cbody) {
			super(data, parent, 'output type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'output type'`; }
	}
	export class Dstatements extends Cstatements {
		constructor(data:Tbody['statements'], parent:Cbody) {
			super(data, parent, {
				stack: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.properties.has_parameters?.state.node.output.stack()).result!)
			}, 'statements')
		}
	}
}
export namespace Cvalue__output_type {
	export class Dreturn_value extends Cvalue_expression {
		constructor(data:Tvalue__output_type['return value'], parent:Cvalue__output_type) {
			super(data, parent, {
				stack: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.properties.statements)
					.then(context => context?.component_root.output.stack()).result!)
			}, 'return value')
		}
	}
}
export namespace Cyes__has_parameters__procedures {
	export class Dparameters extends Cparameter_definition {
		constructor(data:Tyes__has_parameters__procedures['parameters'], parent:Cyes__has_parameters__procedures) {
			super(data, parent, {
				stack: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => test.Cstack.Pempty).result!)
			}, 'parameters')
		}
	}
}
export namespace Cscenarios {
	export class Dgiven extends StateGroup<
		{ name: 'no', node:Cno__given, init:Tno__given}|
		{ name: 'yes', node:Cyes__given, init:Tyes__given}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__given, parent:Cscenarios) => new Cno__given(init, parent);
				case 'yes': return (init:Tyes__given, parent:Cscenarios) => new Cyes__given(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__given;
				case 'yes': return finalize_yes__given;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tscenarios['given'], parent:Cscenarios) {
			super(data, parent, 'given');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'given'`; }
	}
	export class Dthen extends StateGroup<
		{ name: 'no', node:Cno__then, init:Tno__then}|
		{ name: 'yes', node:Cyes__then, init:Tyes__then}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__then, parent:Cscenarios) => new Cno__then(init, parent);
				case 'yes': return (init:Tyes__then, parent:Cscenarios) => new Cyes__then(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__then;
				case 'yes': return finalize_yes__then;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tscenarios['then'], parent:Cscenarios) {
			super(data, parent, 'then');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'then'`; }
	}
	export class Dtimeout extends StateGroup<
		{ name: 'no', node:Cno__timeout__scenarios, init:Tno__timeout__scenarios}|
		{ name: 'yes', node:Cyes__timeout__scenarios, init:Tyes__timeout__scenarios}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__timeout__scenarios, parent:Cscenarios) => new Cno__timeout__scenarios(init, parent);
				case 'yes': return (init:Tyes__timeout__scenarios, parent:Cscenarios) => new Cyes__timeout__scenarios(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__timeout__scenarios;
				case 'yes': return finalize_yes__timeout__scenarios;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tscenarios['timeout'], parent:Cscenarios) {
			super(data, parent, 'timeout');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'timeout'`; }
	}
	export class Dwhen extends StateGroup<
		{ name: 'no', node:Cno__when, init:Tno__when}|
		{ name: 'yes', node:Cyes__when, init:Tyes__when}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__when, parent:Cscenarios) => new Cno__when(init, parent);
				case 'yes': return (init:Tyes__when, parent:Cscenarios) => new Cyes__when(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__when;
				case 'yes': return finalize_yes__when;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tscenarios['when'], parent:Cscenarios) {
			super(data, parent, 'when');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'when'`; }
	}
}
export namespace Cyes__given {
	export class Dstatements extends Cstatements {
		constructor(data:Tyes__given['statements'], parent:Cyes__given) {
			super(data, parent, {
				stack: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.properties.background?.state.node.output.stack()).result!)
			}, 'statements')
		}
	}
}
export namespace Cyes__then {
	export class Dstatements extends Cstatements {
		constructor(data:Tyes__then['statements'], parent:Cyes__then) {
			super(data, parent, {
				stack: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.properties.when?.state.node.output.stack()).result!)
			}, 'statements')
		}
	}
}
export namespace Cyes__timeout__scenarios {
	export class Dmax_test_duration extends AlanInteger {
		constructor(data:Tyes__timeout__scenarios['max test duration'], parent:Cyes__timeout__scenarios) {
			number__is_positive(data);
			super(data, parent);}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'max test duration'`; }
	}
}
export namespace Cyes__when {
	export class Dstatements extends Cstatements {
		constructor(data:Tyes__when['statements'], parent:Cyes__when) {
			super(data, parent, {
				stack: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.properties.given?.state.node.output.stack()).result!)
			}, 'statements')
		}
	}
}
function finalize_attribute__test(obj:Cattribute__test, detach:boolean = false) {
	finalize_value_context(obj.definitions.value_context, detach);
}
function finalize_boolean(obj:Cboolean, detach:boolean = false) {
}
function finalize_collection__test(obj:Ccollection__test, detach:boolean = false) {
	finalize_value_context(obj.definitions.value_context, detach);
}
function finalize_no__assign__condition(obj:Cno__assign__condition, detach:boolean = false) {
	assert((<(detach?:boolean) => test.Cstack>obj.output.stack)(detach) !== undefined || detach);
}
function finalize_yes__assign__condition(obj:Cyes__assign__condition, detach:boolean = false) {
	assert((<(detach?:boolean) => test.Cexists__value_condition>obj.inferences.exists)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => test.Cvalue__test>obj.inferences.value)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => test.Cstack>obj.output.stack)(detach) !== undefined || detach);
	finalize_stack_frame(obj.properties.stack_frame, detach);
}
function finalize_equals(obj:Cequals, detach:boolean = false) {
}
function finalize_not_equals(obj:Cnot_equals, detach:boolean = false) {
}
function finalize_binary(obj:Cbinary, detach:boolean = false) {
	assert((<(detach?:boolean) => test.Cvalue__test>obj.inferences.value)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => test.Cexists__value_condition>obj.inferences.value_exists)(detach) !== undefined || detach);
	switch (obj.properties.condition.state.name) {
		case 'equals': finalize_equals(obj.properties.condition.state.node, detach); break;
		case 'not equals': finalize_not_equals(obj.properties.condition.state.node, detach); break;
	}
	finalize_value_expression(obj.properties.target, detach);
	assert((<(detach?:boolean) => test.Cvalue__test>obj.properties.target.inferences.correct_value)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => test.Cvalue__test>obj.properties.target.inferences.value)(detach) !== undefined || detach);
}
function finalize_unary(obj:Cunary, detach:boolean = false) {
}
function finalize_exists__value_condition(obj:Cexists__value_condition, detach:boolean = false) {
}
function finalize_not_exists(obj:Cnot_exists, detach:boolean = false) {
}
function finalize_condition(obj:Ccondition, detach:boolean = false) {
	assert((<(detach?:boolean) => test.Cstack>obj.input.stack)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => test.Cstack>obj.output.stack)(detach) !== undefined || detach);
	switch (obj.properties.assign.state.name) {
		case 'no': finalize_no__assign__condition(obj.properties.assign.state.node, detach); break;
		case 'yes': finalize_yes__assign__condition(obj.properties.assign.state.node, detach); break;
	}
	switch (obj.properties.expression_type.state.name) {
		case 'binary': finalize_binary(obj.properties.expression_type.state.node, detach); break;
		case 'unary': finalize_unary(obj.properties.expression_type.state.node, detach); break;
	}
	switch (obj.properties.value_condition.state.name) {
		case 'exists': finalize_exists__value_condition(obj.properties.value_condition.state.node, detach); break;
		case 'not exists': finalize_not_exists(obj.properties.value_condition.state.node, detach); break;
	}
	finalize_value_expression(obj.properties.value_expression, detach);
}
function finalize_explicit_view(obj:Cexplicit_view, detach:boolean = false) {
	assert((<(detach?:boolean) => views.Cviews__>obj.output.definition)(detach) !== undefined || detach);
	finalize_view__test(obj.definitions.view, detach);
}
function finalize_implicit_view(obj:Cimplicit_view, detach:boolean = false) {
	finalize_view__test(obj.definitions.view, detach);
}
function finalize_exists__next_argument(obj:Cexists__next_argument, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cexists>obj.inferences.exists)(detach) !== undefined || detach);
	finalize_instruction_argument(obj.properties.argument, detach);
}
function finalize_no__next_argument(obj:Cno__next_argument, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cno__next_argument>obj.inferences.no)(detach) !== undefined || detach);
}
function finalize_instruction_argument(obj:Cinstruction_argument, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cinstruction_argument>obj.input.argument)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => test.Cstack>obj.input.stack)(detach) !== undefined || detach);
	switch (obj.properties.next_argument.state.name) {
		case 'exists': finalize_exists__next_argument(obj.properties.next_argument.state.node, detach); break;
		case 'no': finalize_no__next_argument(obj.properties.next_argument.state.node, detach); break;
	}
	finalize_value_expression(obj.properties.value_expression, detach);
	assert((<(detach?:boolean) => test.Cboolean>obj.properties.value_expression.inferences.correct_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => test.Ctrue>obj.properties.value_expression.inferences.type_is_correct)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => test.Cvalue__test>obj.properties.value_expression.inferences.value)(detach) !== undefined || detach);
}
function finalize_node__test(obj:Cnode__test, detach:boolean = false) {
	finalize_optional_node(obj.definitions.optional_node, detach);
	finalize_value__test(obj.definitions.value, detach);
}
function finalize_no__has_steps__node_type_path(obj:Cno__has_steps__node_type_path, detach:boolean = false) {
	assert((<(detach?:boolean) => test.Cnode__test>obj.output.node)(detach) !== undefined || detach);
}
function finalize_entry__type(obj:Centry__type, detach:boolean = false) {
	assert((<(detach?:boolean) => test.Ccollection__test>obj.inferences.collection)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => test.Cnode__test>obj.inferences.node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => test.Cnode__test>obj.output.node)(detach) !== undefined || detach);
}
function finalize_node__type__yes(obj:Cnode__type__yes, detach:boolean = false) {
	assert((<(detach?:boolean) => test.Cnode__test>obj.inferences.node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => test.Coptional_value>obj.inferences.optional_value)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => test.Cnode__test>obj.output.node)(detach) !== undefined || detach);
}
function finalize_state__type(obj:Cstate__type, detach:boolean = false) {
	assert((<(detach?:boolean) => test.Cstate_group>obj.inferences.state_group)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => test.Cnode__test>obj.output.node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => test.Cstate__test>(obj.properties.state as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => test.Cnode__test>obj.properties.state.inferences.node)(detach) !== undefined || detach);
}
function finalize_yes__has_steps__node_type_path(obj:Cyes__has_steps__node_type_path, detach:boolean = false) {
	assert((<(detach?:boolean) => test.Cnode__test>obj.output.node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => test.Cattribute__test>(obj.properties.attribute as any).resolve)(detach) !== undefined || detach);
	finalize_node_type_path(obj.properties.tail, detach);
	switch (obj.properties.type.state.name) {
		case 'entry': finalize_entry__type(obj.properties.type.state.node, detach); break;
		case 'node': finalize_node__type__yes(obj.properties.type.state.node, detach); break;
		case 'state': finalize_state__type(obj.properties.type.state.node, detach); break;
	}
}
function finalize_node_type_path(obj:Cnode_type_path, detach:boolean = false) {
	assert((<(detach?:boolean) => test.Cnode__test>obj.input.node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => test.Cnode__test>obj.output.node)(detach) !== undefined || detach);
	switch (obj.properties.has_steps.state.name) {
		case 'no': finalize_no__has_steps__node_type_path(obj.properties.has_steps.state.node, detach); break;
		case 'yes': finalize_yes__has_steps__node_type_path(obj.properties.has_steps.state.node, detach); break;
	}
}
function finalize_no__assign__optional_assignment(obj:Cno__assign__optional_assignment, detach:boolean = false) {
	assert((<(detach?:boolean) => test.Cstack>obj.output.stack)(detach) !== undefined || detach);
}
function finalize_yes__assign__optional_assignment(obj:Cyes__assign__optional_assignment, detach:boolean = false) {
	assert((<(detach?:boolean) => test.Cvalue__test>obj.inferences.value)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => test.Cstack>obj.output.stack)(detach) !== undefined || detach);
	finalize_stack_frame(obj.properties.stack_frame, detach);
}
function finalize_optional_assignment(obj:Coptional_assignment, detach:boolean = false) {
	assert((<(detach?:boolean) => test.Cstack>obj.input.stack)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => test.Coptional_value>obj.input.value)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => test.Cstack>obj.output.stack)(detach) !== undefined || detach);
	switch (obj.properties.assign.state.name) {
		case 'no': finalize_no__assign__optional_assignment(obj.properties.assign.state.node, detach); break;
		case 'yes': finalize_yes__assign__optional_assignment(obj.properties.assign.state.node, detach); break;
	}
}
function finalize_optional_node(obj:Coptional_node, detach:boolean = false) {
}
function finalize_optional_value(obj:Coptional_value, detach:boolean = false) {
	finalize_value_context(obj.definitions.value_context, detach);
}
function finalize_no__has_next__parameter_definition(obj:Cno__has_next__parameter_definition, detach:boolean = false) {
	assert((<(detach?:boolean) => test.Cstack>obj.output.stack)(detach) !== undefined || detach);
}
function finalize_yes__has_next__parameter_definition(obj:Cyes__has_next__parameter_definition, detach:boolean = false) {
	assert((<(detach?:boolean) => test.Cstack>obj.output.stack)(detach) !== undefined || detach);
	finalize_parameter_definition(obj.properties.parameters, detach);
}
function finalize_parameter_definition(obj:Cparameter_definition, detach:boolean = false) {
	assert((<(detach?:boolean) => test.Cstack>obj.input.stack)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => test.Cstack>obj.output.stack)(detach) !== undefined || detach);
	switch (obj.properties.has_next.state.name) {
		case 'no': finalize_no__has_next__parameter_definition(obj.properties.has_next.state.node, detach); break;
		case 'yes': finalize_yes__has_next__parameter_definition(obj.properties.has_next.state.node, detach); break;
	}
	finalize_stack_frame(obj.properties.stack_frame, detach);
	finalize_value_type_definition(obj.properties.type, detach);
}
function finalize_no__has_next__parameter_implementation(obj:Cno__has_next__parameter_implementation, detach:boolean = false) {
	assert((<(detach?:boolean) => test.Cno__has_next__parameter_definition>obj.inferences.no)(detach) !== undefined || detach);
}
function finalize_yes__has_next__parameter_implementation(obj:Cyes__has_next__parameter_implementation, detach:boolean = false) {
	assert((<(detach?:boolean) => test.Cyes__has_next__parameter_definition>obj.inferences.yes)(detach) !== undefined || detach);
	finalize_parameter_implementation(obj.properties.parameters, detach);
}
function finalize_parameter_implementation(obj:Cparameter_implementation, detach:boolean = false) {
	assert((<(detach?:boolean) => test.Cparameter_definition>obj.input.definition)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => test.Cstack>obj.input.stack)(detach) !== undefined || detach);
	switch (obj.properties.has_next.state.name) {
		case 'no': finalize_no__has_next__parameter_implementation(obj.properties.has_next.state.node, detach); break;
		case 'yes': finalize_yes__has_next__parameter_implementation(obj.properties.has_next.state.node, detach); break;
	}
	finalize_value_expression(obj.properties.value_expression, detach);
	assert((<(detach?:boolean) => test.Cvalue__test>obj.properties.value_expression.inferences.correct_value)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => test.Cvalue__test>obj.properties.value_expression.inferences.value)(detach) !== undefined || detach);
}
function finalize_stack(obj:Cstack, detach:boolean = false) {
}
function finalize_no__has_successor(obj:Cno__has_successor, detach:boolean = false) {
}
function finalize_yes__has_successor(obj:Cyes__has_successor, detach:boolean = false) {
}
function finalize_named_values(obj:Cnamed_values, detach:boolean = false) {
	switch (obj.properties.has_successor.state.name) {
		case 'no': finalize_no__has_successor(obj.properties.has_successor.state.node, detach); break;
		case 'yes': finalize_yes__has_successor(obj.properties.has_successor.state.node, detach); break;
	}
}
function finalize_stack_frame(obj:Cstack_frame, detach:boolean = false) {
	assert((<(detach?:boolean) => test.Cstack>obj.input.parent)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => test.Cvalue__test>obj.input.value)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => test.Cstack>obj.output.parent)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => test.Cvalue__test>obj.output.value)(detach) !== undefined || detach);
	finalize_stack(obj.definitions.stack, detach);
	assert((<(detach?:boolean) => test.Cnamed_values>(obj.properties.name as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => test.Cno__has_successor>obj.properties.name.inferences.single_name)(detach) !== undefined || detach);
	for (const [_key, entry] of obj.properties.named_values) {
		finalize_named_values(entry, detach);
	}
	if (!detach) {
		if (obj.properties.named_values.size === 0) {
			throw new Error(`Collection cannot be empty!`);
		}
	}
}
function finalize_parent_frame(obj:Cparent_frame, detach:boolean = false) {
	assert((<(detach?:boolean) => test.Cstack_frame>obj.inferences.frame)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => test.Cvalue__test>obj.output.value)(detach) !== undefined || detach);
	finalize_stack_selector(obj.properties.tail, detach);
}
function finalize_this_frame(obj:Cthis_frame, detach:boolean = false) {
	assert((<(detach?:boolean) => test.Cstack_frame>obj.inferences.frame)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => test.Cvalue__test>obj.output.value)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => test.Cnamed_values>(obj.properties.name as any).resolve)(detach) !== undefined || detach);
}
function finalize_stack_selector(obj:Cstack_selector, detach:boolean = false) {
	assert((<(detach?:boolean) => test.Cstack>obj.input.stack)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => test.Cvalue__test>obj.output.value)(detach) !== undefined || detach);
	switch (obj.properties.select.state.name) {
		case 'parent frame': finalize_parent_frame(obj.properties.select.state.node, detach); break;
		case 'this frame': finalize_this_frame(obj.properties.select.state.node, detach); break;
	}
}
function finalize_state__test(obj:Cstate__test, detach:boolean = false) {
	finalize_value_context(obj.definitions.value_context, detach);
}
function finalize_state_group(obj:Cstate_group, detach:boolean = false) {
}
function finalize_assertion(obj:Cassertion, detach:boolean = false) {
	assert((<(detach?:boolean) => test.Cstack>obj.output.stack)(detach) !== undefined || detach);
	finalize_condition(obj.properties.condition, detach);
}
function finalize_expression__type(obj:Cexpression__type, detach:boolean = false) {
	assert((<(detach?:boolean) => test.Cstack>obj.output.stack)(detach) !== undefined || detach);
	finalize_optional_assignment(obj.properties.assignment, detach);
	finalize_value_expression(obj.properties.value_expression, detach);
}
function finalize_nothing(obj:Cnothing, detach:boolean = false) {
}
function finalize_something(obj:Csomething, detach:boolean = false) {
	finalize_statements(obj.properties.statements, detach);
}
function finalize_if(obj:Cif, detach:boolean = false) {
	assert((<(detach?:boolean) => test.Cstack>obj.output.stack)(detach) !== undefined || detach);
	finalize_condition(obj.properties.condition, detach);
	switch (obj.properties.else_.state.name) {
		case 'nothing': finalize_nothing(obj.properties.else_.state.node, detach); break;
		case 'something': finalize_something(obj.properties.else_.state.node, detach); break;
	}
	finalize_statements(obj.properties.then, detach);
}
function finalize_no__has_arguments(obj:Cno__has_arguments, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cno__has_arguments>obj.inferences.no)(detach) !== undefined || detach);
}
function finalize_yes__has_arguments(obj:Cyes__has_arguments, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cyes__has_arguments>obj.inferences.yes)(detach) !== undefined || detach);
	finalize_instruction_argument(obj.properties.first_argument, detach);
}
function finalize_no__ignore_errors(obj:Cno__ignore_errors, detach:boolean = false) {
}
function finalize_yes__ignore_errors(obj:Cyes__ignore_errors, detach:boolean = false) {
}
function finalize_no__wait_on_finish(obj:Cno__wait_on_finish, detach:boolean = false) {
}
function finalize_yes__wait_on_finish(obj:Cyes__wait_on_finish, detach:boolean = false) {
}
function finalize_instruction(obj:Cinstruction, detach:boolean = false) {
	assert((<(detach?:boolean) => test.Cstack>obj.output.stack)(detach) !== undefined || detach);
	switch (obj.properties.has_arguments.state.name) {
		case 'no': finalize_no__has_arguments(obj.properties.has_arguments.state.node, detach); break;
		case 'yes': finalize_yes__has_arguments(obj.properties.has_arguments.state.node, detach); break;
	}
	switch (obj.properties.ignore_errors.state.name) {
		case 'no': finalize_no__ignore_errors(obj.properties.ignore_errors.state.node, detach); break;
		case 'yes': finalize_yes__ignore_errors(obj.properties.ignore_errors.state.node, detach); break;
	}
	assert((<(detach?:boolean) => client_bindings.Cinstructions>(obj.properties.instruction as any).resolve)(detach) !== undefined || detach);
	finalize_value_expression(obj.properties.value_expression, detach);
	assert((<(detach?:boolean) => client_bindings.Cnode__client_bindings>obj.properties.value_expression.inferences.binding_node)(detach) !== undefined || detach);
	switch (obj.properties.wait_on_finish.state.name) {
		case 'no': finalize_no__wait_on_finish(obj.properties.wait_on_finish.state.node, detach); break;
		case 'yes': finalize_yes__wait_on_finish(obj.properties.wait_on_finish.state.node, detach); break;
	}
}
function finalize_panic(obj:Cpanic, detach:boolean = false) {
	assert((<(detach?:boolean) => test.Cstack>obj.output.stack)(detach) !== undefined || detach);
}
function finalize_DSL(obj:CDSL, detach:boolean = false) {
}
function finalize_GPRS(obj:CGPRS, detach:boolean = false) {
}
function finalize_WiFi(obj:CWiFi, detach:boolean = false) {
}
function finalize_good_2G(obj:Cgood_2G, detach:boolean = false) {
}
function finalize_good_3G(obj:Cgood_3G, detach:boolean = false) {
}
function finalize_no_throttling(obj:Cno_throttling, detach:boolean = false) {
}
function finalize_offline(obj:Coffline, detach:boolean = false) {
}
function finalize_regular_2G(obj:Cregular_2G, detach:boolean = false) {
}
function finalize_regular_3G(obj:Cregular_3G, detach:boolean = false) {
}
function finalize_regular_4G(obj:Cregular_4G, detach:boolean = false) {
}
function finalize_set_network_conditions(obj:Cset_network_conditions, detach:boolean = false) {
	assert((<(detach?:boolean) => test.Cstack>obj.output.stack)(detach) !== undefined || detach);
	switch (obj.properties.preset.state.name) {
		case 'DSL': finalize_DSL(obj.properties.preset.state.node, detach); break;
		case 'GPRS': finalize_GPRS(obj.properties.preset.state.node, detach); break;
		case 'WiFi': finalize_WiFi(obj.properties.preset.state.node, detach); break;
		case 'good 2G': finalize_good_2G(obj.properties.preset.state.node, detach); break;
		case 'good 3G': finalize_good_3G(obj.properties.preset.state.node, detach); break;
		case 'no throttling': finalize_no_throttling(obj.properties.preset.state.node, detach); break;
		case 'offline': finalize_offline(obj.properties.preset.state.node, detach); break;
		case 'regular 2G': finalize_regular_2G(obj.properties.preset.state.node, detach); break;
		case 'regular 3G': finalize_regular_3G(obj.properties.preset.state.node, detach); break;
		case 'regular 4G': finalize_regular_4G(obj.properties.preset.state.node, detach); break;
	}
}
function finalize_set_user(obj:Cset_user, detach:boolean = false) {
	assert((<(detach?:boolean) => test.Cstack>obj.output.stack)(detach) !== undefined || detach);
}
function finalize_unset_user(obj:Cunset_user, detach:boolean = false) {
	assert((<(detach?:boolean) => test.Cstack>obj.output.stack)(detach) !== undefined || detach);
}
function finalize_wait(obj:Cwait, detach:boolean = false) {
	assert((<(detach?:boolean) => test.Cstack>obj.output.stack)(detach) !== undefined || detach);
}
function finalize_walk(obj:Cwalk, detach:boolean = false) {
	assert((<(detach?:boolean) => test.Cstack>obj.output.stack)(detach) !== undefined || detach);
	finalize_optional_assignment(obj.properties.assignment, detach);
	assert((<(detach?:boolean) => test.Ccollection__test>(obj.properties.collection as any).resolve)(detach) !== undefined || detach);
	finalize_statements(obj.properties.statements, detach);
	finalize_value_expression(obj.properties.value_expression, detach);
	assert((<(detach?:boolean) => test.Cnode__test>obj.properties.value_expression.inferences.node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => test.Cvalue__test>obj.properties.value_expression.inferences.value)(detach) !== undefined || detach);
}
function finalize_statement(obj:Cstatement, detach:boolean = false) {
	assert((<(detach?:boolean) => test.Cstack>obj.input.stack)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => test.Cstack>obj.output.stack)(detach) !== undefined || detach);
	switch (obj.properties.type.state.name) {
		case 'assertion': finalize_assertion(obj.properties.type.state.node, detach); break;
		case 'expression': finalize_expression__type(obj.properties.type.state.node, detach); break;
		case 'if': finalize_if(obj.properties.type.state.node, detach); break;
		case 'instruction': finalize_instruction(obj.properties.type.state.node, detach); break;
		case 'panic': finalize_panic(obj.properties.type.state.node, detach); break;
		case 'set network conditions': finalize_set_network_conditions(obj.properties.type.state.node, detach); break;
		case 'set user': finalize_set_user(obj.properties.type.state.node, detach); break;
		case 'unset user': finalize_unset_user(obj.properties.type.state.node, detach); break;
		case 'wait': finalize_wait(obj.properties.type.state.node, detach); break;
		case 'walk': finalize_walk(obj.properties.type.state.node, detach); break;
	}
}
function finalize_no__has_next__statements(obj:Cno__has_next__statements, detach:boolean = false) {
	assert((<(detach?:boolean) => test.Cstack>obj.output.stack)(detach) !== undefined || detach);
}
function finalize_yes__has_next__statements(obj:Cyes__has_next__statements, detach:boolean = false) {
	assert((<(detach?:boolean) => test.Cstack>obj.output.stack)(detach) !== undefined || detach);
	finalize_statements(obj.properties.statements, detach);
}
function finalize_statements(obj:Cstatements, detach:boolean = false) {
	assert((<(detach?:boolean) => test.Cstack>obj.input.stack)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => test.Cstack>obj.output.stack)(detach) !== undefined || detach);
	switch (obj.properties.has_next.state.name) {
		case 'no': finalize_no__has_next__statements(obj.properties.has_next.state.node, detach); break;
		case 'yes': finalize_yes__has_next__statements(obj.properties.has_next.state.node, detach); break;
	}
	finalize_statement(obj.properties.statement, detach);
}
function finalize_value__test(obj:Cvalue__test, detach:boolean = false) {
	assert((<(detach?:boolean) => test.Coptional_node>obj.output.node)(detach) !== undefined || detach);
	finalize_optional_value(obj.definitions.optional_value, detach);
}
function finalize_value_context(obj:Cvalue_context, detach:boolean = false) {
	assert((<(detach?:boolean) => test.Coptional_value>obj.output.value)(detach) !== undefined || detach);
}
function finalize_named_value(obj:Cnamed_value, detach:boolean = false) {
	assert((<(detach?:boolean) => test.Coptional_value>obj.output.value)(detach) !== undefined || detach);
	finalize_stack_selector(obj.properties.selector, detach);
}
function finalize_root_node(obj:Croot_node, detach:boolean = false) {
	assert((<(detach?:boolean) => test.Coptional_value>obj.output.value)(detach) !== undefined || detach);
}
function finalize_no__has_parameters__run_procedure(obj:Cno__has_parameters__run_procedure, detach:boolean = false) {
	assert((<(detach?:boolean) => test.Cno__has_parameters__procedures>obj.inferences.no)(detach) !== undefined || detach);
}
function finalize_yes__has_parameters__run_procedure(obj:Cyes__has_parameters__run_procedure, detach:boolean = false) {
	assert((<(detach?:boolean) => test.Cyes__has_parameters__procedures>obj.inferences.yes)(detach) !== undefined || detach);
	finalize_parameter_implementation(obj.properties.parameters, detach);
}
function finalize_run_procedure(obj:Crun_procedure, detach:boolean = false) {
	assert((<(detach?:boolean) => test.Coptional_value>obj.output.value)(detach) !== undefined || detach);
	switch (obj.properties.has_parameters.state.name) {
		case 'no': finalize_no__has_parameters__run_procedure(obj.properties.has_parameters.state.node, detach); break;
		case 'yes': finalize_yes__has_parameters__run_procedure(obj.properties.has_parameters.state.node, detach); break;
	}
	assert((<(detach?:boolean) => test.Cprocedures>(obj.properties.procedure as any).resolve)(detach) !== undefined || detach);
}
function finalize_select(obj:Cselect, detach:boolean = false) {
	assert((<(detach?:boolean) => test.Coptional_value>obj.output.value)(detach) !== undefined || detach);
	finalize_value_selection_path(obj.properties.selection, detach);
	switch (obj.properties.start_from.state.name) {
		case 'named value': finalize_named_value(obj.properties.start_from.state.node, detach); break;
		case 'root node': finalize_root_node(obj.properties.start_from.state.node, detach); break;
		case 'run procedure': finalize_run_procedure(obj.properties.start_from.state.node, detach); break;
	}
}
function finalize_file__type__static(obj:Cfile__type__static, detach:boolean = false) {
	assert((<(detach?:boolean) => test.Coptional_value>obj.output.value)(detach) !== undefined || detach);
}
function finalize_number__type__static(obj:Cnumber__type__static, detach:boolean = false) {
	assert((<(detach?:boolean) => test.Coptional_value>obj.output.value)(detach) !== undefined || detach);
}
function finalize_open_view__type(obj:Copen_view__type, detach:boolean = false) {
	assert((<(detach?:boolean) => test.Coptional_value>obj.output.value)(detach) !== undefined || detach);
	finalize_value_expression(obj.properties.on_node, detach);
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.properties.on_node.inferences.equal_view_context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.properties.on_node.inferences.gui_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => test.Cvalue__test>obj.properties.on_node.inferences.value_exists)(detach) !== undefined || detach);
	finalize_value_expression(obj.properties.view_selection, detach);
	assert((<(detach?:boolean) => test.Cview__test>obj.properties.view_selection.inferences.view)(detach) !== undefined || detach);
}
function finalize_text__type__static(obj:Ctext__type__static, detach:boolean = false) {
	assert((<(detach?:boolean) => test.Coptional_value>obj.output.value)(detach) !== undefined || detach);
}
function finalize_choose__select_views__view_definition(obj:Cchoose__select_views__view_definition, detach:boolean = false) {
	assert((<(detach?:boolean) => views.Cviews>obj.output.views)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => views.Cviews>(obj.properties.views as any).resolve)(detach) !== undefined || detach);
}
function finalize_default__select_views__view_definition(obj:Cdefault__select_views__view_definition, detach:boolean = false) {
	assert((<(detach?:boolean) => test.Cset__default_views_lib>obj.inferences.default_views_is_set)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => views.Cviews>obj.output.views)(detach) !== undefined || detach);
}
function finalize_choose__select_window(obj:Cchoose__select_window, detach:boolean = false) {
	assert((<(detach?:boolean) => settings.Cwindows>obj.output.window)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => settings.Cwindows>(obj.properties.window as any).resolve)(detach) !== undefined || detach);
}
function finalize_default__select_window(obj:Cdefault__select_window, detach:boolean = false) {
	assert((<(detach?:boolean) => test.Cset__default_window>obj.inferences.default_window_is_set)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => settings.Cwindows>obj.output.window)(detach) !== undefined || detach);
}
function finalize_view_definition__type(obj:Cview_definition__type, detach:boolean = false) {
	assert((<(detach?:boolean) => test.Coptional_value>obj.output.value)(detach) !== undefined || detach);
	switch (obj.properties.select_views.state.name) {
		case 'choose': finalize_choose__select_views__view_definition(obj.properties.select_views.state.node, detach); break;
		case 'default': finalize_default__select_views__view_definition(obj.properties.select_views.state.node, detach); break;
	}
	switch (obj.properties.select_window.state.name) {
		case 'choose': finalize_choose__select_window(obj.properties.select_window.state.node, detach); break;
		case 'default': finalize_default__select_window(obj.properties.select_window.state.node, detach); break;
	}
	assert((<(detach?:boolean) => views.Cviews__>(obj.properties.view as any).resolve)(detach) !== undefined || detach);
}
function finalize_static(obj:Cstatic, detach:boolean = false) {
	assert((<(detach?:boolean) => test.Coptional_value>obj.output.value)(detach) !== undefined || detach);
	switch (obj.properties.type.state.name) {
		case 'file': finalize_file__type__static(obj.properties.type.state.node, detach); break;
		case 'number': finalize_number__type__static(obj.properties.type.state.node, detach); break;
		case 'open view': finalize_open_view__type(obj.properties.type.state.node, detach); break;
		case 'text': finalize_text__type__static(obj.properties.type.state.node, detach); break;
		case 'view definition': finalize_view_definition__type(obj.properties.type.state.node, detach); break;
	}
}
function finalize_value_expression(obj:Cvalue_expression, detach:boolean = false) {
	assert((<(detach?:boolean) => test.Cstack>obj.input.stack)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => test.Coptional_value>obj.output.value)(detach) !== undefined || detach);
	switch (obj.properties.type.state.name) {
		case 'select': finalize_select(obj.properties.type.state.node, detach); break;
		case 'static': finalize_static(obj.properties.type.state.node, detach); break;
	}
}
function finalize_context(obj:Ccontext, detach:boolean = false) {
	assert((<(detach?:boolean) => test.Coptional_value>obj.output.value)(detach) !== undefined || detach);
}
function finalize_key(obj:Ckey, detach:boolean = false) {
	assert((<(detach?:boolean) => test.Cboolean>obj.inferences.is_entry)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => test.Ctrue>obj.inferences.value_is_entry)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => test.Coptional_value>obj.output.value)(detach) !== undefined || detach);
}
function finalize_no__has_steps__value_selection_path(obj:Cno__has_steps__value_selection_path, detach:boolean = false) {
	assert((<(detach?:boolean) => test.Coptional_value>obj.output.value)(detach) !== undefined || detach);
	switch (obj.properties.select.state.name) {
		case 'context': finalize_context(obj.properties.select.state.node, detach); break;
		case 'key': finalize_key(obj.properties.select.state.node, detach); break;
	}
}
function finalize_attribute__select__attribute(obj:Cattribute__select__attribute, detach:boolean = false) {
	assert((<(detach?:boolean) => test.Cvalue_context>obj.output.context)(detach) !== undefined || detach);
}
function finalize_ordered_graph(obj:Cordered_graph, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cattributes>obj.inferences.gui_attribute)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cyes__has_ordered_graphs>obj.inferences.ordered_collection)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => test.Cvalue_context>obj.output.context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cordered_graphs>(obj.properties.ordered_graph as any).resolve)(detach) !== undefined || detach);
}
function finalize_state__select__state(obj:Cstate__select__state, detach:boolean = false) {
	assert((<(detach?:boolean) => test.Cvalue_context>obj.output.context)(detach) !== undefined || detach);
}
function finalize_state_rule(obj:Cstate_rule, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cstates__state_group__type__property__type__attributes>obj.inferences.gui_state)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => test.Cvalue_context>obj.output.context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Crules>(obj.properties.rule as any).resolve)(detach) !== undefined || detach);
}
function finalize_state__select__attribute(obj:Cstate__select__attribute, detach:boolean = false) {
	assert((<(detach?:boolean) => test.Cstate_group>obj.inferences.state_group)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => test.Cvalue_context>obj.output.context)(detach) !== undefined || detach);
	switch (obj.properties.select.state.name) {
		case 'state': finalize_state__select__state(obj.properties.select.state.node, detach); break;
		case 'state rule': finalize_state_rule(obj.properties.select.state.node, detach); break;
	}
	assert((<(detach?:boolean) => test.Cstate__test>(obj.properties.state as any).resolve)(detach) !== undefined || detach);
}
function finalize_text_rule(obj:Ctext_rule, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cattributes>obj.inferences.gui_attribute)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cyes__navigable>obj.inferences.reference)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => test.Cvalue_context>obj.output.context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Crules>(obj.properties.rule as any).resolve)(detach) !== undefined || detach);
}
function finalize_attribute__select__yes(obj:Cattribute__select__yes, detach:boolean = false) {
	assert((<(detach?:boolean) => test.Cnode__test>obj.inferences.node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => test.Cvalue__test>obj.inferences.value)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => test.Cvalue_context>obj.output.context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => test.Cattribute__test>(obj.properties.attribute as any).resolve)(detach) !== undefined || detach);
	switch (obj.properties.select.state.name) {
		case 'attribute': finalize_attribute__select__attribute(obj.properties.select.state.node, detach); break;
		case 'ordered graph': finalize_ordered_graph(obj.properties.select.state.node, detach); break;
		case 'state': finalize_state__select__attribute(obj.properties.select.state.node, detach); break;
		case 'text rule': finalize_text_rule(obj.properties.select.state.node, detach); break;
	}
}
function finalize_binding_node(obj:Cbinding_node, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cnode__client_bindings>obj.inferences.binding_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => test.Cvalue__test>obj.inferences.value)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => test.Cvalue_context>obj.output.context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => client_bindings.Cbindings__node>(obj.properties.binding as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => test.Cnode__test>obj.properties.binding.inferences.node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => client_bindings.Cyes__switchable_in_configuration_context_path>obj.properties.binding.inferences.switchable)(detach) !== undefined || detach);
}
function finalize_other(obj:Cother, detach:boolean = false) {
	assert((<(detach?:boolean) => test.Cvalue_context>obj.output.context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => client_bindings.Cbindings__client_bindings>(obj.properties.client_binding as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => test.Ctrue>obj.properties.client_binding.inferences.binding_type_valid)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => test.Cboolean>obj.properties.client_binding.inferences.binding_type_validity)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => test.Ctrue>obj.properties.client_binding.inferences.gui_context_valid)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => test.Cboolean>obj.properties.client_binding.inferences.gui_context_validity)(detach) !== undefined || detach);
}
function finalize_binding__select(obj:Cbinding__select, detach:boolean = false) {
	assert((<(detach?:boolean) => test.Cvalue_context>obj.output.context)(detach) !== undefined || detach);
	switch (obj.properties.context.state.name) {
		case 'binding node': finalize_binding_node(obj.properties.context.state.node, detach); break;
		case 'other': finalize_other(obj.properties.context.state.node, detach); break;
	}
}
function finalize_entry__select(obj:Centry__select, detach:boolean = false) {
	assert((<(detach?:boolean) => test.Cvalue_context>obj.output.context)(detach) !== undefined || detach);
	finalize_value_expression(obj.properties.key, detach);
	assert((<(detach?:boolean) => test.Ctext__value>obj.properties.key.inferences.text)(detach) !== undefined || detach);
}
function finalize_size(obj:Csize, detach:boolean = false) {
	assert((<(detach?:boolean) => test.Cvalue_context>obj.output.context)(detach) !== undefined || detach);
}
function finalize_collection__select(obj:Ccollection__select, detach:boolean = false) {
	assert((<(detach?:boolean) => test.Cvalue_context>obj.inferences.entry_container)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => test.Cvalue_context>obj.output.context)(detach) !== undefined || detach);
	switch (obj.properties.select.state.name) {
		case 'entry': finalize_entry__select(obj.properties.select.state.node, detach); break;
		case 'size': finalize_size(obj.properties.select.state.node, detach); break;
	}
}
function finalize_expression__select_view(obj:Cexpression__select_view, detach:boolean = false) {
	assert((<(detach?:boolean) => test.Cexplicit_view>obj.output.view)(detach) !== undefined || detach);
	finalize_value_expression(obj.properties.view_selection, detach);
	assert((<(detach?:boolean) => test.Cexplicit_view>obj.properties.view_selection.inferences.view)(detach) !== undefined || detach);
}
function finalize_from_context(obj:Cfrom_context, detach:boolean = false) {
	assert((<(detach?:boolean) => test.Cvalue__test>obj.inferences.value)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => test.Cexplicit_view>obj.inferences.view)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => test.Cexplicit_view>obj.output.view)(detach) !== undefined || detach);
}
function finalize_query__select(obj:Cquery__select, detach:boolean = false) {
	assert((<(detach?:boolean) => test.Cvalue_context>obj.output.context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => views.Cqueries>(obj.properties.query as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => test.Cnode__test>obj.properties.query.inferences.node_valid)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => test.Coptional_node>obj.properties.query.inferences.optional_node)(detach) !== undefined || detach);
	switch (obj.properties.select_view.state.name) {
		case 'expression': finalize_expression__select_view(obj.properties.select_view.state.node, detach); break;
		case 'from context': finalize_from_context(obj.properties.select_view.state.node, detach); break;
	}
}
function finalize_yes__has_steps__value_selection_path(obj:Cyes__has_steps__value_selection_path, detach:boolean = false) {
	assert((<(detach?:boolean) => test.Coptional_value>obj.output.value)(detach) !== undefined || detach);
	switch (obj.properties.select.state.name) {
		case 'attribute': finalize_attribute__select__yes(obj.properties.select.state.node, detach); break;
		case 'binding': finalize_binding__select(obj.properties.select.state.node, detach); break;
		case 'collection': finalize_collection__select(obj.properties.select.state.node, detach); break;
		case 'query': finalize_query__select(obj.properties.select.state.node, detach); break;
	}
	finalize_value_selection_path(obj.properties.tail, detach);
}
function finalize_value_selection_path(obj:Cvalue_selection_path, detach:boolean = false) {
	assert((<(detach?:boolean) => test.Cvalue_context>obj.input.context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => test.Cstack>obj.input.stack)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => test.Coptional_value>obj.output.value)(detach) !== undefined || detach);
	switch (obj.properties.has_steps.state.name) {
		case 'no': finalize_no__has_steps__value_selection_path(obj.properties.has_steps.state.node, detach); break;
		case 'yes': finalize_yes__has_steps__value_selection_path(obj.properties.has_steps.state.node, detach); break;
	}
}
function finalize_binding__type(obj:Cbinding__type, detach:boolean = false) {
	assert((<(detach?:boolean) => test.Cvalue__test>obj.output.value)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => client_bindings.Cbindings__client_bindings>(obj.properties.client_binding as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => test.Cnode__test>obj.properties.client_binding.inferences.node)(detach) !== undefined || detach);
	finalize_node_type_path(obj.properties.type, detach);
}
function finalize_file__type__value_type_definition(obj:Cfile__type__value_type_definition, detach:boolean = false) {
	assert((<(detach?:boolean) => test.Cvalue__test>obj.output.value)(detach) !== undefined || detach);
}
function finalize_node__type__value_type_definition(obj:Cnode__type__value_type_definition, detach:boolean = false) {
	assert((<(detach?:boolean) => test.Cvalue__test>obj.output.value)(detach) !== undefined || detach);
	finalize_node_type_path(obj.properties.type, detach);
}
function finalize_number__type__value_type_definition(obj:Cnumber__type__value_type_definition, detach:boolean = false) {
	assert((<(detach?:boolean) => test.Cvalue__test>obj.output.value)(detach) !== undefined || detach);
}
function finalize_choose__select_views__query(obj:Cchoose__select_views__query, detach:boolean = false) {
	assert((<(detach?:boolean) => views.Cviews>obj.output.views)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => views.Cviews>(obj.properties.views as any).resolve)(detach) !== undefined || detach);
}
function finalize_default__select_views__query(obj:Cdefault__select_views__query, detach:boolean = false) {
	assert((<(detach?:boolean) => test.Cset__default_views_lib>obj.inferences.default_views_is_set)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => views.Cviews>obj.output.views)(detach) !== undefined || detach);
}
function finalize_query__type(obj:Cquery__type, detach:boolean = false) {
	assert((<(detach?:boolean) => test.Cvalue__test>obj.output.value)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => views.Cqueries>(obj.properties.query as any).resolve)(detach) !== undefined || detach);
	switch (obj.properties.select_views.state.name) {
		case 'choose': finalize_choose__select_views__query(obj.properties.select_views.state.node, detach); break;
		case 'default': finalize_default__select_views__query(obj.properties.select_views.state.node, detach); break;
	}
	assert((<(detach?:boolean) => views.Cviews__>(obj.properties.view as any).resolve)(detach) !== undefined || detach);
}
function finalize_text__type__value_type_definition(obj:Ctext__type__value_type_definition, detach:boolean = false) {
	assert((<(detach?:boolean) => test.Cvalue__test>obj.output.value)(detach) !== undefined || detach);
}
function finalize_view__type(obj:Cview__type, detach:boolean = false) {
	assert((<(detach?:boolean) => test.Cvalue__test>obj.output.value)(detach) !== undefined || detach);
}
function finalize_value_type_definition(obj:Cvalue_type_definition, detach:boolean = false) {
	assert((<(detach?:boolean) => test.Cvalue__test>obj.output.value)(detach) !== undefined || detach);
	switch (obj.properties.type.state.name) {
		case 'binding': finalize_binding__type(obj.properties.type.state.node, detach); break;
		case 'file': finalize_file__type__value_type_definition(obj.properties.type.state.node, detach); break;
		case 'node': finalize_node__type__value_type_definition(obj.properties.type.state.node, detach); break;
		case 'number': finalize_number__type__value_type_definition(obj.properties.type.state.node, detach); break;
		case 'query': finalize_query__type(obj.properties.type.state.node, detach); break;
		case 'text': finalize_text__type__value_type_definition(obj.properties.type.state.node, detach); break;
		case 'view': finalize_view__type(obj.properties.type.state.node, detach); break;
	}
}
function finalize_view__test(obj:Cview__test, detach:boolean = false) {
	assert((<(detach?:boolean) => test.Cboolean>obj.output.open)(detach) !== undefined || detach);
	finalize_value__test(obj.definitions.value, detach);
}
function finalize_no__background(obj:Cno__background, detach:boolean = false) {
	assert((<(detach?:boolean) => test.Cstack>obj.output.stack)(detach) !== undefined || detach);
}
function finalize_no__timeout__yes(obj:Cno__timeout__yes, detach:boolean = false) {
}
function finalize_yes__timeout__yes(obj:Cyes__timeout__yes, detach:boolean = false) {
}
function finalize_yes__background(obj:Cyes__background, detach:boolean = false) {
	assert((<(detach?:boolean) => test.Cstack>obj.output.stack)(detach) !== undefined || detach);
	finalize_statements(obj.properties.statements, detach);
	switch (obj.properties.timeout.state.name) {
		case 'no': finalize_no__timeout__yes(obj.properties.timeout.state.node, detach); break;
		case 'yes': finalize_yes__timeout__yes(obj.properties.timeout.state.node, detach); break;
	}
}
function finalize_none__default_views_lib(obj:Cnone__default_views_lib, detach:boolean = false) {
}
function finalize_set__default_views_lib(obj:Cset__default_views_lib, detach:boolean = false) {
	assert((<(detach?:boolean) => views.Cviews>(obj.properties.views_lib as any).resolve)(detach) !== undefined || detach);
}
function finalize_none__default_window(obj:Cnone__default_window, detach:boolean = false) {
}
function finalize_set__default_window(obj:Cset__default_window, detach:boolean = false) {
	assert((<(detach?:boolean) => settings.Cwindows>(obj.properties.window as any).resolve)(detach) !== undefined || detach);
}
function finalize_none__output_type(obj:Cnone__output_type, detach:boolean = false) {
	assert((<(detach?:boolean) => test.Coptional_value>obj.output.value)(detach) !== undefined || detach);
}
function finalize_value__output_type(obj:Cvalue__output_type, detach:boolean = false) {
	assert((<(detach?:boolean) => test.Coptional_value>obj.output.value)(detach) !== undefined || detach);
	finalize_value_expression(obj.properties.return_value, detach);
}
function finalize_body(obj:Cbody, detach:boolean = false) {
	switch (obj.properties.output_type.state.name) {
		case 'none': finalize_none__output_type(obj.properties.output_type.state.node, detach); break;
		case 'value': finalize_value__output_type(obj.properties.output_type.state.node, detach); break;
	}
	finalize_statements(obj.properties.statements, detach);
}
function finalize_no__has_parameters__procedures(obj:Cno__has_parameters__procedures, detach:boolean = false) {
	assert((<(detach?:boolean) => test.Cstack>obj.output.stack)(detach) !== undefined || detach);
}
function finalize_yes__has_parameters__procedures(obj:Cyes__has_parameters__procedures, detach:boolean = false) {
	assert((<(detach?:boolean) => test.Cstack>obj.output.stack)(detach) !== undefined || detach);
	finalize_parameter_definition(obj.properties.parameters, detach);
}
function finalize_procedures(obj:Cprocedures, detach:boolean = false) {
	finalize_body(obj.properties.body, detach);
	switch (obj.properties.has_parameters.state.name) {
		case 'no': finalize_no__has_parameters__procedures(obj.properties.has_parameters.state.node, detach); break;
		case 'yes': finalize_yes__has_parameters__procedures(obj.properties.has_parameters.state.node, detach); break;
	}
}
function finalize_no__given(obj:Cno__given, detach:boolean = false) {
	assert((<(detach?:boolean) => test.Cstack>obj.output.stack)(detach) !== undefined || detach);
}
function finalize_yes__given(obj:Cyes__given, detach:boolean = false) {
	assert((<(detach?:boolean) => test.Cstack>obj.output.stack)(detach) !== undefined || detach);
	finalize_statements(obj.properties.statements, detach);
}
function finalize_no__then(obj:Cno__then, detach:boolean = false) {
}
function finalize_yes__then(obj:Cyes__then, detach:boolean = false) {
	finalize_statements(obj.properties.statements, detach);
}
function finalize_no__timeout__scenarios(obj:Cno__timeout__scenarios, detach:boolean = false) {
}
function finalize_yes__timeout__scenarios(obj:Cyes__timeout__scenarios, detach:boolean = false) {
}
function finalize_no__when(obj:Cno__when, detach:boolean = false) {
	assert((<(detach?:boolean) => test.Cstack>obj.output.stack)(detach) !== undefined || detach);
}
function finalize_yes__when(obj:Cyes__when, detach:boolean = false) {
	assert((<(detach?:boolean) => test.Cstack>obj.output.stack)(detach) !== undefined || detach);
	finalize_statements(obj.properties.statements, detach);
}
function finalize_scenarios(obj:Cscenarios, detach:boolean = false) {
	switch (obj.properties.given.state.name) {
		case 'no': finalize_no__given(obj.properties.given.state.node, detach); break;
		case 'yes': finalize_yes__given(obj.properties.given.state.node, detach); break;
	}
	switch (obj.properties.then.state.name) {
		case 'no': finalize_no__then(obj.properties.then.state.node, detach); break;
		case 'yes': finalize_yes__then(obj.properties.then.state.node, detach); break;
	}
	switch (obj.properties.timeout.state.name) {
		case 'no': finalize_no__timeout__scenarios(obj.properties.timeout.state.node, detach); break;
		case 'yes': finalize_yes__timeout__scenarios(obj.properties.timeout.state.node, detach); break;
	}
	switch (obj.properties.when.state.name) {
		case 'no': finalize_no__when(obj.properties.when.state.node, detach); break;
		case 'yes': finalize_yes__when(obj.properties.when.state.node, detach); break;
	}
}
function finalize_test(obj:Ctest, detach:boolean = false) {
	switch (obj.properties.background.state.name) {
		case 'no': finalize_no__background(obj.properties.background.state.node, detach); break;
		case 'yes': finalize_yes__background(obj.properties.background.state.node, detach); break;
	}
	switch (obj.properties.default_views_lib.state.name) {
		case 'none': finalize_none__default_views_lib(obj.properties.default_views_lib.state.node, detach); break;
		case 'set': finalize_set__default_views_lib(obj.properties.default_views_lib.state.node, detach); break;
	}
	switch (obj.properties.default_window.state.name) {
		case 'none': finalize_none__default_window(obj.properties.default_window.state.node, detach); break;
		case 'set': finalize_set__default_window(obj.properties.default_window.state.node, detach); break;
	}
	for (const [_key, entry] of obj.properties.procedures) {
		finalize_procedures(entry, detach);
	}
	if (!detach) {
	}
	for (const [_key, entry] of obj.properties.scenarios) {
		finalize_scenarios(entry, detach);
	}
	if (!detach) {
	}
}

export namespace Ctest {
	export function create(init:Ttest, input: {
	'client_bindings':client_bindings.Cclient_bindings
	'gui_model':gui_model.Cgui_model
	'settings':settings.Csettings
	'views':{[name:string]:views.Cviews}}, symbols_file?: SymbolsFile):Ctest {
		const instance = new Ctest(init, input as any, symbols_file);
		finalize_test(instance);
		return instance;
	};
}
